import {
  AGE_EMPLOYMENT_EXEMPT,
  calcAge,
  calcWageCenterSavingsNontaxBase,
  formatHoursDisplay,
  validateAgeInsurance,
  type AgeInsuranceValidation,
} from '@/composables/wage/useWageCalculator';
import { truncateWageOnes } from './wageOnesTruncatePolicy';
import type { HomeWagePayrollFormData } from './homeWagePayrollFields';
import {
  createEmptyPayrollForm,
  HOME_WAGE_DIRECTOR_MERGED_ALLOWANCE_KEYS,
  HOME_WAGE_NONTAX_TERM,
  HOME_WAGE_STAFF_DIRECTOR_MERGED_ALLOWANCE_KEYS,
  HOME_WAGE_TAXABLE_COMPONENT_KEYS,
  HOME_WAGE_STAFF_TAXABLE_COMPONENT_KEYS,
} from './homeWagePayrollFields';

/**
 * 방문요양 / 방문목욕 / 방문간호 — 월별 임금설정(MemberHomeWageMonthlySetting) 정책
 *
 * ★ 변수별 법칙 초안·작성: `homeWageRulesLawbook.md` (사용자 작성 → 확정 후 이 파일에 구현)
 * ★ 전체 정책 목록(DIST+src): `homeWageMasterPolicyCatalog.md`
 * ★ 과세/비과세 수당: `homeWageTaxAllowancePolicy.md`
 * ★ 데이터 흐름 및 연동 정책: `homeWageDataFlowPolicy.md` ← DB로드→표시, 입력→연쇄계산
 * ★ 정책 추가·변경 시 계산 로직은 이 파일을 단일 소스로 유지한다.
 */

/** 8시간 미만 한도 (휴게시간 차감 — 이상이면 차감 없음) */
export const HOME_WAGE_BREAK_THRESHOLD_HOURS = 8;

/** 240분(4시간) 초과 시 휴게 30분 차감 — 30분 단위 내림 후 4h 초과분부터 적용 */
export const HOME_WAGE_BREAK_AFTER_HOURS = 4;

/** 장시간 방문 휴게시간 차감량(시간) */
export const HOME_WAGE_BREAK_DEDUCT_HOURS = 0.5;

/** @deprecated HOME_WAGE_BREAK_AFTER_HOURS 사용 */
export const HOME_WAGE_BREAK_MINUTES_THRESHOLD = 240;

/** 청구 행 (API rows) */
export interface HomeWageClaimRow {
  clcd_idx?: number | null;
  clc_idx?: number | null;
  date?: string | null;
  recipient_name?: string | null;
  cert_number?: string | null;
  fee_name?: string | null;
  fee_code?: string | null;
  fee_amount?: number | null;
  plan_start_time?: string | null;
  plan_end_time?: string | null;
  /** 실제 서비스 시작 시간 (clcd_service_start_time) */
  service_start_time?: string | null;
  /** 실제 서비스 종료 시간 (clcd_service_end_time) */
  service_end_time?: string | null;
  /** 청구내역상세 처리상태 (미조인 상세 행) */
  detail_status?: string | null;
}

/** 청구내역상세 처리상태 우선순위 — 낮을수록 사용. 지급 > 그 외 > 청구의뢰 */
export function claimDetailStatusRank(status?: string | null): number {
  const s = String(status || '').trim();
  if (s === '지급') return 1;
  if (s === '청구의뢰') return 9;
  return 5;
}

const toClaimVisitTimeKey = (value?: string | null): string =>
  String(value || '').trim().substring(0, 5);

/** 같은 방문(일자·시간·인정번호·수가) 키 */
export function buildClaimDetailVisitKey(row: HomeWageClaimRow): string {
  const date = String(row.date || '').trim().substring(0, 10);
  const start = toClaimVisitTimeKey(row.service_start_time || row.plan_start_time);
  const end = toClaimVisitTimeKey(row.service_end_time || row.plan_end_time);
  const cert = String(row.cert_number || '').trim();
  const fee = String(row.fee_code || '').trim();
  return `${date}|${start}|${end}|${cert}|${fee}`;
}

/**
 * 같은 방문이 청구의뢰·지급으로 둘 다 있으면 지급만 남긴다.
 * 상세 idx가 없는 청구내역 전용 행은 그대로 둔다.
 */
export function preferPaidClaimDetailRows(rows: HomeWageClaimRow[]): HomeWageClaimRow[] {
  if (!rows?.length) return rows || [];

  const bestByKey = new Map<string, { row: HomeWageClaimRow; rank: number; idx: number }>();
  const order: Array<{ type: 'plain'; row: HomeWageClaimRow } | { type: 'visit'; key: string }> = [];

  for (const row of rows) {
    if (row.clcd_idx == null) {
      order.push({ type: 'plain', row });
      continue;
    }
    const key = buildClaimDetailVisitKey(row);
    const next = {
      row,
      rank: claimDetailStatusRank(row.detail_status),
      idx: Number(row.clcd_idx) || 0,
    };
    const prev = bestByKey.get(key);
    if (!prev) {
      bestByKey.set(key, next);
      order.push({ type: 'visit', key });
      continue;
    }
    if (next.rank < prev.rank || (next.rank === prev.rank && next.idx > prev.idx)) {
      bestByKey.set(key, next);
    }
  }

  return order
    .map((item) => (item.type === 'plain' ? item.row : bestByKey.get(item.key)?.row))
    .filter((row): row is HomeWageClaimRow => !!row);
}

/** 청구 건별 수가금액(clcd_fee) 합계 — LTCS 청구 원본 수가 */
export function calcClaimFeeAmountTotal(rows: HomeWageClaimRow[]): number {
  return preferPaidClaimDetailRows(rows).reduce((sum, row) => sum + (Number(row.fee_amount) || 0), 0);
}

/** 수급자 × 수가코드 집계 행 */
export interface HomeWageRecipientFeeSummary {
  groupKey: string;
  recipientName: string;
  certNumber: string;
  feeName: string;
  feeCode: string;
  serviceCount: number;
  /** 중증수급자 가산 — 시급·수가시간 입력 없음, 합계는 수가금액(clcd_fee) 기준 */
  isSevereRecipientSurcharge: boolean;
  /** 1회당 수가시간(시간) — 정책 적용 후, 그룹 내 동일 가정 (가산 행은 0) */
  sugaHoursPerVisit: number;
  /** 1회당 수가금액(clcd_fee) — 가산 행 집계용 */
  feeAmountPerVisit: number;
  /** 수가명에 일요일가산·공휴일가산 포함 — 표시용 (금액은 비과세 연·야·휴에서 산출) */
  isHolidayFeeSurcharge: boolean;
  /** @deprecated 항상 0 — 휴일수당은 비과세 연·야·휴에서만 산출 */
  holidayPayAmount: number;
  hourlyWage: number;
  totalAmount: number;
}

// ─────────────────────────────────────────────
// 1. 수가시간 계산 정책
// ─────────────────────────────────────────────
//
// | 구분 | 조건 | 처리 |
// |------|------|------|
// | 서비스시간(기준) | — | 청구내역 모달 「서비스시간」= plan_start~plan_end 30분 내림 (없으면 service) |
// | 270분초과 등 수가명 | — | 수가명 "270분" 등 숫자 파싱 금지 |
// | 휴게 30분 차감 | 서비스시간 > 4h 이고 < 8h | 0.5h(30분) 차감 (예: 4.5h→4h, 5h→4.5h) |
// | 휴게 30분 차감 | 서비스시간 ≥ 8h | 차감 없음 |
// | 일반 수가 | — | **30분 단위 내림** (7.4h→7.0h, 7.6h→7.5h) |
// | 방문목욕(B2) 구간 수가 | `40분이상 60분미만` 등 | 수가명 **구간 하한** 고정 |
// | 그 외 (B1·B2 60분이상 등) | — | plan → service → 수가명 (**30분 내림** 후 휴게 차감) |
//
// 사원목록 사업구분 칩: B1 일반→「일반」, B1 가족→「가족」, B2→「목욕」(복수 시 각각 표시)
// 인건비지출내역 종사자구분: 요양보호사 → 방문요양(B1) / 방문목욕(B2), 일반·가족 구분 없음
// 가족 판별: 수가명에 '가족' 포함 (isFamilyCare) 또는 clc_is_family=Y. 중증가산 제외.
//
// 칩 색상 (caion_crawl_biz_map 연계 — _home-wage-page.scss 와 동기화)
// | 칩 | DB 참조 | hex | 비고 |
// | 일반 | 공동생활가정 | #577590 | B1 일반. 방문요양·방문목욕 DB색과 구분 |
// | 가족 | 주야간보호 | #E07A5F | B1 가족. DB 가족 전용 없음 |
// | 목욕 | 방문목욕 | #C99700 | B2 방문목욕 (겨자색) |
// ─────────────────────────────────────────────

// ─────────────────────────────────────────────
// 2. 중증수급자 가산
// ─────────────────────────────────────────────
//
// | 구분 | 수가시간 | 시급 | 합계금액 |
// |------|----------|------|----------|
// | 중증수급자 가산 | 수가시간·시급 칸 colspan → 「수가:」입력 | — | 서비스횟수 × 수가(기본 clcd_fee, 수정 가능) |
//
// 시급·수가시간 대신 1회당 수가금액 입력(기본값: 청구 clcd_fee 평균).
// ─────────────────────────────────────────────
//
// ─────────────────────────────────────────────
// 2-1. 휴일가산 수가 (일요일가산·공휴일가산) — 표시만
// ─────────────────────────────────────────────
//
// | 구분 | 표시 | 합계금액 |
// |------|------|----------|
// | 수가명에 일요일가산·공휴일가산 | 휴일 칩 + 행 강조 | 서비스횟수 × 수가시간 × 시급 (일반과 동일) |
//
// ★ 휴일수당 금액은 비과세 수당(연·야·휴)에서만 산출 — 여기서 중복 계산하지 않음
// ─────────────────────────────────────────────

/** 수가명·수가코드가 중증수급자 가산인지 (레거시: 중증수급자 가산 / 중증수급자가산) */
export function isSevereRecipientSurcharge(
  feeName?: string | null,
  feeCode?: string | null
): boolean {
  const name = String(feeName || '').replace(/\s/g, '');
  if (name.includes('중증수급자') && name.includes('가산')) return true;
  if (name === '중증수급자가산') return true;
  const codeText = String(feeCode || '');
  return codeText.includes('중증') && codeText.includes('가산');
}

/** 수가명·수가코드에 '270분초과' 포함 여부 */
export function isOver270Fee(feeName?: string | null, feeCode?: string | null): boolean {
  const text = `${feeName || ''}${feeCode || ''}`;
  return text.includes('270분초과');
}

/** 수가명에 일요일가산·공휴일가산 포함 여부 — 휴일 칩·휴일수당 시간 산출 공통 */
export function isHolidayFeeSurcharge(feeName?: string | null): boolean {
  const name = String(feeName || '');
  return name.includes('일요일가산') || name.includes('공휴일가산');
}

/** 급여 시점 — 센터장(client) vs 관리자(회계·비과세 혜택 전체) */
export type HomeWagePayrollViewMode = 'director' | 'admin';

/**
 * 센터장 시점 — 휴일수당(plan) 집계 대상
 * - 청구 수가명 **일요일가산·공휴일가산** (5/1 plan-only·노동절수당 제외)
 * - 노동절수당(laborDay)은 별도 정책
 */
export function isDirectorHolidayPremiumClaim(
  row: Pick<HomeWageClaimRow, 'fee_code' | 'fee_name'>
): boolean {
  if (!isHomeCareVisitFeeCode(row.fee_code)) return false;
  return isHolidayFeeSurcharge(row.fee_name);
}

/** 시점별 휴일수당(plan) 집계 대상 */
export function isHolidayPremiumClaimForViewMode(
  row: Pick<HomeWageClaimRow, 'fee_code' | 'fee_name' | 'date'>,
  yearMonth: string,
  viewMode: HomeWagePayrollViewMode
): boolean {
  if (viewMode === 'director') {
    return isDirectorHolidayPremiumClaim(row);
  }
  return isHomeWageHolidayPremiumClaim(row, yearMonth);
}

/** 휴일 가산 표기 제거 — 동일 수가 종류 매칭용 */
export function stripHolidaySurchargeFromFeeName(feeName?: string | null): string {
  return String(feeName || '')
    .replace(/공휴일가산_?/g, '')
    .replace(/일요일가산_?/g, '')
    .replace(/\(_+/g, '(')
    .replace(/_+\)/g, ')')
    .trim();
}

export interface RecipientFeeRowPayAmount {
  baseAmount: number;
  holidayPayAmount: number;
  totalAmount: number;
}

/**
 * 수급자별 시급·수가시간 행 지급금액
 * - 합계 = 서비스횟수 × 수가시간 × 수급자별 시급
 * - 휴일가산 수가도 동일 (휴일수당 시간은 수가명 가산 여부로 연·야·휴에서 산출)
 */
export function calcRecipientFeeRowPayAmount(input: {
  serviceCount: number;
  sugaHoursPerVisit: number;
  hourlyWage: number;
  feeAmountPerVisit: number;
}): RecipientFeeRowPayAmount {
  const baseAmount = Math.round(
    input.serviceCount * input.sugaHoursPerVisit * input.hourlyWage
  );
  return { baseAmount, holidayPayAmount: 0, totalAmount: baseAmount };
}

/** 수가코드 접두어 → 방문요양(B1) / 방문목욕(B2) */
export function getFeeCodeBizFlags(feeCode?: string | null): { hasB1: boolean; hasB2: boolean } {
  const code = String(feeCode || '').trim().toUpperCase();
  if (!code) return { hasB1: false, hasB2: false };
  return {
    hasB1: code.startsWith('B1'),
    hasB2: code.startsWith('B2'),
  };
}

export interface CaregiverClaimBizFlags {
  hasRegularVisitCare: boolean;
  hasFamilyVisitCare: boolean;
  hasVisitBath: boolean;
}

/** 청구 사업구분 칩 목록 (일반/가족/목욕 — 복수 가능) */
export function formatCaregiverClaimBizChips(flags?: CaregiverClaimBizFlags | null): string[] {
  if (!flags) return [];
  const chips: string[] = [];
  if (flags.hasRegularVisitCare) chips.push('일반');
  if (flags.hasFamilyVisitCare) chips.push('가족');
  if (flags.hasVisitBath) chips.push('목욕');
  return chips;
}

/** 하위 호환: 칩을 · 로 연결 (인건비지출내역 등) */
export function formatCaregiverClaimBizLabel(flags?: CaregiverClaimBizFlags | null): string {
  const chips = formatCaregiverClaimBizChips(flags);
  return chips.length > 0 ? chips.join('·') : '-';
}

/** 사원·청구 매칭 키 (이름|생년월일) */
export function buildCaregiverMatchKey(name?: string | null, birth?: string | null): string {
  const n = String(name || '').trim();
  const b = birth ? String(birth).substring(0, 10) : '';
  return `${n}|${b}`;
}

/** 직종이 요양보호사인지 */
export function isCaregiverJobTitle(job?: string | null): boolean {
  return String(job || '').includes('요양보호사');
}

/** 칩 배열 → CaregiverClaimBizFlags */
export function parseCaregiverClaimBizFlagsFromChips(
  chips: string[]
): CaregiverClaimBizFlags {
  return {
    hasRegularVisitCare: chips.includes('일반'),
    hasFamilyVisitCare: chips.includes('가족'),
    hasVisitBath: chips.includes('목욕'),
  };
}

/** claim_biz_label(일반·가족·목욕) → flags */
export function parseCaregiverClaimBizFlagsFromLabel(
  label?: string | null
): CaregiverClaimBizFlags {
  if (!label || label === '-') {
    return { hasRegularVisitCare: false, hasFamilyVisitCare: false, hasVisitBath: false };
  }
  return parseCaregiverClaimBizFlagsFromChips(
    label.split('·').map((part) => part.trim()).filter(Boolean)
  );
}

/** 인건비 지출내역 — 요양보호사 B1/B2 구분 (일반·가족 통합 → 방문요양) */
export function resolveLaborCostCaregiverCategory(
  flags?: CaregiverClaimBizFlags | null
): string {
  const hasB1 = Boolean(flags?.hasRegularVisitCare || flags?.hasFamilyVisitCare);
  const hasB2 = Boolean(flags?.hasVisitBath);
  if (hasB1) return '요양보호사(방문요양)';
  if (hasB2) return '요양보호사(방문목욕)';
  return '요양보호사';
}

/** 인건비 지출내역 — 종사자구분 라벨 */
export function formatLaborCostJobCategory(
  job: string,
  claimBizFlags?: CaregiverClaimBizFlags | null
): string {
  const j = (job || '').trim() || '미분류';
  if (isCaregiverJobTitle(j)) return resolveLaborCostCaregiverCategory(claimBizFlags);
  return j;
}

/** 인건비 지출내역 — 종사자구분 정렬 순서 */
export function getLaborCostJobCategoryOrder(category: string): number {
  if (category.includes('방문요양')) return 1;
  if (category.includes('방문목욕')) return 2;
  if (category.includes('사회복지사')) return 3;
  if (category.includes('센터장')) return 4;
  return 99;
}

// ─────────────────────────────────────────────
// 1-1. 가족요양 정책
// ─────────────────────────────────────────────
//
// ★★★ 가족요양 판별 기준 ★★★
// - 수가명(sugaName/feeName)에 '가족'이 포함되어 있으면 가족요양
// - 가족요양 전용: 모든 수급자가 가족요양인 경우 (isAllFamilyCaregiver)
//
// ★★★ 가족요양 vs 일반요양 급여 계산 차이 ★★★
// | 구분 | 일반 요양 | 가족요양 전용 |
// |------|----------|--------------|
// | 기본급 | 최저시급 × 수가시간 | 월급여 전체 (주휴·연차 포함) |
// | 주휴수당 | 기본급 × 0.2 | **0원** (기본급에 포함) |
// | 연차수당 | 공식 계산 | **0원** (기본급에 포함) |
// | 휴일수당 | 별도 항목 | **센터장: 0원** (기본급 포함) / **관리자: 기본급에서 분리** (총지급 추가 아님) |
// | 식대 | 남은금액에서 | **지급 가능** (정책 변경) |
//
// ★★★ 가족요양 + 60시간 미만 제약 ★★★
// | 항목 | 제약 |
// |------|------|
// | 건강보험 | 0원 (가입 불가) |
// | 장기요양보험 | 0원 (건강보험 연동) |
// | 국민연금·고용보험 | 일반과 동일 산출 (나이 면제 등) |
// | 퇴직금 | 적립 불가 (별도 정책) |
// | 식대 | **지급 가능** |
//
// ★★★ 본인부담금공제 (가족요양 전용 기능) ★★★
// - 가족요양만 사용 가능 (일반요양 X)
// - 본인부담금 = 가족요양 수가금액 × 선택 비율 (원단위 절삭)
// - 변수: l_bonin (본인부담금공제)
//
// ★★★ 가족요양 제약 조건 요약표 ★★★
// | 조건 | 기본급 | 주휴 | 연차 | 식대 | 4대보험 | 퇴직금 | 본인부담금공제 |
// |------|--------|------|------|------|---------|--------|---------------|
// | 가족 60시간+ | 통합 | 포함 | 포함 | O | O | O | O |
// | 가족 60시간- | 통합 | 포함 | 포함 | O(건강·장기=0) | X | O |
// | 일반 60시간+ | 분리 | 분리 | 분리 | O | O | O | X |
// | 일반 60시간- | 분리 | 분리 | 분리 | O | O | X | X |
//
// ─────────────────────────────────────────────

/** 수가명에 '가족'이 포함되어 있는지 확인 */
export function isFamilyCare(feeName?: string | null): boolean {
  return String(feeName || '').includes('가족');
}

/** 수가명이 '가족 60분' 형태인지 확인 */
export function isFamilyCare60(feeName?: string | null): boolean {
  const name = String(feeName || '');
  return name.includes('가족') && name.includes('60분');
}

/** 수가명이 '가족 90분' 형태인지 확인 */
export function isFamilyCare90(feeName?: string | null): boolean {
  const name = String(feeName || '');
  return name.includes('가족') && name.includes('90분');
}

/**
 * 그룹 내 모든 수급자가 가족요양인지 확인
 * @param patients 수급자 배열 (각 항목에 feeName/sugaName 포함)
 */
export function isAllFamilyCaregiver(
  patients: Array<{ feeName?: string | null; sugaName?: string | null }>
): boolean {
  if (!patients || patients.length === 0) return false;
  return patients.every((p) => isFamilyCare(p.feeName || p.sugaName));
}

/**
 * 그룹 내 가족요양 수급자가 하나라도 있는지 확인
 * @param patients 수급자 배열
 */
export function hasFamilyCarePatient(
  patients: Array<{ feeName?: string | null; sugaName?: string | null }>
): boolean {
  if (!patients || patients.length === 0) return false;
  return patients.some((p) => isFamilyCare(p.feeName || p.sugaName));
}

/** 가족요양 급여 계산 입력 */
export interface FamilyCareWageInput {
  /** 월급여 (수급자별 합계) */
  totalMonthlyPay: number;
  /** 총 수가시간 */
  totalSugaHours: number;
  /** 가족요양 전용 여부 (모든 수급자가 가족요양) */
  isAllFamily: boolean;
  /** 근무일수 */
  workDays: number;
  /** 점심근무일수 (식대 계산용) */
  lunchWorkDays: number;
}

/** 가족요양 급여 계산 결과 */
export interface FamilyCareWageResult {
  /** 기본급 (가족요양 전용 시 월급여 전체) */
  basePay: number;
  /** 주휴수당 (가족요양 전용 시 0) */
  weeklyHolidayPay: number;
  /** 연차수당 (가족요양 전용 시 0) */
  annualLeavePay: number;
  /** 식대 (가족요양도 지급 가능) */
  lunchPay: number;
  /** 기본급 레이블 (화면 표시용) */
  basePayLabel: string;
  /** 가족요양 전용 여부 */
  isFamilyOnly: boolean;
}

/**
 * 가족요양 전용 급여 계산
 * - 기본급 = 월급여 전체 (주휴·연차 포함)
 * - 주휴수당 = 0
 * - 연차수당 = 0
 * - 식대 = 지급 가능 (정책 변경)
 */
export function calcFamilyCareOnlyWage(
  input: FamilyCareWageInput
): FamilyCareWageResult {
  const { totalMonthlyPay, lunchWorkDays, isAllFamily } = input;

  if (!isAllFamily) {
    // 가족요양 전용이 아닌 경우 — 일반 급여 계산 흐름 사용
    return {
      basePay: 0,
      weeklyHolidayPay: 0,
      annualLeavePay: 0,
      lunchPay: 0,
      basePayLabel: '기본급',
      isFamilyOnly: false,
    };
  }

  // 가족요양 전용: 월급여 전체가 기본급 (주휴·연차 포함)
  const basePay = totalMonthlyPay;
  const weeklyHolidayPay = 0;
  const annualLeavePay = 0;

  // 식대: 가족요양도 지급 가능 (정책 변경)
  const lunchPay = calcHomeWageStaffLunchByDays(lunchWorkDays);

  return {
    basePay,
    weeklyHolidayPay,
    annualLeavePay,
    lunchPay,
    basePayLabel: '기본급 (주휴·연차 포함)',
    isFamilyOnly: true,
  };
}

/**
 * 가족요양 수가금액 합계 계산
 * @param patients 수급자 배열
 */
export function calcFamilyCareTotalSugaMoney(
  patients: Array<{
    feeName?: string | null;
    sugaName?: string | null;
    totalSugaMoney?: number;
    feeAmount?: number;
  }>
): number {
  return patients.reduce((sum, p) => {
    if (isFamilyCare(p.feeName || p.sugaName)) {
      return sum + (p.totalSugaMoney ?? p.feeAmount ?? 0);
    }
    return sum;
  }, 0);
}

/**
 * 가족요양 본인부담금 계산
 * 공식: floor(가족요양 수가금액 × 비율 / 100 / 10) × 10 (원단위 절삭)
 * @param familySugaMoney 가족요양 수가금액 합계
 * @param ratePercent 본인부담 비율 (0~100%)
 */
export function calcFamilyCareSelfPayment(
  familySugaMoney: number,
  ratePercent: number
): number {
  if (familySugaMoney <= 0 || ratePercent <= 0) return 0;
  return Math.floor((familySugaMoney * ratePercent) / 100 / 10) * 10;
}

/** 일반적인 본인부담금 비율 옵션 */
export const FAMILY_CARE_SELF_PAYMENT_RATES = [0, 5, 10, 15, 20] as const;

/**
 * 식대 계산 (일수 기반 — 가족요양/센터장/사회복지사 공용)
 * @param lunchWorkDays 점심근무일수
 * @param dailyAmount 1일 식대 금액 (기본: 10,000원)
 * @param maxAmount 월 최대 한도 (기본: 200,000원)
 */
export function calcHomeWageStaffLunchByDays(
  lunchWorkDays: number,
  dailyAmount: number = HOME_WAGE_LUNCH_DAILY_DEFAULT,
  maxAmount: number = HOME_WAGE_LUNCH_MONTHLY_LIMIT
): number {
  const calculated = lunchWorkDays * dailyAmount;
  return Math.min(calculated, maxAmount);
}

function parsePlanIntervalMinutes(
  start?: string | null,
  endTime?: string | null
): [number, number] | null {
  const startMin = parseTimeToMinutes(start);
  const endMin = parseTimeToMinutes(endTime);
  if (startMin == null || endMin == null) return null;
  let endMinute = endMin;
  if (endMinute <= startMin) endMinute += 24 * 60;
  if (endMinute <= startMin) return null;
  return [startMin, endMinute];
}

/** 겹치는 plan 구간 병합 (연장수당 일별 근무시간 산출용) */
function mergeOverlappingMinuteIntervals(
  intervals: [number, number][]
): [number, number][] {
  if (!intervals.length) return [];
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [[sorted[0][0], sorted[0][1]]];
  for (let i = 1; i < sorted.length; i++) {
    const [start, end] = sorted[i];
    const last = merged[merged.length - 1];
    if (start <= last[1]) {
      last[1] = Math.max(last[1], end);
    } else {
      merged.push([start, end]);
    }
  }
  return merged;
}

/**
 * 동일 일자 B1/B2 plan 구간 병합 후 총 근무시간
 * ★ 동시 케어(겹치는 plan)는 연장수당 산출 시 중복 합산하지 않음
 */
export function calcMergedPlanDurationHoursForOvertime(
  rows: HomeWageClaimRow[]
): number {
  const intervals: [number, number][] = [];
  for (const row of rows) {
    const iv = parsePlanIntervalMinutes(row.plan_start_time, row.plan_end_time);
    if (iv) intervals.push(iv);
  }
  const merged = mergeOverlappingMinuteIntervals(intervals);
  const totalMinutes = merged.reduce((sum, [s, e]) => sum + (e - s), 0);
  return Math.max(0, totalMinutes / 60);
}

function intervalOverlapMinutes(
  a: [number, number],
  b: [number, number]
): number {
  const start = Math.max(a[0], b[0]);
  const end = Math.min(a[1], b[1]);
  return Math.max(0, end - start);
}

/** 일별 병합 근무시간에서 8시간 초과분이 해당하는 plan 분(마지막 시간대) */
function calcOvertimeMinuteWindow(
  merged: [number, number][],
  thresholdHours = HOME_WAGE_DAILY_OVERTIME_THRESHOLD_HOURS
): [number, number] | null {
  if (!merged.length) return null;
  const totalMinutes = merged.reduce((sum, [s, e]) => sum + (e - s), 0);
  const overtimeMinutes = totalMinutes - thresholdHours * 60;
  if (overtimeMinutes <= 0) return null;

  let remaining = overtimeMinutes;
  let windowStart = 0;
  let windowEnd = 0;

  for (let i = merged.length - 1; i >= 0 && remaining > 0; i--) {
    const [s, e] = merged[i];
    const span = e - s;
    if (span <= remaining) {
      windowStart = s;
      if (windowEnd === 0) windowEnd = e;
      remaining -= span;
    } else {
      windowStart = e - remaining;
      windowEnd = e;
      remaining = 0;
    }
  }

  return [windowStart, windowEnd];
}

/** 청구 행 고유 키 (연장 칩·행 매칭용) */
export function getHomeWageClaimRowKey(row: HomeWageClaimRow): string {
  if (row.clcd_idx != null && row.clcd_idx !== '') {
    return `clcd-${row.clcd_idx}`;
  }
  const clc = row.clc_idx != null ? String(row.clc_idx) : 'x';
  const start = String(row.plan_start_time || '').substring(0, 5);
  const end = String(row.plan_end_time || '').substring(0, 5);
  const name = String(row.recipient_name || '').trim();
  const date = String(row.date || '').substring(0, 10);
  return `clc-${clc}-${date}-${start}-${end}-${name}`;
}

/** 청구내역 — 서비스일자·plan 시작시간 순 */
export function sortClaimRowsByDateTime(rows: HomeWageClaimRow[]): HomeWageClaimRow[] {
  return [...rows].sort((a, b) => {
    const da = parseClaimDateKey(a.date) || '';
    const db = parseClaimDateKey(b.date) || '';
    if (da !== db) return da.localeCompare(db);
    const ta = parseTimeToMinutes(a.plan_start_time) ?? 0;
    const tb = parseTimeToMinutes(b.plan_start_time) ?? 0;
    if (ta !== tb) return ta - tb;
    const ea = parseTimeToMinutes(a.plan_end_time) ?? 0;
    const eb = parseTimeToMinutes(b.plan_end_time) ?? 0;
    return ea - eb;
  });
}

/** 계획 시작~종료 실제 근무시간(시간, 소수) */
export function calcPlanDurationHours(
  start?: string | null,
  end?: string | null
): number {
  if (!start || !end) return 0;
  const toMinutes = (t: string) => {
    const parts = String(t).split(':');
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    return h * 60 + m;
  };
  let diff = toMinutes(end) - toMinutes(start);
  if (diff < 0) diff += 24 * 60;
  return Math.max(0, diff / 60);
}

/** 방문요양(B1)·방문목욕(B2) 수가코드 여부 */
export function isHomeCareVisitFeeCode(feeCode?: string | null): boolean {
  const code = String(feeCode || '').trim().toUpperCase();
  return code.startsWith('B1') || code.startsWith('B2');
}

function parseTimeToMinutes(time?: string | null): number | null {
  if (!time) return null;
  const s = String(time).substring(0, 5);
  const match = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

/** 22:00~익일06:00 구간과 겹치는 근무시간(시간) */
export function calcNightOverlapHours(
  start?: string | null,
  end?: string | null
): number {
  const startMin = parseTimeToMinutes(start);
  const endMin = parseTimeToMinutes(end);
  if (startMin == null || endMin == null) return 0;

  let s = startMin;
  let e = endMin;
  if (e <= s) e += 24 * 60;

  const nightSegments: [number, number][] = [
    [HOME_WAGE_NIGHT_START_HOUR * 60, 24 * 60],
    [24 * 60, 24 * 60 + HOME_WAGE_NIGHT_END_HOUR * 60],
  ];

  let nightMinutes = 0;
  for (const [ns, ne] of nightSegments) {
    const overlapStart = Math.max(s, ns);
    const overlapEnd = Math.min(e, ne);
    if (overlapEnd > overlapStart) {
      nightMinutes += overlapEnd - overlapStart;
    }
  }
  return Math.round((nightMinutes / 60) * 10) / 10;
}

function parseClaimDateKey(date?: string | null): string | null {
  if (!date) return null;
  const s = String(date).substring(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

/** @deprecated 휴일수당 산출에 사용하지 않음 — 달력 공휴일명 표시용만. 휴일수당은 `isHolidayFeeSurcharge` 기준 */
export function isHomeWageHolidayWorkDay(
  dateKey: string,
  holidayMap: Record<string, string> = {}
): boolean {
  if (holidayMap[dateKey]) return true;
  const [y, m, d] = dateKey.split('-').map((v) => parseInt(v, 10));
  return new Date(y, m - 1, d).getDay() === 0;
}

/** B1/B2 청구 건이 휴일수당(연·야·휴) 시간 집계 대상인지 */
export function getMayFirstWeekday(year: number): number {
  return new Date(year, 4, 1).getDay();
}

export function isMayFirstSaturday(year: number): boolean {
  return getMayFirstWeekday(year) === 6;
}

export function isMayFirstSunday(year: number): boolean {
  return getMayFirstWeekday(year) === 0;
}

/** 해당 급여연월 5/1 청구 일자인지 */
export function isHomeWageMayFirstClaimDate(
  dateKey: string | null | undefined,
  yearMonth: string
): boolean {
  if (!dateKey) return false;
  const ym = String(yearMonth).replace(/\D/g, '').slice(0, 6);
  if (ym.length !== 6 || ym.slice(4, 6) !== '05') return false;
  return dateKey === `${ym.slice(0, 4)}-05-01`;
}

/**
 * 휴일수당(0.5) 시간 집계 대상 청구 건
 * - 일반: 수가명 일요일가산·공휴일가산
 * - 5/1(노동절): **월~토** 실근무 plan → 휴일시간 (수가명 무관)
 *   → 실근무: 250% (기본100 + 휴일0.5 + laborDay100)
 * - 일요일 5/1: 실근무 시만 plan → 휴일시간 (미근무 청구 없음)
 */
export function isHomeWageHolidayPremiumClaim(
  row: Pick<HomeWageClaimRow, 'fee_code' | 'fee_name' | 'date'>,
  yearMonth = ''
): boolean {
  if (!isHomeCareVisitFeeCode(row.fee_code)) return false;

  const dateKey = parseClaimDateKey(row.date);
  if (yearMonth && isHomeWageMayFirstClaimDate(dateKey, yearMonth)) {
    return true;
  }

  return isHolidayFeeSurcharge(row.fee_name);
}

export interface HomeWagePremiumHoursSummary {
  overtimeHours: number;
  nightHours: number;
  holidayHours: number;
}

/** 연·야·휴 시간 파싱 — 0·빈값·"0.00"은 0 */
export function parseHomeWagePremiumHours(
  hours: string | number | null | undefined
): number {
  if (hours === null || hours === undefined || hours === '') return 0;
  const n = typeof hours === 'number'
    ? hours
    : parseFloat(String(hours).replace(/,/g, '').trim());
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 10) / 10;
}

/** 연·야·휴 시간 표시 — 0·빈값·"0.00"은 빈 문자열, 소수 첫째 자리까지 (46.5→47 반올림 금지) */
export function formatHomeWagePremiumHoursDisplay(
  hours: string | number | null | undefined
): string {
  const parsed = parseHomeWagePremiumHours(hours);
  return formatHoursDisplay(parsed);
}

/**
 * 청구(plan 시간) 기준 연·야·휴 시간 산출 (방문요양·목욕 B1/B2만)
 * - 연장: 일별 8시간 초과분 — **동일 일자 plan 구간 병합**(동시 케어 중복 제외)
 * - 야간: 22:00~06:00 겹침 합계 (건당 합산)
 * - 휴일: 수가명 **일요일가산·공휴일가산** 또는 **5/1 실근무** plan 시간
 * - ★ 야간·휴일은 건별 독립 집계 — 연장만 일별 병합
 */
export interface HomeWageDayPremiumDetail {
  dateKey: string;
  totalVisitHours: number;
  overtimeHours: number;
  nightHours: number;
  holidayHours: number;
}

export interface HomeWagePremiumDetailsFromClaims {
  summary: HomeWagePremiumHoursSummary;
  byDate: HomeWageDayPremiumDetail[];
  /** 일 8시간 초과분이 있는 날 (YYYY-MM-DD) */
  overtimeDayKeys: Set<string>;
  /** 연장수당 대상 plan 구간과 겹치는 청구 행 키 */
  overtimeRowKeys: Set<string>;
}

export function calcPremiumDetailsFromClaims(
  rows: HomeWageClaimRow[],
  yearMonth = '',
  viewMode: HomeWagePayrollViewMode = 'admin'
): HomeWagePremiumDetailsFromClaims {
  const visitRows = rows.filter((row) => isHomeCareVisitFeeCode(row.fee_code));
  const rowsByDate = new Map<string, HomeWageClaimRow[]>();
  const nightByDate = new Map<string, number>();
  const holidayByDate = new Map<string, number>();
  let nightHours = 0;
  let holidayHours = 0;

  for (const row of visitRows) {
    const dateKey = parseClaimDateKey(row.date);
    const planHours = calcPlanDurationHours(row.plan_start_time, row.plan_end_time);
    if (planHours <= 0) continue;

    const rowNight = calcNightOverlapHours(row.plan_start_time, row.plan_end_time);
    nightHours += rowNight;

    if (dateKey) {
      if (!rowsByDate.has(dateKey)) rowsByDate.set(dateKey, []);
      rowsByDate.get(dateKey)!.push(row);

      if (rowNight > 0) {
        nightByDate.set(dateKey, (nightByDate.get(dateKey) || 0) + rowNight);
      }
      if (isHolidayPremiumClaimForViewMode(row, yearMonth, viewMode)) {
        holidayHours += planHours;
        holidayByDate.set(dateKey, (holidayByDate.get(dateKey) || 0) + planHours);
      }
    }
  }

  const overtimeDayKeys = new Set<string>();
  const overtimeRowKeys = new Set<string>();
  let overtimeHours = 0;
  const byDate: HomeWageDayPremiumDetail[] = [];

  for (const [dateKey, dayRows] of rowsByDate.entries()) {
    const mergedDayHours = calcMergedPlanDurationHoursForOvertime(dayRows);
    const dayOvertime = Math.max(
      0,
      mergedDayHours - HOME_WAGE_DAILY_OVERTIME_THRESHOLD_HOURS
    );
    overtimeHours += dayOvertime;
    if (dayOvertime > 0) overtimeDayKeys.add(dateKey);

    const intervals: [number, number][] = [];
    for (const row of dayRows) {
      const iv = parsePlanIntervalMinutes(row.plan_start_time, row.plan_end_time);
      if (iv) intervals.push(iv);
    }
    const mergedIntervals = mergeOverlappingMinuteIntervals(intervals);
    const overtimeWindow = calcOvertimeMinuteWindow(mergedIntervals);
    if (overtimeWindow) {
      for (const row of dayRows) {
        const rowIv = parsePlanIntervalMinutes(row.plan_start_time, row.plan_end_time);
        if (!rowIv) continue;
        if (intervalOverlapMinutes(rowIv, overtimeWindow) > 0) {
          overtimeRowKeys.add(getHomeWageClaimRowKey(row));
        }
      }
    }

    byDate.push({
      dateKey,
      totalVisitHours: Math.round(mergedDayHours * 10) / 10,
      overtimeHours: Math.round(dayOvertime * 10) / 10,
      nightHours: Math.round((nightByDate.get(dateKey) || 0) * 10) / 10,
      holidayHours: Math.round((holidayByDate.get(dateKey) || 0) * 10) / 10,
    });
  }

  byDate.sort((a, b) => a.dateKey.localeCompare(b.dateKey));

  return {
    summary: {
      overtimeHours: Math.round(overtimeHours * 10) / 10,
      nightHours: Math.round(nightHours * 10) / 10,
      holidayHours: Math.round(holidayHours * 10) / 10,
    },
    byDate,
    overtimeDayKeys,
    overtimeRowKeys,
  };
}

export function calcPremiumHoursFromClaims(
  rows: HomeWageClaimRow[],
  yearMonth = '',
  viewMode: HomeWagePayrollViewMode = 'admin'
): HomeWagePremiumHoursSummary {
  return calcPremiumDetailsFromClaims(rows, yearMonth, viewMode).summary;
}

/** 청구 1건의 연·야·휴 해당 여부 (근무달력·청구목록 표시용) */
export interface HomeWageClaimRowPremiumInfo {
  isVisitFee: boolean;
  planHours: number;
  nightHours: number;
  isHolidayWork: boolean;
  /** plan 시간이 일 8시간 초과분(마지막 시간대)과 겹침 → 연장수당 대상 건 */
  isOvertimeRow: boolean;
}

export function getClaimRowPremiumInfo(
  row: HomeWageClaimRow,
  overtimeRowKeys: Set<string>,
  yearMonth = '',
  viewMode: HomeWagePayrollViewMode = 'admin'
): HomeWageClaimRowPremiumInfo {
  const isVisitFee = isHomeCareVisitFeeCode(row.fee_code);
  const planHours = isVisitFee
    ? calcPlanDurationHours(row.plan_start_time, row.plan_end_time)
    : 0;
  const nightHours = isVisitFee
    ? calcNightOverlapHours(row.plan_start_time, row.plan_end_time)
    : 0;
  const isHolidayWork = isHolidayPremiumClaimForViewMode(row, yearMonth, viewMode);
  const isOvertimeRow = isVisitFee && overtimeRowKeys.has(getHomeWageClaimRowKey(row));

  return {
    isVisitFee,
    planHours,
    nightHours,
    isHolidayWork,
    isOvertimeRow,
  };
}

/** 수가명에서 분 단위 추출 (예: 150분이상 → 150) — 방문요양(B1) 등 일반용 */
export function parseFeeNameMinutes(feeName?: string | null): number | null {
  if (!feeName) return null;
  // ★ 270분초과: 등급명(270분 넘음)이지 1회 270분(4.5h) 고정 시간이 아님
  if (isOver270Fee(feeName, null)) return null;
  const m = String(feeName).match(/(\d+)\s*분/);
  if (!m) return null;
  const minutes = parseInt(m[1], 10);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
}

/** `40분` · `1시간 20분` 등 수가명 시간 표기 → 분 */
export function parseDurationLabelToMinutes(label?: string | null): number | null {
  const s = String(label || '').trim();
  if (!s) return null;
  const hourMin = s.match(/(?:(\d+)\s*시간\s*)?(\d+)\s*분/);
  if (hourMin) {
    const hours = parseInt(hourMin[1] || '0', 10) || 0;
    const mins = parseInt(hourMin[2], 10) || 0;
    const total = hours * 60 + mins;
    return total > 0 ? total : null;
  }
  return null;
}

const VISIT_BATH_RANGE_FEE_PATTERN =
  /((?:\d+\s*시간\s*)?\d+\s*분)\s*이상[\s,]*(?:(?:\d+\s*시간\s*)?\d+\s*분)\s*(?:미만|이하)/;

/** 방문목욕(B2) 수가명 구간형 — `40분이상 60분미만` · `1시간20분이상 1시간40분미만` */
export function isVisitBathRangeFee(
  feeName?: string | null,
  feeCode?: string | null
): boolean {
  const code = String(feeCode || '').trim().toUpperCase();
  if (!code.startsWith('B2')) return false;
  const name = String(feeName || '').trim();
  if (!name) return false;
  return VISIT_BATH_RANGE_FEE_PATTERN.test(name);
}

/**
 * 구간형 방문목욕 수가 — 구간 하한 분
 * 예: `40분이상 60분미만` → 40 · `1시간20분이상 1시간40분미만` → 80
 */
export function parseVisitBathRangeMinMinutes(
  feeName?: string | null,
  feeCode?: string | null
): number | null {
  if (!isVisitBathRangeFee(feeName, feeCode)) return null;
  const rangeMatch = String(feeName || '').match(VISIT_BATH_RANGE_FEE_PATTERN);
  if (!rangeMatch) return null;
  return parseDurationLabelToMinutes(rangeMatch[1]);
}

/** @deprecated 구간형만 사용 — parseVisitBathRangeMinMinutes */
export function parseVisitBathFixedMinutes(
  feeName?: string | null,
  feeCode?: string | null
): number | null {
  return parseVisitBathRangeMinMinutes(feeName, feeCode);
}

/** 분 → 시간 (30분 내림 없음, 방문목욕 고정 분용) */
export function minutesToExactHours(minutes: number): number {
  if (!Number.isFinite(minutes) || minutes <= 0) return 0;
  return minutes / 60;
}

/** 시간(소수)을 30분 단위로 내림 — 7.4→7.0, 7.6→7.5 */
export function floorHoursToHalfHour(hours: number): number {
  if (!Number.isFinite(hours) || hours <= 0) return 0;
  return Math.floor(hours * 2) / 2;
}

/** 실제 서비스 시간(시간) — service_start/end_time, 30분 단위 내림 */
export function calcServiceDurationHours(
  start?: string | null,
  end?: string | null
): number {
  if (!start || !end) return 0;
  const toMinutes = (t: string) => {
    const parts = String(t).split(':');
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    return h * 60 + m;
  };
  let diff = toMinutes(end) - toMinutes(start);
  if (diff < 0) diff += 24 * 60;
  return Math.floor(diff / 30) * 0.5;
}

/** 실제/계획 시간(시간) — 분 단위 정확 환산 (방문목욕 B2 구간용) */
export function calcServiceDurationExactHours(
  start?: string | null,
  end?: string | null
): number {
  if (!start || !end) return 0;
  const toMinutes = (t: string) => {
    const parts = String(t).split(':');
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    return h * 60 + m;
  };
  let diff = toMinutes(end) - toMinutes(start);
  if (diff < 0) diff += 24 * 60;
  if (diff <= 0) return 0;
  return diff / 60;
}

export function isVisitBathFeeCode(feeCode?: string | null): boolean {
  return String(feeCode || '').trim().toUpperCase().startsWith('B2');
}

/**
 * 청구내역 모달 「서비스시간」과 동일 기준 — plan 우선, 30분 단위 내림
 */
export function calcClaimModalServiceHours(row: HomeWageClaimRow): number {
  const planHours = calcServiceDurationHours(row.plan_start_time, row.plan_end_time);
  if (planHours > 0) return planHours;
  const serviceHours = calcServiceDurationHours(row.service_start_time, row.service_end_time);
  if (serviceHours > 0) return serviceHours;
  if (isOver270Fee(row.fee_name, row.fee_code)) return 0;
  const minutes = parseFeeNameMinutes(row.fee_name);
  if (minutes != null) return floorHoursToHalfHour(minutes / 60);
  return 0;
}

/**
 * 240분(4h) 초과 · 8h 미만 — 휴게 30분(0.5h) 차감
 * (청구내역 모달 서비스시간 30분 내림 값 기준)
 */
export function applyHomeWageLongVisitBreakDeduction(hours: number): number {
  const h = Number(hours);
  if (!Number.isFinite(h) || h <= 0) return 0;
  if (h > HOME_WAGE_BREAK_AFTER_HOURS && h < HOME_WAGE_BREAK_THRESHOLD_HOURS) {
    return Math.max(
      0,
      Math.round((h - HOME_WAGE_BREAK_DEDUCT_HOURS) * 10) / 10
    );
  }
  return h;
}

/** @deprecated applyHomeWageLongVisitBreakDeduction — feeName/feeCode 무시 */
export function applyHomeWageOver270BreakDeduction(
  hours: number,
  _feeName?: string | null,
  _feeCode?: string | null
): number {
  return applyHomeWageLongVisitBreakDeduction(hours);
}

/**
 * 1건 청구의 1회당 수가시간(시간)
 * ★ B2 구간 수가: 수가명 구간 하한 고정
 * ★ 그 외: 청구내역 모달 서비스시간(plan 30분 내림) → 240분 초과·8h 미만 시 30분 차감
 */
function calcSugaHoursPerVisit(
  row: HomeWageClaimRow,
  _caregiverOver270PlanHours: number = 0
): number {
  if (isSevereRecipientSurcharge(row.fee_name, row.fee_code)) {
    return 0;
  }

  if (isVisitBathRangeFee(row.fee_name, row.fee_code)) {
    const rangeMin = parseVisitBathRangeMinMinutes(row.fee_name, row.fee_code);
    if (rangeMin != null) return minutesToExactHours(rangeMin);
    return 0;
  }

  const hours = calcClaimModalServiceHours(row);
  return applyHomeWageLongVisitBreakDeduction(hours);
}

export { calcSugaHoursPerVisit };

/** 그룹 평균 1회 수가시간 — B2 구간 수가는 하한 고정, 그 외 30분 단위 내림 */
export function resolveGroupSugaHoursPerVisit(
  feeCode: string,
  feeName: string,
  sugaHoursSum: number,
  serviceCount: number
): number {
  if (serviceCount <= 0) return 0;
  if (isVisitBathRangeFee(feeName, feeCode)) {
    const rangeMin = parseVisitBathRangeMinMinutes(feeName, feeCode);
    if (rangeMin != null) return minutesToExactHours(rangeMin);
  }
  const avg = sugaHoursSum / serviceCount;
  return floorHoursToHalfHour(avg);
}

/** 270분초과 plan: 30분 단위 내림 (집계 참고용) */
export function calcOver270SugaHoursPerVisit(
  start?: string | null,
  end?: string | null
): number {
  return calcServiceDurationHours(start, end);
}

/** 당월 요양보호사 270분초과 계획시간 합계(차감 전, 30분절사 단위 합) */
export function sumCaregiverOver270PlanHours(rows: HomeWageClaimRow[]): number {
  let total = 0;
  for (const row of rows) {
    if (!isOver270Fee(row.fee_name, row.fee_code)) continue;
    total += calcOver270SugaHoursPerVisit(row.plan_start_time, row.plan_end_time);
  }
  return Math.round(total * 10) / 10;
}

/** 집계 그룹 키: 수급자(인정번호) + 수가코드 + 수가금액 (수가금액이 다르면 별도 row) */
export function buildRecipientFeeGroupKey(row: HomeWageClaimRow): string {
  const name = String(row.recipient_name || '').trim();
  const cert = String(row.cert_number || '').trim();
  const code = String(row.fee_code || '').trim() || '_';
  const fee = String(row.fee_amount || 0);
  return `${name}|${cert}|${code}|${fee}`;
}

/**
 * 청구 행 → 수급자·수가코드별 집계
 * - 일반·휴일가산: 합계금액 = 서비스횟수 × 수가시간(1회) × 시급
 * - 중증수급자 가산: 합계금액 = 서비스횟수 × clcd_fee(1회당 수가금액, 동일 가정 시)
 */
export function aggregateRecipientFeeSummary(
  rows: HomeWageClaimRow[],
  hourlyWageByGroup: Record<string, number> = {},
  severeFeeByGroup: Record<string, number> = {}
): HomeWageRecipientFeeSummary[] {
  if (!rows.length) return [];

  // ★ 수가명/수가코드가 없는 행은 수급자별 시급 집계에서 제외
  // (청구내역에만 있고 청구내역상세에 연결되지 않은 경우)
  const validRows = rows.filter((row) => {
    const feeName = String(row.fee_name || '').trim();
    const feeCode = String(row.fee_code || '').trim();
    return feeName.length > 0 || feeCode.length > 0;
  });

  if (!validRows.length) return [];

  const over270Total = sumCaregiverOver270PlanHours(validRows);
  const map = new Map<
    string,
    {
      recipientName: string;
      certNumber: string;
      feeName: string;
      feeCode: string;
      isSevereRecipientSurcharge: boolean;
      isHolidayFeeSurcharge: boolean;
      serviceCount: number;
      sugaHoursSum: number;
      feeAmountSum: number;
    }
  >();

  for (const row of validRows) {
    const key = buildRecipientFeeGroupKey(row);
    const isSevere = isSevereRecipientSurcharge(row.fee_name, row.fee_code);
    const isHoliday = isHolidayFeeSurcharge(row.fee_name);
    const perVisit = calcSugaHoursPerVisit(row, over270Total);
    const feeAmt = Number(row.fee_amount) || 0;
    const existing = map.get(key);
    if (existing) {
      existing.serviceCount += 1;
      existing.sugaHoursSum += perVisit;
      existing.feeAmountSum += feeAmt;
      if (!existing.feeName && row.fee_name) existing.feeName = String(row.fee_name);
    } else {
      map.set(key, {
        recipientName: String(row.recipient_name || '').trim(),
        certNumber: String(row.cert_number || '').trim(),
        feeName: String(row.fee_name || '').trim(),
        feeCode: String(row.fee_code || '').trim(),
        isSevereRecipientSurcharge: isSevere,
        isHolidayFeeSurcharge: isHoliday,
        serviceCount: 1,
        sugaHoursSum: perVisit,
        feeAmountSum: feeAmt,
      });
    }
  }

  const result: HomeWageRecipientFeeSummary[] = [];
  for (const [groupKey, g] of map) {
    const sugaHoursPerVisit = resolveGroupSugaHoursPerVisit(
      g.feeCode,
      g.feeName,
      g.sugaHoursSum,
      g.serviceCount
    );
    const feeAmountPerVisit =
      g.serviceCount > 0 ? Math.round(g.feeAmountSum / g.serviceCount) : 0;

    let hourlyWage = 0;
    let totalAmount = 0;

    if (g.isSevereRecipientSurcharge) {
      const feePerVisit =
        severeFeeByGroup[groupKey] != null && severeFeeByGroup[groupKey] > 0
          ? severeFeeByGroup[groupKey]
          : feeAmountPerVisit;
      totalAmount = Math.round(g.serviceCount * feePerVisit);
      result.push({
        groupKey,
        recipientName: g.recipientName,
        certNumber: g.certNumber,
        feeName: g.feeName,
        feeCode: g.feeCode,
        serviceCount: g.serviceCount,
        isSevereRecipientSurcharge: true,
        isHolidayFeeSurcharge: false,
        sugaHoursPerVisit: 0,
        feeAmountPerVisit: feePerVisit,
        hourlyWage: 0,
        holidayPayAmount: 0,
        totalAmount,
      });
      continue;
    }

    hourlyWage = hourlyWageByGroup[groupKey] ?? 0;
    const pay = calcRecipientFeeRowPayAmount({
      serviceCount: g.serviceCount,
      sugaHoursPerVisit,
      hourlyWage,
      feeAmountPerVisit,
    });
    totalAmount = pay.totalAmount;

    result.push({
      groupKey,
      recipientName: g.recipientName,
      certNumber: g.certNumber,
      feeName: g.feeName,
      feeCode: g.feeCode,
      serviceCount: g.serviceCount,
      isSevereRecipientSurcharge: false,
      isHolidayFeeSurcharge: g.isHolidayFeeSurcharge,
      sugaHoursPerVisit,
      feeAmountPerVisit,
      hourlyWage,
      holidayPayAmount: pay.holidayPayAmount,
      totalAmount,
    });
  }

  result.sort((a, b) => {
    const n = a.recipientName.localeCompare(b.recipientName, 'ko');
    if (n !== 0) return n;
    return a.feeCode.localeCompare(b.feeCode, 'ko');
  });

  return result;
}

// ─────────────────────────────────────────────
// 3. 연차 일수 (근속 기준, 월말 시점)
// ─────────────────────────────────────────────
//
// | 구분 | 연차 일수 |
// |------|-----------|
// | 1년차 (12개월 미만) | 1개월당 1일 (0개월 0일, 1개월 이상 1일~11개월 11일) |
// | 2~3년차 | 15일 |
// | 4~5년차 | 16일 |
// | 6~7년차 | 17일 |
// | 8~9년차 | 18일 |
// | 10~11년차 | 19일 |
// | 12~13년차 | 20일 |
// | 14~15년차 | 21일 |
// | 16~17년차 | 22일 |
// | 18~19년차 | 23일 |
// | 20년차 | 24일 |
// | 21년차 이상 | 25일 |
// ─────────────────────────────────────────────

/** 주휴수당 = 기본급 × 0.2 */
export const HOME_WAGE_WEEKLY_HOLIDAY_RATE = 0.2;

/** 센터장/사복 등 — 연장·휴일 1.5배, 야간 0.5배 */
export const HOME_WAGE_OVERTIME_RATE = 1.5;
export const HOME_WAGE_NIGHT_RATE = 0.5;
export const HOME_WAGE_HOLIDAY_RATE = 1.5;

/** 요양보호사(방문요양·목욕 B1/B2) — 연·야·휴 가산율: 시급 × 0.5 × 시간 */
export const HOME_WAGE_CAREGIVER_PREMIUM_RATE = 0.5;

/** 일별 연장수당 기준: 하루 8시간 초과분 */
export const HOME_WAGE_DAILY_OVERTIME_THRESHOLD_HOURS = 8;

/** 야간수당: 22:00 ~ 익일 06:00 */
export const HOME_WAGE_NIGHT_START_HOUR = 22;
export const HOME_WAGE_NIGHT_END_HOUR = 6;

/** 입사일 문자열(YYYY-MM-DD) */
export function parseJoinDate(joinDate?: string | null): string | null {
  if (!joinDate) return null;
  const s = String(joinDate).substring(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

/** 동일 기관 · 65세 이전 입사 → 만 65세 이후에도 고용보험 유지 */
export const HOME_WAGE_EMPLOYMENT_CONTINUITY_BEFORE_65_MESSAGE =
  '★ 동일 기관 · 65세 이전 입사 → 고용보험 유지';

/**
 * 방문요양 임금설정 — 나이 기반 4대보험 면제 + 고용보험 연속 가입 예외
 *
 * 만 65세 이상이라도 **입사일 기준 만 65세 미만**에 동일 기관에 입사한 경우
 * 고용보험(근로·기관)을 면제하지 않고 계속 산출한다.
 * (만 65세 이후 신규 입사·입사일 미상 → 기존처럼 면제)
 */
export function resolveHomeWageAgeInsurance(
  birthDate: string | null | undefined,
  year: number,
  month: number,
  joinDate?: string | null
): AgeInsuranceValidation {
  const base = validateAgeInsurance(birthDate, year, month);
  if (!base.employmentExempt) return base;

  const parsedJoin = parseJoinDate(joinDate);
  if (!parsedJoin) return base;

  const ageAtJoin = calcAge(birthDate, new Date(parsedJoin));
  if (ageAtJoin === null || ageAtJoin >= AGE_EMPLOYMENT_EXEMPT) return base;

  return {
    ...base,
    employmentExempt: false,
    employmentMessage: [
      HOME_WAGE_EMPLOYMENT_CONTINUITY_BEFORE_65_MESSAGE,
      `입사 ${parsedJoin} (만 ${ageAtJoin}세) → 현재 만 ${base.age}세, 고용보험 적용`,
    ].join('\n'),
  };
}

/**
 * 입사일 ~ 기준연월 말일까지 근속 개월 수 (말일 포함, 미만 1개월은 0)
 */
export function calcTenureMonths(
  joinDate: string,
  asOfYear: number,
  asOfMonth: number
): number {
  const [jy, jm, jd] = joinDate.split('-').map((v) => parseInt(v, 10));
  const lastDay = new Date(asOfYear, asOfMonth, 0).getDate();
  const endY = asOfYear;
  const endM = asOfMonth;
  const endD = lastDay;

  if (endY < jy || (endY === jy && endM < jm)) return 0;
  if (endY === jy && endM === jm && endD < jd) return 0;

  let months = (endY - jy) * 12 + (endM - jm);
  if (endD < jd) months -= 1;
  return Math.max(0, months);
}

/**
 * 연차 일수 (기준: 해당 연월 말일)
 */
export function getAnnualLeaveDays(
  joinDate: string | null | undefined,
  asOfYear: number,
  asOfMonth: number
): number {
  const parsed = parseJoinDate(joinDate);
  if (!parsed) return 0;

  const tenureMonths = calcTenureMonths(parsed, asOfYear, asOfMonth);
  if (tenureMonths < 1) return 0;
  if (tenureMonths < 12) return Math.min(11, tenureMonths);

  const years = Math.floor(tenureMonths / 12);
  if (years >= 21) return 25;
  if (years >= 20) return 24;
  if (years >= 2) {
    const band = Math.floor((years - 2) / 2);
    return 15 + Math.min(band, 8);
  }
  return 15;
}

// ─────────────────────────────────────────────
// 4. 지급 항목 계산 (요양보호사 월별 임금설정)
// ─────────────────────────────────────────────
//
// 관계:
// - 수급자별 표 시급: 수급자·수가코드별 청구 산정 → 표 합계금액 = 지급합계(월급여)
// - 지급계산시급: 기본급·주휴·연차 분해용 시급(표 시급과 별개)
// - 기본급·주휴 = 지급계산시급 기준, 표 합계와의 차액은 연차수당(또는 역산 조정)에 반영
// - 지급합계 = 기본급 + 주휴수당 + 연차수당 (+ 추후 추가항목) = 표 합계
//
// [최저시급 기준] 기본급·주휴·연차 합이 월급여 미달 시:
// - 연차비율 = 연차일수 / 12 / 근무일수
// - totalRatio = 1 + 0.2 + 연차비율
// - 조정시급 = floor10(월급여 / (총수가시간 × totalRatio))
// - 조정시급으로 기본급·주휴·연차 재계산 후, 차액은 연차수당에 가산
// ─────────────────────────────────────────────

/**
 * 수급자별 집계 → DB cwhd_svc / 명세서 PDF용 문자열 (JSON)
 * ★ 화면 totalAmount·시급·횟수·시간을 그대로 저장 — 명세서 재계산 없음
 */
export function serializeHomeWageRecipientSummaryToSvc(
  rows: HomeWageRecipientFeeSummary[]
): string {
  if (!rows?.length) return '';

  const payload = rows
    .filter((r) => String(r.recipientName || '').trim())
    .map((r) => ({
      patientName: String(r.recipientName || '').trim(),
      sugaCode: String(r.feeCode || '').trim(),
      sugaName: String(r.feeName || '').trim(),
      timePay: r.isSevereRecipientSurcharge
        ? Math.round(r.feeAmountPerVisit || 0)
        : Math.round(r.hourlyWage || 0),
      feePerVisit: Math.round(r.feeAmountPerVisit || 0),
      serviceDays: Math.round(r.serviceCount || 0),
      sugaHours: r.isSevereRecipientSurcharge ? 0 : (r.sugaHoursPerVisit || 0),
      amount: Math.round(r.totalAmount || 0),
      isSevere: !!r.isSevereRecipientSurcharge,
    }));

  return payload.length > 0 ? JSON.stringify(payload) : '';
}

/** DB cwhd_svc → 화면 수급자별 집계 (저장 스냅샷 — 구간형 B2 수가시간은 수가명 하한 보정) */
export function resolveRecipientSummarySugaHoursPerVisit(
  feeName: string,
  feeCode: string,
  storedHours: number,
  isSevere: boolean
): number {
  if (isSevere) return 0;
  if (isVisitBathRangeFee(feeName, feeCode)) {
    const rangeMin = parseVisitBathRangeMinMinutes(feeName, feeCode);
    if (rangeMin != null) return minutesToExactHours(rangeMin);
  }
  return Number(storedHours) || 0;
}

export function parseHomeWageRecipientSummaryFromSvc(
  svc: string | null | undefined
): HomeWageRecipientFeeSummary[] {
  if (!svc || !String(svc).trim()) return [];
  const raw = String(svc).trim();
  if (!raw.startsWith('[')) return [];

  try {
    const arr = JSON.parse(raw) as Array<{
      patientName?: string;
      sugaCode?: string;
      sugaName?: string;
      timePay?: number;
      feePerVisit?: number;
      serviceDays?: number;
      serviceCount?: number;
      sugaHours?: number;
      amount?: number;
      isSevere?: boolean;
    }>;
    if (!Array.isArray(arr)) return [];

    return arr
      .filter((item) => String(item?.patientName || '').trim())
      .map((item, idx) => {
        const feeName = String(item.sugaName || '').trim();
        const feeCode = String(item.sugaCode || '').trim();
        const isSevere =
          item.isSevere === true || isSevereRecipientSurcharge(feeName, feeCode);
        const serviceCount = Number(item.serviceDays ?? item.serviceCount) || 0;
        const timePay = Number(item.timePay) || 0;
        const sugaHoursPerVisit = resolveRecipientSummarySugaHoursPerVisit(
          feeName,
          feeCode,
          Number(item.sugaHours) || 0,
          isSevere
        );
        const hourlyWage = isSevere ? 0 : timePay;
        const feeAmountPerVisit = isSevere
          ? timePay
          : (Number(item.feePerVisit) || 0);
        const totalAmount = isSevere
          ? Math.round(serviceCount * timePay)
          : calcRecipientFeeRowPayAmount({
              serviceCount,
              sugaHoursPerVisit,
              hourlyWage,
              feeAmountPerVisit,
            }).totalAmount;
        return {
          groupKey: `svc-${idx}|${String(item.patientName).trim()}|${feeCode}|${timePay}`,
          recipientName: String(item.patientName || '').trim(),
          certNumber: '',
          feeName,
          feeCode,
          serviceCount,
          isSevereRecipientSurcharge: isSevere,
          isHolidayFeeSurcharge: isHolidayFeeSurcharge(feeName),
          sugaHoursPerVisit,
          feeAmountPerVisit,
          hourlyWage,
          holidayPayAmount: 0,
          totalAmount,
        };
      });
  } catch {
    return [];
  }
}

/** 수급자별 집계 행 매칭 키 (groupKey와 무관 — cwhd_svc 스냅샷 병합용) */
export function recipientSummaryMatchKey(
  row: Pick<
    HomeWageRecipientFeeSummary,
    'recipientName' | 'feeCode' | 'feeName' | 'isSevereRecipientSurcharge'
  >
): string {
  const name = String(row.recipientName || '').trim();
  const code = String(row.feeCode || '').trim();
  const feeName = String(row.feeName || '').trim();
  const severe = row.isSevereRecipientSurcharge ? '1' : '0';
  return `${name}|${code}|${feeName}|${severe}`;
}

/**
 * 비중증 행 — 시급×수가시간×횟수로 합계 재계산 (저장 스냅샷 amount 구로직 잔존 방지)
 */
export function recalcRecipientSummaryRowTotal(
  row: HomeWageRecipientFeeSummary
): HomeWageRecipientFeeSummary {
  if (row.isSevereRecipientSurcharge) return row;
  const hourlyWage = Number(row.hourlyWage) || 0;
  const sugaHoursPerVisit = Number(row.sugaHoursPerVisit) || 0;
  const serviceCount = Number(row.serviceCount) || 0;
  if (hourlyWage <= 0 || sugaHoursPerVisit <= 0 || serviceCount <= 0) return row;
  const pay = calcRecipientFeeRowPayAmount({
    serviceCount,
    sugaHoursPerVisit,
    hourlyWage,
    feeAmountPerVisit: row.feeAmountPerVisit,
  });
  if (pay.totalAmount === row.totalAmount) return row;
  return { ...row, totalAmount: pay.totalAmount };
}

/**
 * 청구 집계(live) + DB cwhd_svc 스냅샷 병합
 * - 1회수가·수가시간·서비스횟수: 청구 집계 우선 (오조인·재크롤 후 svc 구스냅샷 보정)
 * - 시급: 저장 스냅샷 우선
 * - 합계: 시급×수가시간×횟수 재계산
 */
export function mergeLiveClaimSummaryWithDbSnapshot(
  live: HomeWageRecipientFeeSummary[],
  saved: HomeWageRecipientFeeSummary[]
): HomeWageRecipientFeeSummary[] {
  if (!live.length) return saved;
  if (!saved.length) return live;

  const snapByKey = new Map<string, HomeWageRecipientFeeSummary>();
  for (const row of saved) {
    snapByKey.set(recipientSummaryMatchKey(row), row);
  }

  return live.map((row) => {
    const snap = snapByKey.get(recipientSummaryMatchKey(row));
    if (!snap) return recalcRecipientSummaryRowTotal(row);

    if (row.isSevereRecipientSurcharge) {
      return {
        ...row,
        feeAmountPerVisit:
          snap.feeAmountPerVisit > 0 ? snap.feeAmountPerVisit : row.feeAmountPerVisit,
        serviceCount: snap.serviceCount > 0 ? snap.serviceCount : row.serviceCount,
        totalAmount: snap.totalAmount > 0 ? snap.totalAmount : row.totalAmount,
      };
    }

    const merged: HomeWageRecipientFeeSummary = {
      ...row,
      feeAmountPerVisit:
        row.feeAmountPerVisit > 0 ? row.feeAmountPerVisit : snap.feeAmountPerVisit,
      sugaHoursPerVisit:
        row.sugaHoursPerVisit > 0 ? row.sugaHoursPerVisit : snap.sugaHoursPerVisit,
      hourlyWage: snap.hourlyWage > 0 ? snap.hourlyWage : row.hourlyWage,
      serviceCount: row.serviceCount > 0 ? row.serviceCount : snap.serviceCount,
      totalAmount: row.totalAmount,
    };
    return recalcRecipientSummaryRowTotal(merged);
  });
}

/** 집계 행 기준 총수가시간(시간) — 중증가산 제외 */
export function calcTotalSugaHoursFromSummary(
  rows: HomeWageRecipientFeeSummary[]
): number {
  let total = 0;
  for (const row of rows) {
    if (row.isSevereRecipientSurcharge) continue;
    total += row.serviceCount * row.sugaHoursPerVisit;
  }
  return Math.round(total * 10) / 10;
}

/** 청구 행 — 중증수급자가산 제외 */
export function filterNonSevereClaimRows(rows: HomeWageClaimRow[]): HomeWageClaimRow[] {
  return rows.filter((r) => !isSevereRecipientSurcharge(r.fee_name, r.fee_code));
}

/** 청구 행 — 중증수급자가산만 */
export function filterSevereClaimRows(rows: HomeWageClaimRow[]): HomeWageClaimRow[] {
  return rows.filter((r) => isSevereRecipientSurcharge(r.fee_name, r.fee_code));
}

/** 수급자별 집계 행 — 중증수급자가산 제외 */
export function filterNonSevereSummaryRows(
  rows: HomeWageRecipientFeeSummary[]
): HomeWageRecipientFeeSummary[] {
  return rows.filter((r) => !r.isSevereRecipientSurcharge);
}

/** 집계 행 기준 총 서비스횟수 — 중증수급자가산 제외 */
export function calcTotalServiceCountFromSummary(
  rows: HomeWageRecipientFeeSummary[]
): number {
  return filterNonSevereSummaryRows(rows).reduce(
    (sum, row) => sum + (Number(row.serviceCount) || 0),
    0
  );
}

/** 집계 행 기준 중증수급자가산 서비스횟수 */
export function calcSevereServiceCountFromSummary(
  rows: HomeWageRecipientFeeSummary[]
): number {
  return rows
    .filter((r) => r.isSevereRecipientSurcharge)
    .reduce((sum, row) => sum + (Number(row.serviceCount) || 0), 0);
}

/** 청구 행 기준 근로일(유니크 서비스일) — 중증수급자가산·무수가 행 제외 */
export function calcWorkDaysFromClaimRows(rows: HomeWageClaimRow[]): number {
  const days = new Set<string>();
  for (const row of filterNonSevereClaimRows(rows)) {
    const feeName = String(row.fee_name || '').trim();
    const feeCode = String(row.fee_code || '').trim();
    if (!feeName && !feeCode) continue;
    const s = String(row.date || '').trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) days.add(`${m[1]}-${m[2]}-${m[3]}`);
  }
  return days.size;
}

export function calcHomeWageBasePay(
  hourlyWage: number,
  totalSugaHours: number,
  yearMonth?: string
): number {
  if (!Number.isFinite(hourlyWage) || hourlyWage <= 0) return 0;
  if (!Number.isFinite(totalSugaHours) || totalSugaHours <= 0) return 0;
  // 2026-09부터 1의자리 절삭 (8월 이하 임금신고분 보호)
  return truncateWageOnes(hourlyWage * totalSugaHours, yearMonth);
}

export function calcHomeWageWeeklyHolidayPay(basePay: number): number {
  if (!Number.isFinite(basePay) || basePay <= 0) return 0;
  return Math.round(basePay * HOME_WAGE_WEEKLY_HOLIDAY_RATE);
}

export function calcHomeWageAnnualLeavePay(
  hourlyWage: number,
  totalSugaHours: number,
  workDays: number,
  annualLeaveDays: number
): number {
  if (!Number.isFinite(hourlyWage) || hourlyWage <= 0) return 0;
  if (!Number.isFinite(totalSugaHours) || totalSugaHours <= 0) return 0;
  if (!Number.isFinite(workDays) || workDays <= 0) return 0;
  if (!Number.isFinite(annualLeaveDays) || annualLeaveDays <= 0) return 0;
  const dailyBase = calcHomeWageAverageDailyPay(hourlyWage, totalSugaHours, workDays);
  return Math.round((dailyBase * annualLeaveDays) / 12);
}

/** 당월 평균일당 = 지급계산시급 × 총수가시간 ÷ 근무일수 (연차 공통) */
export function calcHomeWageAverageDailyPay(
  hourlyWage: number,
  totalSugaHours: number,
  workDays: number
): number {
  if (!Number.isFinite(hourlyWage) || hourlyWage <= 0) return 0;
  if (!Number.isFinite(totalSugaHours) || totalSugaHours <= 0) return 0;
  if (!Number.isFinite(workDays) || workDays <= 0) return 0;
  return (hourlyWage * totalSugaHours) / workDays;
}

/** 관리자 UI — 기본급 필드 툴팁 입력 */
export interface HomeWageBasicPayAdminTooltipInput {
  payCalcHourlyWage: number;
  totalSugaHours: number;
  workDays: number;
  annualLeaveDays: number;
  actualBasic: number;
  /** 청구합계 − 중증수급자가산 */
  nonSevereTarget?: number;
  claimGrandTotal?: number;
  severeSum?: number;
  lunch?: number;
  isFamilyCareOnly?: boolean;
  /** 저장 급여대장 lock — 시급·수가시간 변경 후 basic 미재계산 가능 */
  payrollDbLock?: boolean;
}

/**
 * 관리자 UI — 기본급 툴팁
 * ★ 표시값이 round(시급×수가시간)과 다를 수 있음 — 청구합계 배분·저장값·수동조정
 */
export function buildHomeWageBasicPayAdminTooltip(
  input: HomeWageBasicPayAdminTooltipInput
): string {
  const fmt = fmtLaborDayTooltipWon;

  if (input.isFamilyCareOnly) {
    const claim = Math.round(Number(input.claimGrandTotal) || 0);
    const severe = Math.round(Number(input.severeSum) || 0);
    const lunch = Math.round(Number(input.lunch) || 0);
    const actual = Math.round(Number(input.actualBasic) || 0);
    return [
      '★ 가족요양 전용 ★',
      '기본급 = 월급여 전체 (주휴·연차 포함)',
      `수급자별합계 − 중증 − 식대`,
      `${fmt(claim)}원 − ${fmt(severe)}원 − ${fmt(lunch)}원`,
      `= ${fmt(actual)}원`,
    ].join('\n');
  }

  const hourly = Number(input.payCalcHourlyWage) || 0;
  const hours = Number(input.totalSugaHours) || 0;
  const linkedBasic = calcHomeWageBasePay(hourly, hours);
  const actual = Math.round(Number(input.actualBasic) || 0);

  const lines: string[] = [
    '【기본급 — 지급계산시급 기준】',
    'round(지급계산시급 × 총수가시간)',
    `${fmt(hourly)}원 × ${hours.toFixed(1)}시간 = ${fmt(linkedBasic)}원`,
  ];

  if (actual === linkedBasic) {
    lines.push(`→ 기본급 ${fmt(actual)}원`);
    return lines.join('\n');
  }

  lines.push('');
  lines.push(`현재 표시: ${fmt(actual)}원`);

  if (input.payrollDbLock) {
    lines.push('※ 저장된 급여대장 값');
    lines.push('  (지급계산시급·수가시간 변경 후 「계산」 전이면 시급×시간과 다를 수 있음)');
  } else {
    lines.push('※ 청구합계 배분·수동조정·미저장 변경 등으로');
    lines.push('  시급×수가시간과 표시값이 다를 수 있습니다.');
    lines.push('  「계산」 또는 수급자 시급 입력 후 자동 재계산됩니다.');
  }

  const target = Math.round(Number(input.nonSevereTarget) || 0);
  if (target > 0 && hourly > 0 && hours > 0) {
    const linkedTotal = calcHomeWageHourlyLinkedPay({
      payCalcHourlyWage: hourly,
      totalSugaHours: hours,
      workDays: input.workDays,
      annualLeaveDays: input.annualLeaveDays,
    });
    if (linkedTotal.total < target) {
      lines.push('');
      lines.push(`청구합계(중증 제외) ${fmt(target)}원`);
      lines.push(`시급×수가 기준 소계 ${fmt(linkedTotal.total)}원`);
      lines.push(`→ 차액 ${fmt(target - linkedTotal.total)}원은 연차수당에 반영`);
    }
  }

  return lines.join('\n');
}

/** 노동절(5/1) 해당 연월 — 매년 5월, 기한 없음 */
export function isHomeWageLaborDayMonth(yearMonth: string): boolean {
  const ym = String(yearMonth || '').replace(/\D/g, '').slice(0, 6);
  return ym.length === 6 && ym.slice(4, 6) === '05';
}

/** 노동절 고정일 (달력·표시용) */
export const HOME_WAGE_LABOR_DAY_DATE_SUFFIX = '-05-01';

/** 5/1 당일 B1/B2 plan 근무시간 합계 */
export function calcMayFirstPlanHoursFromClaims(
  rows: HomeWageClaimRow[],
  yearMonth: string
): number {
  let hours = 0;
  for (const row of rows) {
    if (!isHomeWageMayFirstClaimDate(parseClaimDateKey(row.date), yearMonth)) continue;
    if (!isHomeCareVisitFeeCode(row.fee_code)) continue;
    hours += calcPlanDurationHours(row.plan_start_time, row.plan_end_time);
  }
  return Math.round(hours * 10) / 10;
}

/** 5/1 실근무 여부 (청구 plan 1건 이상) */
export function hasWorkedOnMayFirstFromClaims(
  rows: HomeWageClaimRow[],
  yearMonth: string
): boolean {
  return calcMayFirstPlanHoursFromClaims(rows, yearMonth) > 0;
}

/**
 * 노동절수당 — 5인 이상 사업장, 방문요양·목욕 요양보호사
 * - **비과세** (nonTax·laborDay 합산)
 * - 당월 총수가시간 **60시간 이상**
 * - **월~토 5/1**: 근무 없어도 laborDay(유급휴일 100%) 지급
 * - **일요일 5/1 미근무**: 0원 (주휴수당에 일요일분 포함)
 * - **일요일 5/1 실근무**: laborDay 지급 + 250% 구조
 * - 휴일수당(holiday)과 별도 항목(laborDay)
 */
export function calcHomeWageLaborDayPay(input: {
  yearMonth: string;
  payCalcHourlyWage: number;
  totalSugaHours: number;
  workDays: number;
  isCaregiver: boolean;
  /** 5/1 청구 plan 존재 여부 — 일요일 판정에 필요 */
  workedOnMayFirst?: boolean;
}): number {
  if (!input.isCaregiver) return 0;
  if (!isHomeWageLaborDayMonth(input.yearMonth)) return 0;
  if (
    !Number.isFinite(input.totalSugaHours)
    || input.totalSugaHours < HOME_WAGE_INSURANCE_MIN_HOURS
  ) {
    return 0;
  }
  if (!Number.isFinite(input.workDays) || input.workDays <= 0) return 0;

  const year = parseInt(String(input.yearMonth).replace(/\D/g, '').slice(0, 4), 10);
  if (isMayFirstSunday(year) && !input.workedOnMayFirst) {
    return 0;
  }

  return Math.round(
    calcHomeWageAverageDailyPay(
      input.payCalcHourlyWage,
      input.totalSugaHours,
      input.workDays
    )
  );
}

export interface HomeWageLaborDayTooltipInput {
  yearMonth: string;
  payCalcHourlyWage: number;
  totalSugaHours: number;
  workDays: number;
  isCaregiver: boolean;
  workedOnMayFirst: boolean;
  mayFirstPlanHours: number;
  laborDayAmount: number;
  weekendPay: number;
}

function fmtLaborDayTooltipWon(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return Math.round(n).toLocaleString('ko-KR');
}

/** 노동절수당 필드 — 조건·산식·250% 구조 상세 툴팁 */
export function buildHomeWageLaborDayTooltip(input: HomeWageLaborDayTooltipInput): string {
  const fmt = fmtLaborDayTooltipWon;
  const lines: string[] = ['【노동절수당 · 비과세】'];

  const appendFormulaGuide = () => {
    lines.push('');
    lines.push('【식 정리】');
    lines.push('① 월~토 · 5/1 미근무');
    lines.push('  노동절수당 = 시급 × 월총수가시간 ÷ 근무일수');
    lines.push('  휴일수당   = 0');
    lines.push('');
    lines.push('② 5/1 실근무 plan H시간');
    lines.push('  기본급(실근로)     = 시급 × H');
    lines.push('  휴일수당(연·야·휴) = 시급 × 0.5 × H');
    lines.push('  노동절수당         = 시급 × 월총수가시간 ÷ 근무일수');
    lines.push('  → 100% + 50% + 100% = 250%');
    lines.push('');
    lines.push('③ 일요일 5/1 · 미근무');
    lines.push('  노동절수당 = 0  (주휴에 포함)');
    lines.push('  휴일수당   = 0');
  };

  if (!isHomeWageLaborDayMonth(input.yearMonth)) {
    return [...lines, '조건: 5월 급여 아님 → 0원'].join('\n');
  }
  if (!input.isCaregiver) {
    return [...lines, '조건: 요양보호사 아님 → 0원'].join('\n');
  }
  if (
    !Number.isFinite(input.totalSugaHours)
    || input.totalSugaHours < HOME_WAGE_INSURANCE_MIN_HOURS
  ) {
    return [
      ...lines,
      `조건: 월 ${HOME_WAGE_INSURANCE_MIN_HOURS}시간 미만 (${input.totalSugaHours.toFixed(1)}h) → 0원`,
    ].join('\n');
  }
  if (!Number.isFinite(input.workDays) || input.workDays <= 0) {
    return [...lines, '조건: 근무일수 0 → 0원'].join('\n');
  }

  const year = parseInt(String(input.yearMonth).replace(/\D/g, '').slice(0, 4), 10);
  const dowLabels = ['일', '월', '화', '수', '목', '금', '토'];
  const dow = getMayFirstWeekday(year);
  lines.push(`올해 5/1: ${dowLabels[dow]}요일`);

  appendFormulaGuide();

  if (isMayFirstSunday(year) && !input.workedOnMayFirst) {
    lines.push('');
    lines.push('【이 사원 · ③ 적용】');
    lines.push('일요일 5/1 + 미근무 → 노동절수당 0원');
    lines.push(`주휴수당 ${fmt(input.weekendPay)}원 (일요일 유급분 포함)`);
    return lines.join('\n');
  }

  const avgDaily = calcHomeWageAverageDailyPay(
    input.payCalcHourlyWage,
    input.totalSugaHours,
    input.workDays
  );
  const laborDay = input.laborDayAmount;
  const w = input.payCalcHourlyWage;

  lines.push('');
  lines.push('【이 사원 · 노동절수당】');
  lines.push('노동절수당 = 시급 × 월총수가시간 ÷ 근무일수');
  lines.push(
    `= ${fmt(w)} × ${input.totalSugaHours.toFixed(1)} ÷ ${input.workDays}`
  );
  lines.push(`= ${fmt(avgDaily)}원 → round = ${fmt(laborDay)}원`);

  if (!input.workedOnMayFirst && dow >= 1 && dow <= 6) {
    lines.push('');
    lines.push('【이 사원 · ① 적용】');
    lines.push('월~토 5/1 미근무 — 유급휴일 100%만 (휴일수당 0)');
    return lines.join('\n');
  }

  if (input.workedOnMayFirst && input.mayFirstPlanHours > 0) {
    const h = input.mayFirstPlanHours;
    const basePortion = Math.round(w * h);
    const holidayPortion = Math.round(w * 0.5 * h);
    lines.push('');
    lines.push(`【이 사원 · ② 적용】 5/1 plan ${h}h`);
    lines.push(`기본급     = ${fmt(w)} × ${h}h = ${fmt(basePortion)}원`);
    lines.push(`휴일수당   = ${fmt(w)} × 0.5 × ${h}h = ${fmt(holidayPortion)}원`);
    lines.push(`노동절수당 = ${fmt(laborDay)}원 (위 식)`);
    lines.push(
      `합계 ${fmt(basePortion)} + ${fmt(holidayPortion)} + ${fmt(laborDay)}`
      + ` = ${fmt(basePortion + holidayPortion + laborDay)}원 (250%)`
    );
  }

  return lines.join('\n');
}

/** 연장·야간·휴일 수당 = 시급 × 배율 × 시간. 2026-09부터 1의자리 절삭 */
export function calcHomeWagePremiumPay(
  hourlyWage: number,
  hours: number,
  rate: number,
  yearMonth?: string
): number {
  if (!Number.isFinite(hourlyWage) || hourlyWage <= 0) return 0;
  if (!Number.isFinite(hours) || hours <= 0) return 0;
  if (!Number.isFinite(rate) || rate <= 0) return 0;
  return truncateWageOnes(hourlyWage * rate * hours, yearMonth);
}

export function calcHomeWageOvertimePay(
  hourlyWage: number,
  hours: number,
  yearMonth?: string
): number {
  return calcHomeWagePremiumPay(hourlyWage, hours, HOME_WAGE_OVERTIME_RATE, yearMonth);
}

export function calcHomeWageNightPay(
  hourlyWage: number,
  hours: number,
  yearMonth?: string
): number {
  return calcHomeWagePremiumPay(hourlyWage, hours, HOME_WAGE_NIGHT_RATE, yearMonth);
}

export function calcHomeWageHolidayPay(
  hourlyWage: number,
  hours: number,
  yearMonth?: string
): number {
  return calcHomeWagePremiumPay(hourlyWage, hours, HOME_WAGE_HOLIDAY_RATE, yearMonth);
}

/** 요양보호사(방문요양·목욕) 연장수당 — 시급 × 0.5 × 시간 */
export function calcHomeWageCaregiverOvertimePay(
  hourlyWage: number,
  hours: number,
  yearMonth?: string
): number {
  return calcHomeWagePremiumPay(hourlyWage, hours, HOME_WAGE_CAREGIVER_PREMIUM_RATE, yearMonth);
}

/** 요양보호사(방문요양·목욕) 야간수당 — 시급 × 0.5 × 시간 */
export function calcHomeWageCaregiverNightPay(
  hourlyWage: number,
  hours: number,
  yearMonth?: string
): number {
  return calcHomeWagePremiumPay(hourlyWage, hours, HOME_WAGE_CAREGIVER_PREMIUM_RATE, yearMonth);
}

/** 요양보호사(방문요양·목욕) 휴일수당 — 시급 × 0.5 × 시간 */
export function calcHomeWageCaregiverHolidayPay(
  hourlyWage: number,
  hours: number,
  yearMonth?: string
): number {
  return calcHomeWagePremiumPay(hourlyWage, hours, HOME_WAGE_CAREGIVER_PREMIUM_RATE, yearMonth);
}

export type HomeWagePremiumPayMode = 'additive' | 'pool';

/** pool 모드 — 항목별 최소 지급 기준 시간 (0.5h 상당액 미만은 0원) */
export const HOME_WAGE_PREMIUM_POOL_MIN_HOURS = 0.5;

/** pool 모드 최소 연·야·휴 금액 = 지급계산시급 × 0.5 × 0.5h */
export function calcHomeWageCaregiverMinPremiumPay(hourlyWage: number): number {
  return calcHomeWageCaregiverOvertimePay(
    hourlyWage,
    HOME_WAGE_PREMIUM_POOL_MIN_HOURS
  );
}

/** 비가족요양 → 항상 가산(additive), 가족요양 전용만 pool */
export function resolveHomeWagePremiumPayMode(input: {
  totalSugaHours: number;
  isFamilyCare: boolean;
}): HomeWagePremiumPayMode {
  if (input.isFamilyCare) return 'pool';
  return 'additive';
}

/**
 * 가족요양 — 시점별 휴일수당 pool 배분용 시간
 * - 센터장: 0 (미지급, 휴일분은 기본급에 포함)
 * - 관리자: admin 청구 기준 휴일시간 → 기본급에서 분리 지급 (총지급 추가 아님)
 * @deprecated resolvePremiumHoursForAllocation 사용
 */
export function resolveFamilyCareHolidayHoursForAllocation(
  holidayHours: number,
  viewMode: HomeWagePayrollViewMode
): number {
  if (viewMode === 'director') return 0;
  return Math.max(0, Number(holidayHours) || 0);
}

/** 센터장 — 연·야·휴 미지급 (가족요양 전용 또는 월 수가시간 60h 미만, override 미적용) */
export function shouldHomeWageDirectorBlockPremiumPay(input: {
  viewMode: HomeWagePayrollViewMode;
  isFamilyCareOnly: boolean;
  totalSugaHours: number;
}): boolean {
  if (input.viewMode !== 'director') return false;
  if (input.isFamilyCareOnly) return true;
  return input.totalSugaHours > 0 && input.totalSugaHours < HOME_WAGE_INSURANCE_MIN_HOURS;
}

/** 센터장 연·휴 지급 override — null이면 DB/화면 미설정 */
export type HomeWageDirectorPremiumPayOverride = boolean | null;

/** DB Y/N → 센터장 연·휴 지급 override */
export function parseHomeWageDirectorPremiumPayFlag(
  value: unknown
): HomeWageDirectorPremiumPayOverride {
  if (value === 'Y' || value === true || value === 1) return true;
  if (value === 'N' || value === false || value === 0) return false;
  return null;
}

/** 센터장 연·휴 지급 override → DB Y/N */
export function formatHomeWageDirectorPremiumPayFlag(
  value: HomeWageDirectorPremiumPayOverride
): 'Y' | 'N' | null {
  if (value === true) return 'Y';
  if (value === false) return 'N';
  return null;
}

/** 60h 정책 기본 — 60h 이상이면 지급, 미만이면 미지급 */
export function resolveDirectorPremiumPayDefaultBy60h(totalSugaHours: number): boolean {
  return !(totalSugaHours > 0 && totalSugaHours < HOME_WAGE_INSURANCE_MIN_HOURS);
}

/**
 * 센터장 시점 연장·휴일·야간 지급 여부
 * - 연·야·휴: override가 60h 정책보다 우선 (야간 세부 정책은 추후 확정)
 */
export function resolveDirectorPremiumPayEnabled(input: {
  type: 'overtime' | 'holiday' | 'night';
  payOverride?: HomeWageDirectorPremiumPayOverride;
  totalSugaHours: number;
  isFamilyCareOnly?: boolean;
}): boolean {
  if (input.isFamilyCareOnly) return false;
  if (input.payOverride === true) return true;
  if (input.payOverride === false) return false;
  return resolveDirectorPremiumPayDefaultBy60h(input.totalSugaHours);
}

/** 센터장 시점 연·야·휴 입력 차단 알림 */
export function getHomeWageDirectorPremiumBlockedAlert(input: {
  isFamilyCareOnly: boolean;
  totalSugaHours: number;
}): string {
  if (input.isFamilyCareOnly && input.totalSugaHours >= HOME_WAGE_INSURANCE_MIN_HOURS) {
    return '가족요양은 센터장 시점에서 연장·야간·휴일 수당을 지급할 수 없습니다.';
  }
  return '근로시간 60시간 미만이어서 연장·야간·휴일 수당을 지급할 수 없습니다.';
}

export interface HomeWagePremiumHoursTriple {
  overtimeHours: number;
  nightHours: number;
  holidayHours: number;
}

/**
 * pool/additive 배분용 연·야·휴 시간
 * - 일반요양: 연·야·휴 override는 관리자·센터장 공통 적용 (60h 정책 상위)
 * - 가족요양: 센터장 시점 0 / 관리자 청구 기준
 */
export function resolvePremiumHoursForAllocation(input: {
  overtimeHours: number;
  nightHours: number;
  holidayHours: number;
  viewMode: HomeWagePayrollViewMode;
  isFamilyCareOnly: boolean;
  totalSugaHours: number;
  directorPayOvertime?: HomeWageDirectorPremiumPayOverride;
  directorPayHoliday?: HomeWageDirectorPremiumPayOverride;
  directorPayNight?: HomeWageDirectorPremiumPayOverride;
}): HomeWagePremiumHoursTriple {
  const overtimeHours = Math.max(0, Number(input.overtimeHours) || 0);
  const nightHours = Math.max(0, Number(input.nightHours) || 0);
  const holidayHours = Math.max(0, Number(input.holidayHours) || 0);

  if (input.isFamilyCareOnly) {
    if (input.viewMode === 'director') {
      return { overtimeHours: 0, nightHours: 0, holidayHours: 0 };
    }
    return { overtimeHours, nightHours, holidayHours };
  }

  const payOvertime = resolveDirectorPremiumPayEnabled({
    type: 'overtime',
    payOverride: input.directorPayOvertime,
    totalSugaHours: input.totalSugaHours,
  });
  const payHoliday = resolveDirectorPremiumPayEnabled({
    type: 'holiday',
    payOverride: input.directorPayHoliday,
    totalSugaHours: input.totalSugaHours,
  });
  const payNight = resolveDirectorPremiumPayEnabled({
    type: 'night',
    payOverride: input.directorPayNight,
    totalSugaHours: input.totalSugaHours,
  });

  return {
    overtimeHours: payOvertime ? overtimeHours : 0,
    nightHours: payNight ? nightHours : 0,
    holidayHours: payHoliday ? holidayHours : 0,
  };
}

/** @deprecated resolvePremiumHoursForAllocation 사용 */
export function resolveHolidayHoursForPremiumAllocation(input: {
  holidayHours: number;
  viewMode: HomeWagePayrollViewMode;
  isFamilyCareOnly: boolean;
}): number {
  return resolvePremiumHoursForAllocation({
    overtimeHours: 0,
    nightHours: 0,
    holidayHours: input.holidayHours,
    viewMode: input.viewMode,
    isFamilyCareOnly: input.isFamilyCareOnly,
    totalSugaHours: HOME_WAGE_INSURANCE_MIN_HOURS,
  }).holidayHours;
}

export interface HomeWagePremiumPayAmounts {
  overtime: number;
  night: number;
  holiday: number;
}

export function calcHomeWageCaregiverPremiumPays(
  hourlyWage: number,
  overtimeHours: number,
  nightHours: number,
  holidayHours: number,
  yearMonth?: string
): HomeWagePremiumPayAmounts {
  return {
    overtime: calcHomeWageCaregiverOvertimePay(hourlyWage, overtimeHours, yearMonth),
    night: calcHomeWageCaregiverNightPay(hourlyWage, nightHours, yearMonth),
    holiday: calcHomeWageCaregiverHolidayPay(hourlyWage, holidayHours, yearMonth),
  };
}

/** pool 모드 — 항목별 금액이 최소(0.5h 상당) 미만이면 0 */
function zeroPremiumBelowMinPay(
  amounts: HomeWagePremiumPayAmounts,
  minPay: number
): HomeWagePremiumPayAmounts {
  if (minPay <= 0) return amounts;
  const result = { ...amounts };
  for (const key of ['overtime', 'night', 'holiday'] as const) {
    if (result[key] > 0 && result[key] < minPay) {
      result[key] = 0;
    }
  }
  return result;
}

function distributePremiumPool(
  full: HomeWagePremiumPayAmounts,
  pool: number
): HomeWagePremiumPayAmounts {
  const fullTotal = full.overtime + full.night + full.holiday;
  if (fullTotal <= 0 || pool <= 0) {
    return { overtime: 0, night: 0, holiday: 0 };
  }
  if (pool >= fullTotal) return full;

  const keys: (keyof HomeWagePremiumPayAmounts)[] = ['overtime', 'night', 'holiday'];
  const allocated: HomeWagePremiumPayAmounts = { overtime: 0, night: 0, holiday: 0 };
  let remaining = pool;
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (i === keys.length - 1) {
      allocated[key] = remaining;
      continue;
    }
    const share = Math.floor(((full[key] / fullTotal) * pool) / 10) * 10;
    allocated[key] = share;
    remaining -= share;
  }
  return allocated;
}

export interface HomeWagePremiumAllocationInput {
  mode: HomeWagePremiumPayMode;
  hourlyWage: number;
  overtimeHours: number;
  nightHours: number;
  holidayHours: number;
  /** pool 모드: 연·야·휴에 쓸 수 있는 금액 */
  premiumPool?: number;
  /** YYYYMM — 2026-09부터 원단위 절삭 */
  yearMonth?: string;
}

/** 연·야·휴 금액 산출 (pool: 풀 한도 배분 + 0.5h 상당 최소금액 미만 제외) */
export function allocateHomeWagePremiumPays(
  input: HomeWagePremiumAllocationInput
): HomeWagePremiumPayAmounts {
  const full = calcHomeWageCaregiverPremiumPays(
    input.hourlyWage,
    input.overtimeHours,
    input.nightHours,
    input.holidayHours,
    input.yearMonth
  );
  if (input.mode === 'additive') return full;

  const minPay = calcHomeWageCaregiverMinPremiumPay(input.hourlyWage);
  const eligible = zeroPremiumBelowMinPay(full, minPay);
  const distributed = distributePremiumPool(
    eligible,
    Math.max(0, Number(input.premiumPool) || 0)
  );
  return zeroPremiumBelowMinPay(distributed, minPay);
}

/** 연·야·휴 자동 산출 스냅샷 (수동조정 모달용) */
export interface HomeWageAutoPremiumSnapshot {
  mode: HomeWagePremiumPayMode;
  full: HomeWagePremiumPayAmounts;
  allocated: HomeWagePremiumPayAmounts;
  split: HomeWagePremiumSplitAmounts;
  allocatedTotal: number;
}

export function calcHomeWageAutoPremiumSnapshot(input: {
  hourlyWage: number;
  overtimeHours: number;
  nightHours: number;
  holidayHours: number;
  mode: HomeWagePremiumPayMode;
  premiumPool?: number;
  productionNontax: boolean;
  yearMonth?: string;
}): HomeWageAutoPremiumSnapshot {
  const full = calcHomeWageCaregiverPremiumPays(
    input.hourlyWage,
    input.overtimeHours,
    input.nightHours,
    input.holidayHours,
    input.yearMonth
  );
  const allocated = allocateHomeWagePremiumPays({
    mode: input.mode,
    hourlyWage: input.hourlyWage,
    overtimeHours: input.overtimeHours,
    nightHours: input.nightHours,
    holidayHours: input.holidayHours,
    premiumPool: input.premiumPool,
    yearMonth: input.yearMonth,
  });
  const split = splitHomeWagePremiumForStorage(
    allocated.overtime,
    allocated.night,
    allocated.holiday,
    input.productionNontax
  );
  const allocatedTotal =
    split.overtime +
    split.night +
    split.holiday +
    split.overtimeNontax +
    split.nightNontax +
    split.holidayNontax;
  return {
    mode: input.mode,
    full,
    allocated,
    split,
    allocatedTotal,
  };
}

/**
 * 생산직 비과세 적용 시 연장·야간·휴일이 과세·비과세로 나뉘는지 (20만 한도 초과)
 */
export function isHomeWagePremiumProductionTaxNontaxSplit(
  fullOvertime: number,
  fullNight: number,
  fullHoliday: number,
  productionNontax: boolean
): boolean {
  if (!productionNontax) return false;
  const split = calcHomeWageOvertimeNontax(fullOvertime, fullNight, fullHoliday, true);
  const taxTotal = split.overtimeTax + split.nightTax + split.holidayTax;
  return split.totalNontax > 0 && taxTotal > 0;
}

/** 수동조정 — 연·야·휴 금액 적용 + 미지급분을 연차(일반) 또는 기본급(가족 pool)으로 환원 */
export function applyHomeWageManualPremiumAdjustment(input: {
  manualFull: HomeWagePremiumPayAmounts;
  autoAllocatedTotal: number;
  baselineAnnual: number;
  baselineBasic: number;
  isFamilyCare: boolean;
  productionNontax: boolean;
}): {
  split: HomeWagePremiumSplitAmounts;
  annual: number;
  basic: number;
  returnedAmount: number;
} {
  const split = splitHomeWagePremiumForStorage(
    Math.max(0, input.manualFull.overtime),
    Math.max(0, input.manualFull.night),
    Math.max(0, input.manualFull.holiday),
    input.productionNontax
  );
  const manualTotal =
    split.overtime +
    split.night +
    split.holiday +
    split.overtimeNontax +
    split.nightNontax +
    split.holidayNontax;
  const returnedAmount = Math.max(0, input.autoAllocatedTotal - manualTotal);

  if (input.isFamilyCare) {
    return {
      split,
      annual: 0,
      basic: Math.max(0, input.baselineBasic + returnedAmount),
      returnedAmount,
    };
  }

  return {
    split,
    annual: Math.max(0, input.baselineAnnual + returnedAmount),
    basic: input.baselineBasic,
    returnedAmount,
  };
}

/** 수동조정 해제 — enable 시점 baseline + 자동 연·야·휴 분리값으로 복원 */
export function revertHomeWageManualPremiumAdjustment(input: {
  baselineAnnual: number;
  baselineBasic: number;
  isFamilyCare: boolean;
  autoSplit: HomeWagePremiumSplitAmounts;
}): {
  split: HomeWagePremiumSplitAmounts;
  annual: number;
  basic: number;
} {
  return {
    split: input.autoSplit,
    annual: input.isFamilyCare ? 0 : Math.max(0, input.baselineAnnual),
    basic: Math.max(0, input.baselineBasic),
  };
}

/** 휴일수당 발생 청구 건 상세 (수동조정 모달 — 근무 내역) */
export interface HomeWageHolidayPremiumDetailRow {
  date: string;
  recipientName: string;
  planTime: string;
  hours: number;
  reason: string;
}

export function buildHomeWageHolidayPremiumClaimRows(
  rows: HomeWageClaimRow[],
  yearMonth = '',
  viewMode: HomeWagePayrollViewMode = 'admin'
): HomeWageHolidayPremiumDetailRow[] {
  const result: HomeWageHolidayPremiumDetailRow[] = [];
  for (const row of rows) {
    if (!isHolidayPremiumClaimForViewMode(row, yearMonth, viewMode)) continue;
    const hours = calcPlanDurationHours(row.plan_start_time, row.plan_end_time);
    if (hours <= 0) continue;
    const dateKey = parseClaimDateKey(row.date) || String(row.date || '').trim();
    let reason = String(row.fee_name || '').trim() || '휴일가산';
    if (yearMonth && isHomeWageMayFirstClaimDate(dateKey, yearMonth)) {
      reason = '5/1 노동절 실근무';
    }
    const start = String(row.plan_start_time || '').trim();
    const end = String(row.plan_end_time || '').trim();
    result.push({
      date: dateKey,
      recipientName: String(row.recipient_name || '').trim(),
      planTime: start && end ? `${start}~${end}` : '-',
      hours: Math.round(hours * 10) / 10,
      reason,
    });
  }
  result.sort((a, b) => a.date.localeCompare(b.date) || a.recipientName.localeCompare(b.recipientName));
  return result;
}

/** 연·야·휴 시간 집계 안내 */
export const HOME_WAGE_PREMIUM_HOURS_OVERLAP_LINES = [
  '【시간 집계】',
  '연장: 동일 일자 겹치는 plan 구간 병합(동시 케어) 후 8h 초과',
  '야간·휴일: 건별 합산',
  '야간·휴일·연장은 유형별 독립 (같은 구간이 조건 충족 시 각각 포함)',
] as const;

export interface HomeWagePremiumAdminTooltipInput {
  kind: 'overtime' | 'night' | 'holiday';
  hourlyWage: number;
  hours: number;
  amount: number;
  mode: HomeWagePremiumPayMode;
  totalSugaHours: number;
  isFamilyCare: boolean;
  nonSevereTarget: number;
  linkedBasic: number;
  linkedWeekend: number;
  linkedAnnual: number;
  lunch: number;
  fullPremiums: HomeWagePremiumPayAmounts;
  allocatedPremiums: HomeWagePremiumPayAmounts;
  premiumPool: number;
}

function fmtPremiumTooltipWon(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return Math.round(n).toLocaleString('ko-KR');
}

const PREMIUM_KIND_LABELS: Record<HomeWagePremiumAdminTooltipInput['kind'], string> = {
  overtime: '연장수당',
  night: '야간수당',
  holiday: '휴일수당',
};

const PREMIUM_KIND_HOUR_RULES: Record<HomeWagePremiumAdminTooltipInput['kind'], string> = {
  overtime: `일 ${HOME_WAGE_DAILY_OVERTIME_THRESHOLD_HOURS}시간 초과분 (B1/B2 plan, 동일 일자 겹침 병합)`,
  night: '22:00~익일 06:00 plan 겹침',
  holiday: '수가명 가산 또는 5/1 실근무 plan',
};

function buildPremiumAdditiveModeLines(input: HomeWagePremiumAdminTooltipInput): string[] {
  const fullTotal =
    input.fullPremiums.overtime + input.fullPremiums.night + input.fullPremiums.holiday;
  return [
    '【총지급 반영 — 가산(additive)】',
    '조건: 비가족요양 (월 수가시간과 무관)',
    `① 과세(기본+주휴+연차+…) = 청구합계 ${fmtPremiumTooltipWon(input.nonSevereTarget)}원 (중증 제외)`,
    '② 연·야·휴 = 시급×0.5×시간 → 비과세 카드, 총지급에 추가',
    `   이론상 연·야·휴 합계 ${fmtPremiumTooltipWon(fullTotal)}원`,
    '③ 지급금액 = 과세합계 + 비과세(식대+연·야·휴+…)',
    '→ 청구합계 대비 연·야·휴만큼 지급금액이 증가',
  ];
}

function buildPremiumPoolModeLines(input: HomeWagePremiumAdminTooltipInput): string[] {
  const fullTotal =
    input.fullPremiums.overtime + input.fullPremiums.night + input.fullPremiums.holiday;
  const allocTotal =
    input.allocatedPremiums.overtime +
    input.allocatedPremiums.night +
    input.allocatedPremiums.holiday;

  const minPay = calcHomeWageCaregiverMinPremiumPay(input.hourlyWage);
  const minPayLine =
    minPay > 0
      ? `★ 항목별 최소 ${fmtPremiumTooltipWon(minPay)}원 (0.5h×시급×0.5) 미만 → 0원`
      : '';

  return [
    '【총지급 반영 — 내부배분(pool)】',
    '조건: 가족요양 전용 (주휴=0, 연차=0)',
    `① 풀 = 청구합계 − 식대`,
    `   ${fmtPremiumTooltipWon(input.nonSevereTarget)} − ${fmtPremiumTooltipWon(input.lunch)} = ${fmtPremiumTooltipWon(input.premiumPool)}원`,
    `② 이론상 연·야·휴 ${fmtPremiumTooltipWon(fullTotal)}원`,
    allocTotal < fullTotal
      ? `   풀 부족 → 연장→야간→휴일 순 비례 배분 → ${fmtPremiumTooltipWon(allocTotal)}원`
      : `   풀 충분 → 이론상 전액 반영`,
    ...(minPayLine ? [minPayLine] : []),
    `③ 기본급 = 청구합계 − 식대 − 실제 연·야·휴`,
    `   ${fmtPremiumTooltipWon(input.nonSevereTarget)} − ${fmtPremiumTooltipWon(input.lunch)} − ${fmtPremiumTooltipWon(allocTotal)}원`,
    ...(input.isFamilyCare
      ? [
          '',
          '★ 가족요양 휴일수당',
          '• 센터장: 0원 (기본급 포함)',
          '• 관리자: 기본급에서 분리 (총지급 추가 아님)',
        ]
      : []),
  ];
}

/** 연·야·휴 필드 — 관리자(60+) 전용 상세 툴팁 */
export function buildHomeWagePremiumAdminTooltip(
  input: HomeWagePremiumAdminTooltipInput
): string {
  const label = PREMIUM_KIND_LABELS[input.kind];
  const hourRule = PREMIUM_KIND_HOUR_RULES[input.kind];
  const lines: string[] = [
    `【${label}】`,
    `시간: ${hourRule}`,
    `금액: 시급 × 0.5 × 시간`,
    `${fmtPremiumTooltipWon(input.hourlyWage)} × 0.5 × ${input.hours}h = ${fmtPremiumTooltipWon(input.amount)}원`,
    '',
    ...HOME_WAGE_PREMIUM_HOURS_OVERLAP_LINES,
    '',
  ];

  lines.push(
    ...(input.mode === 'additive'
      ? buildPremiumAdditiveModeLines(input)
      : buildPremiumPoolModeLines(input))
  );

  return lines.join('\n');
}

/** 지급계산시급 기준 — 기본급·주휴·연차·(입력 시간이 있으면) 연장·야간·휴일 */
export interface HomeWageHourlyLinkedPayInput {
  payCalcHourlyWage: number;
  totalSugaHours: number;
  workDays: number;
  annualLeaveDays: number;
  overtimeHours?: number;
  nightHours?: number;
  holidayHours?: number;
  yearMonth?: string;
}

export interface HomeWageHourlyLinkedPayResult {
  basic: number;
  weekend: number;
  annual: number;
  overtime: number;
  night: number;
  holiday: number;
}

export interface HomeWageTaxableIncomeOptions {
  /** 생산직 비과세(레거시) — 요양보호사 연·야·휴는 비과세 카드로 분리 */
  productionNontax?: boolean;
  /** 센터장/사복: 연·야·휴를 과세임금에 포함 */
  staffEmployee?: boolean;
  /** 귀속연월(YYYYMM) — 5월 외 노동절 비과세 미적용 */
  yearMonth?: string;
}

type HomeWageTaxableIncomeForm = Pick<
  HomeWagePayrollFormData,
  | 'basic'
  | 'weekend'
  | 'annual'
  | 'overtime'
  | 'night'
  | 'holiday'
  | 'senior'
  | 'bosu'
  | 'severe'
  | 'bonus'
  | 'welfare'
  | 'other'
  | 'wageTotal'
  | 'lunch'
  | 'lunchDirector'
  | 'car'
  | 'child'
>;

/** 비과세 수당 카드 금액 합 (식대·차량·자녀교육비, nonTax 합계 필드는 사용하지 않음) */
export function calcHomeWageCashNonTaxAllowance(
  form: Pick<HomeWagePayrollFormData, 'lunch' | 'lunchDirector' | 'car' | 'child'>
): number {
  const lunch = calcHomeWageCombinedLunch(form.lunch, form.lunchDirector);
  const car = Math.min(Number(form.car) || 0, HOME_WAGE_CAR_MONTHLY_LIMIT);
  const child = Math.min(Number(form.child) || 0, HOME_WAGE_CHILD_MONTHLY_LIMIT);
  return lunch + car + child;
}

/** 연·야·휴 전액(지급계산시급×시간) 기준 과세/비과세 분리 */
export function calcHomeWagePremiumTaxSplit(
  form: Pick<HomeWagePayrollFormData, 'overtime' | 'night' | 'holiday'>,
  productionNontax: boolean
): HomeWageOvertimeNontaxResult {
  return calcHomeWageOvertimeNontax(
    Number(form.overtime) || 0,
    Number(form.night) || 0,
    Number(form.holiday) || 0,
    productionNontax
  );
}

/** 연야휴 비과세분 — 관리자 연야휴 반영 시 월 20만 한도(연→야→휴). productionNontax=false면 0 */
export function calcHomeWagePremiumNonTaxAllowance(
  form: Pick<
    HomeWagePayrollFormData,
    'overtime' | 'night' | 'holiday' | 'overtimeNontax' | 'nightNontax' | 'holidayNontax'
  >,
  productionNontax?: boolean
): number {
  if (!productionNontax) return 0;
  const stored =
    (Number(form.overtimeNontax) || 0) +
    (Number(form.nightNontax) || 0) +
    (Number(form.holidayNontax) || 0);
  if (stored > 0) return stored;
  const full = getHomeWagePremiumFullAmounts({
    overtime: Number(form.overtime) || 0,
    night: Number(form.night) || 0,
    holiday: Number(form.holiday) || 0,
    overtimeNontax: 0,
    nightNontax: 0,
    holidayNontax: 0,
  });
  return calcHomeWageOvertimeNontax(full.overtime, full.night, full.holiday, true).totalNontax;
}

/**
 * 지급합계(과세합계+비과세)에 더할 연·야·휴 비과세
 * — DB *_nontax 저장분 우선
 * — 과세 칸(cwhd_overtime 등) 금액을 비과세로 재해석해 더하지 않음
 *   (레거시: 생산직Y + 휴일수당이 과세합계에 이미 포함된 경우 중복 가산 방지)
 * — 예외: wageTotal이 과세분만 담고 *_nontax가 비어 있는 편집 중 폼 → 재산출 허용
 */
export function calcHomeWagePremiumNonTaxForGrossPay(
  form: Pick<
    HomeWagePayrollFormData,
    | 'wageTotal'
    | 'basic'
    | 'weekend'
    | 'annual'
    | 'senior'
    | 'sunim'
    | 'bosu'
    | 'severe'
    | 'bonus'
    | 'welfare'
    | 'other'
    | 'overtime'
    | 'night'
    | 'holiday'
    | 'overtimeNontax'
    | 'nightNontax'
    | 'holidayNontax'
  >,
  productionNontax = false
): number {
  if (!productionNontax) return 0;

  const stored =
    dbWageAmount(form.overtimeNontax) +
    dbWageAmount(form.nightNontax) +
    dbWageAmount(form.holidayNontax);
  if (stored > 0) return stored;

  const fullOvertime = dbWageAmount(form.overtime);
  const fullNight = dbWageAmount(form.night);
  const fullHoliday = dbWageAmount(form.holiday);
  if (fullOvertime + fullNight + fullHoliday <= 0) return 0;

  const recalcNontax = calcHomeWageOvertimeNontax(
    fullOvertime,
    fullNight,
    fullHoliday,
    true
  ).totalNontax;
  if (recalcNontax <= 0) return 0;

  const wageTotal = dbWageAmount(form.wageTotal);
  if (wageTotal <= 0) return recalcNontax;

  const baseComponents = HOME_WAGE_TAXABLE_COMPONENT_KEYS.reduce(
    (sum, key) => sum + dbWageAmount(form[key] as number | string | null | undefined),
    0
  );
  const taxablePremium = calcHomeWagePremiumTaxableAmount(
    { overtime: fullOvertime, night: fullNight, holiday: fullHoliday },
    true
  );
  const taxableOnlyTotal = baseComponents + taxablePremium;

  // wageTotal ≈ 과세분만 → 비과세 연·야·휴를 합계에 추가해도 중복 아님
  if (Math.abs(wageTotal - taxableOnlyTotal) <= 1) return recalcNontax;

  // wageTotal이 연·야·휴 전액을 이미 포함(레거시 DB) → 추가 시 중복
  return 0;
}

/** 연야휴 과세분 — 생산직 미적용 시 전액, 적용 시 초과분 */
export function calcHomeWagePremiumTaxableAmount(
  form: Pick<HomeWagePayrollFormData, 'overtime' | 'night' | 'holiday'>,
  productionNontax: boolean
): number {
  const split = calcHomeWagePremiumTaxSplit(form, productionNontax);
  return split.overtimeTax + split.nightTax + split.holidayTax;
}

/** DB 저장용 — 연·야·휴 과세/비과세 분리 금액 */
export interface HomeWagePremiumSplitAmounts {
  overtime: number;
  night: number;
  holiday: number;
  overtimeNontax: number;
  nightNontax: number;
  holidayNontax: number;
}

/** 전액 → 과세(cwhd_*) + 비과세(cwhd_*_nontax) 분리 */
export function splitHomeWagePremiumForStorage(
  fullOvertime: number,
  fullNight: number,
  fullHoliday: number,
  productionNontax: boolean
): HomeWagePremiumSplitAmounts {
  if (!productionNontax) {
    return {
      overtime: fullOvertime,
      night: fullNight,
      holiday: fullHoliday,
      overtimeNontax: 0,
      nightNontax: 0,
      holidayNontax: 0,
    };
  }
  const split = calcHomeWageOvertimeNontax(fullOvertime, fullNight, fullHoliday, true);
  return {
    overtime: split.overtimeTax,
    night: split.nightTax,
    holiday: split.holidayTax,
    overtimeNontax: split.overtimeNontax,
    nightNontax: split.nightNontax,
    holidayNontax: split.holidayNontax,
  };
}

/**
 * DB 로드 — 연·야·휴 전액 기준 월 20만 비과세 재분리
 * (productionNontax 플래그·구 저장분과 무관, 관리자·회계 정책과 동일)
 */
export function normalizeHomeWagePremiumFromDb(row: {
  overtime?: number;
  night?: number;
  holiday?: number;
  overtimeNontax?: number;
  nightNontax?: number;
  holidayNontax?: number;
  productionNontax?: string | boolean;
}): HomeWagePremiumSplitAmounts {
  const otN = Number(row.overtimeNontax) || 0;
  const niN = Number(row.nightNontax) || 0;
  const hoN = Number(row.holidayNontax) || 0;
  return splitHomeWagePremiumForStorage(
    (Number(row.overtime) || 0) + otN,
    (Number(row.night) || 0) + niN,
    (Number(row.holiday) || 0) + hoN,
    true
  );
}

export function getHomeWagePremiumFullAmounts(split: HomeWagePremiumSplitAmounts) {
  return {
    overtime: split.overtime + split.overtimeNontax,
    night: split.night + split.nightNontax,
    holiday: split.holiday + split.holidayNontax,
  };
}

export function applyHomeWagePremiumSplitToForm(
  form: Pick<
    HomeWagePayrollFormData,
    'overtime' | 'night' | 'holiday' | 'overtimeNontax' | 'nightNontax' | 'holidayNontax'
  >,
  split: HomeWagePremiumSplitAmounts
): void {
  form.overtime = split.overtime;
  form.night = split.night;
  form.holiday = split.holiday;
  form.overtimeNontax = split.overtimeNontax;
  form.nightNontax = split.nightNontax;
  form.holidayNontax = split.holidayNontax;
}

/**
 * 4대보험 산정 기준 과세임금 (요양시설 임금대장 cwhd_taxable 과 동일: 총지급 − 비과세)
 * - 과세 항목 합계에서 비과세 수당(식대·차량·자녀교육비·노동절) 및 연야휴 비과세분 제외
 * - 레거시 wageTotal(임금구성합계=총지급)만 있는 경우에도 비과세 카드 금액 차감
 */
export function calcHomeWageTaxableIncome(
  form: HomeWageTaxableIncomeForm,
  options: HomeWageTaxableIncomeOptions = {}
): number {
  const productionNontax = options.productionNontax ?? false;
  const staffEmployee = options.staffEmployee ?? false;
  const cashNontax = calcHomeWageCashNonTaxAllowance(form);
  const premiumNontax = calcHomeWagePremiumNonTaxAllowance(form, productionNontax);
  const rawLaborDay = Number((form as HomeWagePayrollFormData).laborDay) || 0;
  const laborDayNontax =
    options.yearMonth && !isHomeWageLaborDayMonth(options.yearMonth)
      ? 0
      : rawLaborDay;
  const totalNontax = cashNontax + premiumNontax + laborDayNontax;

  const componentKeys = staffEmployee
    ? HOME_WAGE_STAFF_TAXABLE_COMPONENT_KEYS
    : HOME_WAGE_TAXABLE_COMPONENT_KEYS;

  const taxableComponentSum = componentKeys.reduce(
    (sum, key) => sum + (Number((form as HomeWagePayrollFormData)[key]) || 0),
    0
  );

  if (taxableComponentSum > 0) {
    if (staffEmployee) {
      return Math.max(0, taxableComponentSum - premiumNontax);
    }
    const premiumTax = calcHomeWagePremiumTaxableAmount(form, productionNontax);
    return Math.max(0, taxableComponentSum + premiumTax);
  }

  const wageTotal = Number(form.wageTotal) || 0;
  if (wageTotal <= 0) return 0;

  // 레거시: wageTotal이 비과세를 포함한 총지급(임금구성합계)인 경우
  return Math.max(0, wageTotal - totalNontax);
}

/** caion_tax_rate DECIMAL → number */
export function parseHomeWageTaxRateValue(
  v: number | string | undefined | null,
  fallback: number
): number {
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

/** 퍼센트 표시용 — trailing zero 제거 */
function trimInsignificantZeros(s: string): string {
  if (!s.includes('.')) return s;
  return s.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

/** 소수 문자열 × 100 (정밀도 유지) */
function decimalStringTimes100(s: string): string {
  const neg = s.startsWith('-');
  const raw = neg ? s.slice(1) : s;
  const [intPart = '0', fracPart = ''] = raw.split('.');
  if (fracPart.length >= 2) {
    const newInt = (intPart + fracPart.slice(0, 2)).replace(/^0+(?=\d)/, '') || '0';
    const newFrac = fracPart.slice(2);
    const body = newFrac ? `${newInt}.${newFrac}` : newInt;
    return (neg ? '-' : '') + body;
  }
  const padded = fracPart.padEnd(2, '0');
  const combined = (intPart + padded).replace(/^0+(?=\d)/, '') || '0';
  return (neg ? '-' : '') + combined;
}

export interface FormatHomeWageRatePercentOptions {
  /** true: 0.045 → 4.5% / false: 이미 퍼센트 값(인건비율 등) */
  scaleFromDecimal?: boolean;
  /** false: 입력란용 "4.5" (접미사 % 별도 표시) */
  withSuffix?: boolean;
}

/**
 * 요율 표시 — 소수(0.0068) 또는 DB DECIMAL → "0.68%" 또는 "0.68"
 * UI·툴팁·요율 패널 공통
 */
export function formatHomeWageRatePercent(
  val: number | string | null | undefined,
  options: FormatHomeWageRatePercentOptions = {}
): string {
  const scaleFromDecimal = options.scaleFromDecimal !== false;
  const withSuffix = options.withSuffix !== false;
  if (val === null || val === undefined || val === '') return withSuffix ? '—' : '';
  const strVal = String(val).trim().replace(/%/g, '');
  if (!strVal || Number.isNaN(Number(strVal))) return withSuffix ? '—' : '';
  let display = strVal;
  if (scaleFromDecimal) {
    display = strVal.includes('.') ? decimalStringTimes100(strVal) : String(Number(strVal) * 100);
  }
  const trimmed = trimInsignificantZeros(display);
  return withSuffix ? `${trimmed}%` : trimmed;
}

/** 입력 "0.68" / "0.68%" → 소수 0.0068 (DB·계산용) */
export function parseHomeWageRatePercentInput(input: string | number): number | null {
  const raw = String(input).trim().replace(/,/g, '').replace(/%/g, '');
  if (!raw) return null;
  const n = parseFloat(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return n / 100;
}

export function calcHomeWageHourlyLinkedPay(
  input: HomeWageHourlyLinkedPayInput
): HomeWageHourlyLinkedPayResult {
  const hourly = Number(input.payCalcHourlyWage) || 0;
  const totalSugaHours = Number(input.totalSugaHours) || 0;
  const workDays = Number(input.workDays) || 0;
  const annualLeaveDays = Number(input.annualLeaveDays) || 0;

  const ym = input.yearMonth;
  const basic = calcHomeWageBasePay(hourly, totalSugaHours, ym);
  const weekend = calcHomeWageWeeklyHolidayPay(basic);
  const annual = calcHomeWageAnnualLeavePay(
    hourly,
    totalSugaHours,
    workDays,
    annualLeaveDays
  );

  const overtimeHours = Number(input.overtimeHours) || 0;
  const nightHours = Number(input.nightHours) || 0;
  const holidayHours = Number(input.holidayHours) || 0;

  return {
    basic,
    weekend,
    annual,
    overtime: calcHomeWageCaregiverOvertimePay(hourly, overtimeHours, ym),
    night: calcHomeWageCaregiverNightPay(hourly, nightHours, ym),
    holiday: calcHomeWageCaregiverHolidayPay(hourly, holidayHours, ym),
  };
}

/** 임금 10원 단위 내림 */
export function floorWageToTen(won: number): number {
  if (!Number.isFinite(won) || won <= 0) return 0;
  return Math.floor(won / 10) * 10;
}

/** 4대보험·지원공제 보험분 — 원 단위 반올림 후 10원 미만 절삭 (calcEmployerDeductions 동일) */
export function insuranceWageToTen(won: number): number {
  if (!Number.isFinite(won) || won <= 0) return 0;
  return floorWageToTen(Math.round(won));
}

/**
 * 최적시급(조정시급): 표 합계를 기본급+주휴+연차로 나눌 때 10원 단위 역산 시급
 * = floor10(월급여 ÷ (총수가시간 × (1 + 0.2 + 연차비율)))
 */
export function calcOptimalHourlyWage(
  targetMonthly: number,
  totalSugaHours: number,
  workDays: number,
  annualLeaveDays: number
): number | null {
  const target = Math.round(Number(targetMonthly)) || 0;
  const hours = Number(totalSugaHours) || 0;
  if (target <= 0 || hours <= 0) return null;
  const annualRatio = calcAnnualLeaveRatio(workDays, annualLeaveDays);
  const totalRatio = 1 + HOME_WAGE_WEEKLY_HOLIDAY_RATE + annualRatio;
  if (totalRatio <= 0) return null;
  const floored = floorWageToTen(target / (hours * totalRatio));
  return floored > 0 ? floored : null;
}

/** 연차비율 = 연차일수 / 12 / 근무일수 */
export function calcAnnualLeaveRatio(workDays: number, annualLeaveDays: number): number {
  if (!Number.isFinite(workDays) || workDays <= 0) return 0;
  if (!Number.isFinite(annualLeaveDays) || annualLeaveDays <= 0) return 0;
  return annualLeaveDays / 12 / workDays;
}

export interface HomeWagePayBreakdown {
  /** 수급자별 합계금액 합계(월급여) */
  targetMonthly: number;
  minHourlyWage: number;
  totalSugaHours: number;
  workDays: number;
  annualLeaveDays: number;
  /** 최저시급 기준 합계 (참고) */
  minWageSubtotal: number;
  base: number;
  weeklyHoliday: number;
  annualLeave: number;
  total: number;
  adjustedHourlyWage: number | null;
  wasAdjusted: boolean;
}

function calcPayComponentsAtHourly(
  hourlyWage: number,
  totalSugaHours: number,
  workDays: number,
  annualLeaveDays: number
): { base: number; weeklyHoliday: number; annualLeave: number; total: number } {
  const base = calcHomeWageBasePay(hourlyWage, totalSugaHours);
  const weeklyHoliday = calcHomeWageWeeklyHolidayPay(base);
  const annualLeave = calcHomeWageAnnualLeavePay(
    hourlyWage,
    totalSugaHours,
    workDays,
    annualLeaveDays
  );
  return { base, weeklyHoliday, annualLeave, total: base + weeklyHoliday + annualLeave };
}

const EMPTY_PAY_BREAKDOWN = (targetMonthly = 0): HomeWagePayBreakdown => ({
  targetMonthly,
  minHourlyWage: 0,
  totalSugaHours: 0,
  workDays: 0,
  annualLeaveDays: 0,
  minWageSubtotal: 0,
  base: 0,
  weeklyHoliday: 0,
  annualLeave: 0,
  total: targetMonthly,
  adjustedHourlyWage: null,
  wasAdjusted: false,
});

/**
 * 수급자별 표 합계(월급여)에 맞춰 기본급·주휴·연차 분해
 * @param payCalcHourlyWage 지급계산시급(표 시급과 별개)
 */
export function calcHomeWagePayBreakdown(input: {
  /** 수급자별 표 합계금액 합계 */
  targetMonthly: number;
  /** 지급계산시급 — 기본급·주휴 산정용 */
  payCalcHourlyWage?: number;
  minHourlyWage: number;
  totalSugaHours: number;
  workDays: number;
  annualLeaveDays: number;
}): HomeWagePayBreakdown {
  const targetMonthly = Math.round(Number(input.targetMonthly)) || 0;
  const payCalcHourlyWage = Number(input.payCalcHourlyWage) || 0;
  const minHourlyWage = Number(input.minHourlyWage) || 0;
  const totalSugaHours = Number(input.totalSugaHours) || 0;
  const workDays = Number(input.workDays) || 0;
  const annualLeaveDays = Number(input.annualLeaveDays) || 0;

  if (targetMonthly <= 0) {
    return EMPTY_PAY_BREAKDOWN(0);
  }

  const minWageSubtotal =
    minHourlyWage > 0 && totalSugaHours > 0
      ? calcPayComponentsAtHourly(minHourlyWage, totalSugaHours, workDays, annualLeaveDays).total
      : 0;

  if (totalSugaHours <= 0) {
    return {
      ...EMPTY_PAY_BREAKDOWN(targetMonthly),
      minHourlyWage,
      minWageSubtotal,
    };
  }

  const annualRatio = calcAnnualLeaveRatio(workDays, annualLeaveDays);
  const totalRatio = 1 + HOME_WAGE_WEEKLY_HOLIDAY_RATE + annualRatio;

  /** 지급계산시급 기준 분해 후 표 합계에 맞춤(차액 → 연차) */
  if (payCalcHourlyWage > 0) {
    const payCalcParts = calcPayComponentsAtHourly(
      payCalcHourlyWage,
      totalSugaHours,
      workDays,
      annualLeaveDays
    );
    if (payCalcParts.total > 0 && payCalcParts.total <= targetMonthly) {
      const annualLeave = payCalcParts.annualLeave + (targetMonthly - payCalcParts.total);
      return {
        targetMonthly,
        minHourlyWage,
        totalSugaHours,
        workDays,
        annualLeaveDays,
        minWageSubtotal,
        base: payCalcParts.base,
        weeklyHoliday: payCalcParts.weeklyHoliday,
        annualLeave,
        total: targetMonthly,
        adjustedHourlyWage: payCalcHourlyWage,
        wasAdjusted: payCalcParts.total < targetMonthly,
      };
    }
  }

  const needsAdjustment = minWageSubtotal > 0 && minWageSubtotal < targetMonthly;

  if (!needsAdjustment && minWageSubtotal >= targetMonthly && minHourlyWage > 0) {
    const minParts = calcPayComponentsAtHourly(
      minHourlyWage,
      totalSugaHours,
      workDays,
      annualLeaveDays
    );
    let { base, weeklyHoliday, annualLeave } = minParts;
    let total = base + weeklyHoliday + annualLeave;
    if (total > targetMonthly) {
      annualLeave = Math.max(0, annualLeave - (total - targetMonthly));
      total = base + weeklyHoliday + annualLeave;
    }
    if (total < targetMonthly) {
      annualLeave += targetMonthly - total;
      total = targetMonthly;
    }
    return {
      targetMonthly,
      minHourlyWage,
      totalSugaHours,
      workDays,
      annualLeaveDays,
      minWageSubtotal,
      base,
      weeklyHoliday,
      annualLeave,
      total: targetMonthly,
      adjustedHourlyWage: null,
      wasAdjusted: false,
    };
  }

  const reverseHourly = targetMonthly / (totalSugaHours * totalRatio);
  const adjustedHourlyWage = floorWageToTen(reverseHourly);
  const adjusted = calcPayComponentsAtHourly(
    adjustedHourlyWage,
    totalSugaHours,
    workDays,
    annualLeaveDays
  );
  const diff = targetMonthly - adjusted.total;
  const annualLeave = adjusted.annualLeave + diff;

  return {
    targetMonthly,
    minHourlyWage,
    totalSugaHours,
    workDays,
    annualLeaveDays,
    minWageSubtotal,
    base: adjusted.base,
    weeklyHoliday: adjusted.weeklyHoliday,
    annualLeave,
    total: targetMonthly,
    adjustedHourlyWage: adjustedHourlyWage > 0 ? adjustedHourlyWage : null,
    wasAdjusted: needsAdjustment || adjustedHourlyWage !== reverseHourly,
  };
}

/**
 * 센터장 시점 — 기본급·주휴·연차 분해 (비과세 혜택 미적용)
 * - 식대 0 (식대 비과세 혜택 없음)
 * - 연차 surplus → 식대 깎기 없음 (연차는 breakdown 그대로)
 */
export function calcDirectorCaregiverLinkedPay(input: {
  targetMonthly: number;
  payCalcHourlyWage?: number;
  minHourlyWage: number;
  totalSugaHours: number;
  workDays: number;
  annualLeaveDays: number;
}): {
  breakdown: HomeWagePayBreakdown;
  lunch: number;
  annual: number;
} {
  const breakdown = calcHomeWagePayBreakdown(input);
  return {
    breakdown,
    lunch: 0,
    annual: breakdown.annualLeave,
  };
}

// ─────────────────────────────────────────────
// 비과세 설정 및 계산
// ─────────────────────────────────────────────

/** 방문요양 비과세 설정 */
export interface HomeWageNontaxSettings {
  /**
   * @deprecated 연야휴 월 20만 비과세는 생산직 체크와 무관하게 자동 적용.
   * DB `productionNontax` 하위호환용으로만 유지 (저장 시 관리자 기준 Y).
   */
  productionNontax: boolean;
  /** 식대 실비변상 처리 (퇴직금 제외) */
  lunchExpense: boolean;
}

export const DEFAULT_HOME_WAGE_NONTAX_SETTINGS: HomeWageNontaxSettings = {
  productionNontax: true,
  lunchExpense: true,
};

/** 연장/야간/휴일 비과세 계산 결과 */
export interface HomeWageOvertimeNontaxResult {
  overtimeNontax: number;
  overtimeTax: number;
  nightNontax: number;
  nightTax: number;
  holidayNontax: number;
  holidayTax: number;
  totalNontax: number;
}

/** 연장/야간/휴일 비과세 월 한도 */
export const HOME_WAGE_OVERTIME_NONTAX_MONTHLY_LIMIT = 200000;

/**
 * 연·야·휴 월 20만 비과세 적용 여부 (관리자·회계)
 *
 * - 대상: 연야휴가 관리자/회계에 반영된 경우
 *   (60h+ 기본 지급, override 지급, 가족요양 관리자 분리 표시 포함)
 * - 생산직 체크·직종·통상임금 조건 없음
 * - 가족요양: 포함 (관리자에 연야휴 금액이 잡히면 동일 20만 규칙)
 * - 센터장 시점: 과세/비과세 분리 미적용(합산 표시) → false
 * - 60h 미만·가족 센터장 미지급: 연야휴 칸 자체가 0이라 실질 영향 없음
 */
export function shouldApplyHomeWagePremiumMonthlyNontax(
  viewMode: HomeWagePayrollViewMode | string = 'admin'
): boolean {
  return viewMode !== 'director';
}

/**
 * 관리자 표시 칸 — 연·야·휴 전액을 월 20만 한도로 과세/비과세 재분리
 * (저장된 productionNontax=N·구 AD 값도 회계·상세가 동일하게 보이도록)
 */
export function applyHomeWageAccountingPremiumMonthlyNontax<
  T extends {
    overtime: number;
    overtimeNontax: number;
    night: number;
    nightNontax: number;
    holiday: number;
    holidayNontax: number;
  }
>(parts: T): T {
  const fullOvertime = (Number(parts.overtime) || 0) + (Number(parts.overtimeNontax) || 0);
  const fullNight = (Number(parts.night) || 0) + (Number(parts.nightNontax) || 0);
  const fullHoliday = (Number(parts.holiday) || 0) + (Number(parts.holidayNontax) || 0);
  if (fullOvertime + fullNight + fullHoliday <= 0) {
    return {
      ...parts,
      overtime: 0,
      overtimeNontax: 0,
      night: 0,
      nightNontax: 0,
      holiday: 0,
      holidayNontax: 0,
    };
  }
  const split = calcHomeWageOvertimeNontax(fullOvertime, fullNight, fullHoliday, true);
  return {
    ...parts,
    overtime: split.overtimeTax,
    overtimeNontax: split.overtimeNontax,
    night: split.nightTax,
    nightNontax: split.nightNontax,
    holiday: split.holidayTax,
    holidayNontax: split.holidayNontax,
  };
}

/**
 * 연장/야간/휴일 비과세 계산
 * - apply=true 시 월 20만원 한도 내에서 연장→야간→휴일 순서로 비과세 적용
 * - 초과분은 과세 처리
 * - 관리자·회계 연야휴 반영 분은 shouldApplyHomeWagePremiumMonthlyNontax 기준으로 apply
 */
export function calcHomeWageOvertimeNontax(
  overtime: number,
  night: number,
  holiday: number,
  productionNontax: boolean
): HomeWageOvertimeNontaxResult {
  if (!productionNontax) {
    return {
      overtimeNontax: 0,
      overtimeTax: overtime,
      nightNontax: 0,
      nightTax: night,
      holidayNontax: 0,
      holidayTax: holiday,
      totalNontax: 0,
    };
  }

  let remainingLimit = HOME_WAGE_OVERTIME_NONTAX_MONTHLY_LIMIT;

  const overtimeNontax = Math.min(overtime, remainingLimit);
  remainingLimit -= overtimeNontax;

  const nightNontax = Math.min(night, remainingLimit);
  remainingLimit -= nightNontax;

  const holidayNontax = Math.min(holiday, remainingLimit);

  return {
    overtimeNontax,
    overtimeTax: overtime - overtimeNontax,
    nightNontax,
    nightTax: night - nightNontax,
    holidayNontax,
    holidayTax: holiday - holidayNontax,
    totalNontax: overtimeNontax + nightNontax + holidayNontax,
  };
}

/** 식대·차량·자녀교육비 비과세 월 한도 (입력·산출 공통) */
export const HOME_WAGE_NONTAX_CASH_MONTHLY_LIMIT = 200000;

/** @deprecated HOME_WAGE_NONTAX_CASH_MONTHLY_LIMIT 와 동일 */
export const HOME_WAGE_LUNCH_MONTHLY_LIMIT = HOME_WAGE_NONTAX_CASH_MONTHLY_LIMIT;

export const HOME_WAGE_CAR_MONTHLY_LIMIT = HOME_WAGE_NONTAX_CASH_MONTHLY_LIMIT;
export const HOME_WAGE_CHILD_MONTHLY_LIMIT = HOME_WAGE_NONTAX_CASH_MONTHLY_LIMIT;

/** 식대 1일 기본 금액 (기본값) */
export const HOME_WAGE_LUNCH_DAILY_DEFAULT = 10000;

/** 식대 최소 지급 기준 (미만이면 0원) */
export const HOME_WAGE_LUNCH_MIN_THRESHOLD = 5000;

/**
 * 자동계산 중식대 + 센터장 입력 중식대 합산 (월 20만원 한도)
 * - form.lunch 는 이미 조정된 자동분(adjusted auto)이어야 함
 * - 자동(cwhd_lunch): 연차 잉여에서 차감
 * - 센터장(cwhd_lunch_director): 총급여에 추가되는 수당
 */
export function calcHomeWageCombinedLunch(
  adjustedAutoLunch: number | null | undefined,
  directorLunch: number | null | undefined
): number {
  const adjusted = Math.max(0, Number(adjustedAutoLunch) || 0);
  const director = clampHomeWageLunchDirectorInput(directorLunch);
  return Math.min(adjusted + director, HOME_WAGE_LUNCH_MONTHLY_LIMIT);
}

/** 센터장 중식대 입력 상한 — 센터장은 자동 산출분을 모르므로 항상 20만원 */
export const HOME_WAGE_LUNCH_DIRECTOR_INPUT_MAX = HOME_WAGE_LUNCH_MONTHLY_LIMIT;

export function clampHomeWageLunchDirectorInput(
  directorLunch: number | null | undefined
): number {
  return Math.min(
    Math.max(0, Number(directorLunch) || 0),
    HOME_WAGE_LUNCH_DIRECTOR_INPUT_MAX
  );
}

/**
 * 자동 산출 중식대(autoBase) + 센터장 입력에 따른 DB 저장용 자동분 조정
 * adjusted = min(autoBase, 20만 − director)
 */
export function resolveHomeWageAdjustedAutoLunch(
  autoBaseLunch: number | null | undefined,
  directorLunch: number | null | undefined
): number {
  const autoBase = Math.max(0, Number(autoBaseLunch) || 0);
  const director = clampHomeWageLunchDirectorInput(directorLunch);
  return Math.min(autoBase, HOME_WAGE_LUNCH_MONTHLY_LIMIT - director);
}

/**
 * 센터장 slip·명세서 표시용 자동 중식대 원값
 * payrollForm.lunch 는 센터장 UI에서 0으로 지우므로 autoBase·shadow·DB fallback 사용
 */
export function resolveHomeWageSlipAutoLunch(
  formLunch: number | null | undefined,
  autoBaseLunch: number | null | undefined,
  shadowLunch?: number | null | undefined
): number {
  const fromForm = Math.max(0, Number(formLunch) || 0);
  if (fromForm > 0) return fromForm;
  const fromRef = Math.max(0, Number(autoBaseLunch) || 0);
  if (fromRef > 0) return fromRef;
  const fromShadow = Math.max(0, Number(shadowLunch) || 0);
  if (fromShadow > 0) return fromShadow;
  return 0;
}

/** 중식 연차추출 판정용 — 수급자합·기본·주휴로 분리저장 오탐 방지 */
export type HomeWageLunchExtractContext = {
  basic?: number | null;
  weekend?: number | null;
  recipientTotal?: number | null;
  svc?: string | null;
};

/** row/form → 추출 판정 컨텍스트 */
export function buildHomeWageLunchExtractContext(
  row: Record<string, unknown> | null | undefined
): HomeWageLunchExtractContext {
  if (!row) return {};
  return {
    basic: (row.basic ?? row.cwhd_basic) as number | null | undefined,
    weekend: (row.weekend ?? row.cwhd_weekend) as number | null | undefined,
    recipientTotal: (row.recipientTotal as number | null | undefined) ?? undefined,
    svc: (row.svc ?? row.cwhd_svc) as string | null | undefined,
  };
}

/**
 * cwhd_annual에 자동 중식대가 아직 합산돼 있는지 (재추출·재차감 방지)
 * - 일반: 순수연차 ≥ 자동중식 (예: 56672 = 34472 + 22200)
 * - 소액 순수: annual≈lunch+소액 (예: 27200 = 5000 + 22200) — 순수가 중식의 10% 이상일 때만
 * - 이미 분리(34472 + lunch 22200 별도, 또는 연차≈중식인 분리 저장)면 false
 * ★ 오탐 예:
 *   - 연차 30984 + 중식 30000 분리 저장 → 구휴리스틱(a≤l×1.25)이 추출해 중식 누락
 *   - 김영남 5월 연차 509384 + 중식 160000 분리(중식만 추가) → pure≥l 이 추출해 합계 −16만
 *     → 수급자합과 비교해 「중식 별도 가산」이 더 가까우면 추출 금지
 */
export function shouldExtractAutoLunchFromAnnualField(
  annual: number | null | undefined,
  autoLunch: number | null | undefined,
  ctx?: HomeWageLunchExtractContext
): boolean {
  const a = dbWageAmount(annual);
  const l = dbWageAmount(autoLunch);
  if (l <= 0 || a <= l) return false;
  const pure = a - l;

  const basic = dbWageAmount(ctx?.basic);
  const weekend = dbWageAmount(ctx?.weekend);
  const rt =
    dbWageAmount(ctx?.recipientTotal)
    || (ctx?.svc ? calcHomeWageNonSevereRecipientTotalFromSvc(ctx.svc) : 0);
  if (rt > 0) {
    const errSeparateLunch = Math.abs(basic + weekend + a + l - rt);
    const errAnnualOnly = Math.abs(basic + weekend + a - rt);
    // 중식을 연차 밖에 더할 때 수급자합에 더 가까움 → 분리 저장(추출 금지)
    if (errSeparateLunch < errAnnualOnly) return false;
    // 연차만으로 수급자합이 맞는 경우:
    // - 분리·미지급 orphan 과 연차흡수(고영이 6월)를 여기선 구분 불가 → 추출 금지
    // - 연차흡수분은 isHomeWageAutoLunchAbsorbedInAnnual 로 회계 칸만 분리
    if (errAnnualOnly <= 1) return false;
  }

  if (pure >= l) return true;
  // 소액 순수 합산으로 볼 수 있을 때만 (연차가 중식의 125% 이하 + 순수≥중식의 10%)
  if (pure < l * 0.1) return false;
  return a <= Math.floor(l * 1.25);
}

/**
 * slip·센터장 연차 합산용 — DB cwhd_annual이 (순수연차+자동중식) 합산값일 때 순수 관리자 연차 분리
 * annual ≤ lunch 이면 이미 순수 연차로 간주
 */
export function resolveHomeWageAdminAnnualForSlip(
  dbAnnual: number | null | undefined,
  dbAutoLunch: number | null | undefined,
  ctx?: HomeWageLunchExtractContext
): number {
  const annual = dbWageAmount(dbAnnual);
  const lunch = dbWageAmount(dbAutoLunch);
  if (lunch <= 0) return annual;
  if (!shouldExtractAutoLunchFromAnnualField(annual, lunch, ctx)) return annual;
  return Math.max(0, annual - lunch);
}

/**
 * 관리자 폼 — 연차수당에서 자동 중식대만큼 떼어 lunch로 이동
 * (annual 36652 + lunch 0 → annual 52, lunch 36600)
 */
export function applyHomeWageExtractLunchFromAnnual(input: {
  annual: number | null | undefined;
  lunch?: number | null | undefined;
  autoLunch: number | null | undefined;
}): { annual: number; lunch: number; autoLunch: number } {
  const autoLunch = Math.max(0, dbWageAmount(input.autoLunch));
  const storedLunch = Math.max(0, dbWageAmount(input.lunch));
  const lunch = storedLunch > 0 ? storedLunch : autoLunch;
  let annual = dbWageAmount(input.annual);

  if (lunch <= 0) {
    return { annual, lunch: 0, autoLunch: 0 };
  }

  if (shouldExtractAutoLunchFromAnnualField(annual, lunch)) {
    annual = Math.max(0, annual - lunch);
  }

  return { annual, lunch, autoLunch: lunch };
}

/** autoBase 기준 회계용 합산 (저장 전 검증·표시용) */
export function calcHomeWageCombinedLunchFromAutoBase(
  autoBaseLunch: number | null | undefined,
  directorLunch: number | null | undefined
): number {
  const adjusted = resolveHomeWageAdjustedAutoLunch(autoBaseLunch, directorLunch);
  const director = clampHomeWageLunchDirectorInput(directorLunch);
  return adjusted + director;
}

export interface HomeWageLunchDirectorAdjustmentResult {
  autoBaseLunch: number;
  lunchDirector: number;
  lunch: number;
  combinedLunch: number;
  /** autoBase − adjusted (연차·기본급에 되돌릴 금액) */
  annualRestore: number;
}

/**
 * 센터장 입력 반영 — 자동 중식대 조정 + 연차(또는 기본급) 복원액 산출
 * @param currentAnnualOrBasic 식대 차감 후 연차(또는 가족요양 기본급) 현재값
 * ★ 저장 시점. 이미 줄어든 채 저장된 DB는
 *   `resolveHomeWageLunchDirectorCapAnnualRestore` 로 표시만 보정
 */
export function applyHomeWageLunchDirectorAdjustment(
  autoBaseLunch: number | null | undefined,
  directorLunch: number | null | undefined,
  currentAnnualOrBasic = 0
): HomeWageLunchDirectorAdjustmentResult & { annualOrBasic: number } {
  const autoBase = Math.max(0, Number(autoBaseLunch) || 0);
  const lunchDirector = clampHomeWageLunchDirectorInput(directorLunch);
  const lunch = resolveHomeWageAdjustedAutoLunch(autoBase, lunchDirector);
  const annualRestore = autoBase - lunch;
  return {
    autoBaseLunch: autoBase,
    lunchDirector,
    lunch,
    combinedLunch: lunch + lunchDirector,
    annualRestore,
    annualOrBasic: Math.max(0, (Number(currentAnnualOrBasic) || 0) + annualRestore),
  };
}

/**
 * @deprecated 센터장 입력 상한은 항상 20만원 — clampHomeWageLunchDirectorInput 사용
 */
export function calcHomeWageLunchDirectorMax(
  _autoLunch?: number | null
): number {
  return HOME_WAGE_LUNCH_DIRECTOR_INPUT_MAX;
}

// ─────────────────────────────────────────────
// 5. 식대(lunch)와 기본급/주휴/연차의 상관관계
// ─────────────────────────────────────────────
//
// 핵심 개념:
// - 월급여(합계금액) = Σ(수급자별 시급 × 서비스일수 × 수가시간)
// - 이 월급여를 기본급 → 주휴 → 연차 → 식대 순서로 배분
//
// ┌─────────────────────────────────────────────────────────────┐
// │ 배분 순서                                                    │
// ├─────────────────────────────────────────────────────────────┤
// │ 1. 기본급 = 최저시급 × 총수가시간                            │
// │ 2. 주휴수당 = 기본급 × 0.2                                   │
// │ 3. 연차수당 = (최저시급 × 총수가시간 ÷ 근무일수) × 연차일수 ÷ 12 │
// │ 4. 남은금액 = 월급여 - 기본급 - 주휴 - 연차                   │
// │ 5. 식대 = min(점심근무일 × 1일식대, 남은금액, 20만원)         │
// │    - 1일식대 기본값 = 1만원                                   │
// │    - 백원 단위 내림                                          │
// │    - 5,000원 미만이면 0원                                    │
// │ 6. 연차수당 += (남은금액 - 식대) ← 차액을 연차에 추가          │
// └─────────────────────────────────────────────────────────────┘
//
// 상관관계 요약:
// | 시급 변화 | 남은금액 | 식대                   | 연차 추가분 |
// |----------|---------|------------------------|-----------|
// | 시급 ↑   | ↑       | 최대 20만원까지 ↑       | ↑         |
// | 시급 ↓   | ↓       | ↓ (5천원 미만이면 0원)  | ↓         |
// | 시급=최저 | 최소    | 적거나 0원              | 적음       |
//
// 핵심: 식대는 남은금액에서 지급되므로, 시급이 높을수록 식대를 더 많이 받을 수 있고,
//       시급이 낮으면 식대가 줄거나 0원이 됩니다. 식대로 지급하지 못한 금액은 연차수당에 추가됩니다.
// ─────────────────────────────────────────────────────────────

/** 식대 계산 결과 */
export interface HomeWageLunchCalcResult {
  /** 요청 식대 (점심근무일 × 1일식대) */
  requestedLunch: number;
  /** 남은금액 (월급여 - 기본급 - 주휴 - 연차) */
  remainingAmount: number;
  /** 실제 지급 식대 (백원 단위 내림, 5천원 미만이면 0) */
  actualLunch: number;
  /** 연차에 추가할 금액 (남은금액 - 실제식대) */
  annualLeaveAddition: number;
  /** 5천원 미만으로 식대 미지급 여부 */
  isBelowMinThreshold: boolean;
}

/**
 * 식대 계산
 * @param lunchWorkDays 점심근무일 (11:30~13:30 사이 근무한 일수)
 * @param monthlyWage 월급여 (수급자별 합계금액 합계)
 * @param basePay 기본급
 * @param weeklyHolidayPay 주휴수당
 * @param annualLeavePay 연차수당 (식대 배분 전)
 * @param dailyLunchAmount 1일 식대 금액 (기본값: 10,000원)
 */
export function calcHomeWageLunch(
  lunchWorkDays: number,
  monthlyWage: number,
  basePay: number,
  weeklyHolidayPay: number,
  annualLeavePay: number,
  dailyLunchAmount: number = HOME_WAGE_LUNCH_DAILY_DEFAULT
): HomeWageLunchCalcResult {
  // 남은금액 계산
  const subtotal = basePay + weeklyHolidayPay + annualLeavePay;
  const remainingAmount = Math.max(0, monthlyWage - subtotal);

  // 요청 식대 = 점심근무일 × 1일식대
  const requestedLunch = lunchWorkDays * dailyLunchAmount;

  // 실제 식대 = min(요청식대, 남은금액, 20만원)
  let actualLunch = Math.min(requestedLunch, remainingAmount, HOME_WAGE_LUNCH_MONTHLY_LIMIT);

  // 백원 단위 내림
  actualLunch = Math.floor(actualLunch / 100) * 100;

  // 5천원 미만이면 0원
  const isBelowMinThreshold = actualLunch < HOME_WAGE_LUNCH_MIN_THRESHOLD;
  if (isBelowMinThreshold) {
    actualLunch = 0;
  }

  // 연차에 추가할 금액 = 남은금액 - 실제식대
  const annualLeaveAddition = remainingAmount - actualLunch;

  return {
    requestedLunch,
    remainingAmount,
    actualLunch,
    annualLeaveAddition,
    isBelowMinThreshold,
  };
}

/** 요양보호사 자동 중식대(cwhd_lunch) 산출 입력 */
export interface HomeWageAutoLunchInput {
  /** 식대 실비변상 — false면 자동 중식대 0 */
  lunchExpense: boolean;
  /** 가족요양 전용 — 잉여금 전체에서 식대 배분 */
  isFamilyCareOnly: boolean;
  /** 청구합계 − 중증가산 */
  nonSevereTarget: number;
  /** 점심근무일(11:30~13:30) */
  lunchWorkDayCount: number;
  /** 일반요양 — calcHomeWagePayBreakdown.annualLeave */
  annualLeaveBeforeLunch?: number;
  /** 일반요양 — calcHomeWageHourlyLinkedPay().annual (연차→식대 깎기 기준) */
  linkedMinAnnual?: number;
}

/** 요양보호사 자동 중식대(cwhd_lunch) 산출 결과 */
export interface HomeWageAutoLunchResult {
  autoLunch: number;
  /** 일반요양 — 식대 차감 후 연차 (가족요양은 null) */
  annualAfterLunch: number | null;
}

/**
 * 요양보호사 자동 중식대(cwhd_lunch) — ★ 단일 소스
 *
 * | 구분 | 산출 |
 * |------|------|
 * | 가족요양 + 실비변상 | min(점심일×1만, 20만, nonSevere) 백원내림, 5천 미만 0 |
 * | 일반요양 + 실비변상 | 연차 잉여(annual − linkedMinAnnual)에서 동일 규칙 |
 * | 실비변상 OFF | 0 |
 *
 * 센터장 UI: autoLunch → autoBaseLunchRef, payrollForm.lunch=0, slip·UI는 연차와 분리 표시
 * 관리자 UI: autoLunch → payrollForm.lunch, 연차/기본급에서 차감
 */
export function calcHomeWageAutoLunch(input: HomeWageAutoLunchInput): HomeWageAutoLunchResult {
  const annualBefore = Number(input.annualLeaveBeforeLunch) || 0;

  if (!input.lunchExpense || input.lunchWorkDayCount <= 0) {
    return {
      autoLunch: 0,
      annualAfterLunch: input.isFamilyCareOnly ? null : annualBefore,
    };
  }

  const maxLunch = Math.min(
    input.lunchWorkDayCount * HOME_WAGE_LUNCH_DAILY_DEFAULT,
    HOME_WAGE_LUNCH_MONTHLY_LIMIT
  );

  const roundAutoLunch = (sourceAmount: number): number => {
    const positive = Math.max(0, Number(sourceAmount) || 0);
    const rounded = Math.min(maxLunch, Math.floor(positive / 100) * 100);
    return rounded >= HOME_WAGE_LUNCH_MIN_THRESHOLD ? rounded : 0;
  };

  if (input.isFamilyCareOnly) {
    return {
      autoLunch: roundAutoLunch(input.nonSevereTarget),
      annualAfterLunch: null,
    };
  }

  const linkedMinAnnual = Number(input.linkedMinAnnual) || 0;
  if (annualBefore <= linkedMinAnnual) {
    return { autoLunch: 0, annualAfterLunch: annualBefore };
  }

  const autoLunch = roundAutoLunch(annualBefore - linkedMinAnnual);
  return {
    autoLunch,
    annualAfterLunch: annualBefore - autoLunch,
  };
}

/** 식대 포함 급여 배분 결과 */
export interface HomeWagePayDistributionResult {
  /** 기본급 */
  basePay: number;
  /** 주휴수당 */
  weeklyHolidayPay: number;
  /** 연차수당 (식대 차액 포함) */
  annualLeavePay: number;
  /** 식대 */
  lunchPay: number;
  /** 합계 (월급여와 일치) */
  total: number;
  /** 식대 계산 상세 */
  lunchCalc: HomeWageLunchCalcResult;
}

/**
 * 월급여를 기본급/주휴/연차/식대로 배분
 * @param monthlyWage 월급여 (수급자별 합계금액 합계)
 * @param minHourlyWage 최저시급
 * @param totalSugaHours 총수가시간
 * @param workDays 근무일수
 * @param annualLeaveDays 연차일수
 * @param lunchWorkDays 점심근무일수
 * @param dailyLunchAmount 1일 식대 금액 (기본값: 10,000원)
 */
export function distributeHomeWagePay(input: {
  monthlyWage: number;
  minHourlyWage: number;
  totalSugaHours: number;
  workDays: number;
  annualLeaveDays: number;
  lunchWorkDays: number;
  dailyLunchAmount?: number;
}): HomeWagePayDistributionResult {
  const {
    monthlyWage,
    minHourlyWage,
    totalSugaHours,
    workDays,
    annualLeaveDays,
    lunchWorkDays,
    dailyLunchAmount = HOME_WAGE_LUNCH_DAILY_DEFAULT,
  } = input;

  // 1단계: 기본급/주휴/연차 계산 (최저시급 기준)
  const basePay = calcHomeWageBasePay(minHourlyWage, totalSugaHours);
  const weeklyHolidayPay = calcHomeWageWeeklyHolidayPay(basePay);
  const initialAnnualLeavePay = calcHomeWageAnnualLeavePay(
    minHourlyWage,
    totalSugaHours,
    workDays,
    annualLeaveDays
  );

  // 2단계: 식대 계산
  const lunchCalc = calcHomeWageLunch(
    lunchWorkDays,
    monthlyWage,
    basePay,
    weeklyHolidayPay,
    initialAnnualLeavePay,
    dailyLunchAmount
  );

  // 3단계: 최종 연차수당 = 초기 연차 + 차액
  const finalAnnualLeavePay = initialAnnualLeavePay + lunchCalc.annualLeaveAddition;

  return {
    basePay,
    weeklyHolidayPay,
    annualLeavePay: finalAnnualLeavePay,
    lunchPay: lunchCalc.actualLunch,
    total: basePay + weeklyHolidayPay + finalAnnualLeavePay + lunchCalc.actualLunch,
    lunchCalc,
  };
}

export type HomeWageNontaxViewMode = 'admin' | 'director';

/**
 * 비과세 수당 합계 계산
 * @param viewMode director — 센터장 입력 중식대만 / admin — 자동+센터장 합산
 * ★ 과세임금 산정·nonTax 저장용 — 연·야·휴는 재산출(fallback) 포함 가능
 * ★ 지급합계(회계대장)에는 calcHomeWageAccountingAddonNonTax 사용 (중복 가산 방지)
 */
export function calcHomeWageTotalNontax(
  form: Pick<
    HomeWagePayrollFormData,
    | 'lunch'
    | 'lunchDirector'
    | 'car'
    | 'child'
    | 'laborDay'
    | 'overtime'
    | 'night'
    | 'holiday'
    | 'overtimeNontax'
    | 'nightNontax'
    | 'holidayNontax'
  >,
  productionNontax = false,
  yearMonth?: string,
  viewMode: HomeWageNontaxViewMode = 'admin'
): number {
  const lunch =
    viewMode === 'director'
      ? clampHomeWageLunchDirectorInput(form.lunchDirector)
      : calcHomeWageCombinedLunch(form.lunch, form.lunchDirector);
  const car = Math.min(Number(form.car) || 0, HOME_WAGE_CAR_MONTHLY_LIMIT);
  const child = Math.min(Number(form.child) || 0, HOME_WAGE_CHILD_MONTHLY_LIMIT);
  const rawLaborDay = Number(form.laborDay) || 0;
  const laborDay =
    yearMonth && !isHomeWageLaborDayMonth(yearMonth) ? 0 : rawLaborDay;
  const premiumNontax = calcHomeWagePremiumNonTaxAllowance(form, productionNontax);

  return lunch + car + child + laborDay + premiumNontax;
}

/** 회계/관리자 지급합계에 더할 비과세 (식대·차량·자녀·노동절 + 중복 없는 연·야·휴) */
export function calcHomeWageAccountingAddonNonTax(
  form: Pick<
    HomeWagePayrollFormData,
    | 'wageTotal'
    | 'basic'
    | 'weekend'
    | 'annual'
    | 'senior'
    | 'sunim'
    | 'bosu'
    | 'severe'
    | 'bonus'
    | 'welfare'
    | 'other'
    | 'lunch'
    | 'lunchDirector'
    | 'car'
    | 'child'
    | 'laborDay'
    | 'overtime'
    | 'night'
    | 'holiday'
    | 'overtimeNontax'
    | 'nightNontax'
    | 'holidayNontax'
  >,
  productionNontax = false,
  yearMonth?: string,
  viewMode: HomeWageNontaxViewMode = 'admin'
): number {
  const lunchDirector = clampHomeWageLunchDirectorInput(form.lunchDirector);
  /**
   * 연차(cwhd_annual)에 자동중식이 합산된 레거시 — wage_total에 이미 포함되므로
   * 자동중식만 재가산 금지 (예: 김영남 5월 연차 509384+중식 160000)
   * ★ 센터장 입력 중식(lunchDirector)은 추가지급 — extract 여부와 무관하게 항상 합산
   *   (김영남 6월: extract 오탐 시 lunchDirector 180000까지 0 처리되면 안 됨)
   */
  const extractCtx = buildHomeWageLunchExtractContext(form as Record<string, unknown>);
  const lunchAlreadyInAnnual =
    shouldExtractAutoLunchFromAnnualField(form.annual, form.lunch, extractCtx)
    || isHomeWageAutoLunchAbsorbedInAnnual(form as Record<string, unknown>);
  const autoLunch = lunchAlreadyInAnnual ? 0 : Math.max(0, Number(form.lunch) || 0);
  const lunch =
    viewMode === 'director'
      ? lunchDirector
      : Math.min(autoLunch + lunchDirector, HOME_WAGE_LUNCH_MONTHLY_LIMIT);
  const car = Math.min(Number(form.car) || 0, HOME_WAGE_CAR_MONTHLY_LIMIT);
  const child = Math.min(Number(form.child) || 0, HOME_WAGE_CHILD_MONTHLY_LIMIT);
  const rawLaborDay = Number(form.laborDay) || 0;
  const laborDay =
    yearMonth && !isHomeWageLaborDayMonth(yearMonth) ? 0 : rawLaborDay;
  const premiumNontax = calcHomeWagePremiumNonTaxForGrossPay(form, productionNontax);
  return lunch + car + child + laborDay + premiumNontax;
}

/**
 * 인건비절감액(c_save) 산정 기준 비과세액
 * = 비과세 합계(calcHomeWageTotalNontax) − 차량비(car) − 자녀교육비(child)
 * (방문요양·요양시설·공통 임금 — calcWageCenterSavingsNontaxBase 공통)
 */
export function calcHomeWageCenterSavingsNontaxBase(
  form: Pick<
    HomeWagePayrollFormData,
    | 'lunch'
    | 'lunchDirector'
    | 'car'
    | 'child'
    | 'laborDay'
    | 'overtime'
    | 'night'
    | 'holiday'
    | 'overtimeNontax'
    | 'nightNontax'
    | 'holidayNontax'
  >,
  productionNontax = false,
  yearMonth?: string,
  viewMode: HomeWageNontaxViewMode = 'admin'
): number {
  const totalNontax = calcHomeWageTotalNontax(form, productionNontax, yearMonth, viewMode);
  return calcWageCenterSavingsNontaxBase(totalNontax, form.car, form.child);
}

// ─────────────────────────────────────────────
// 9-3. 퇴직금 · 지원공제 (요양·비요양 공통)
// ─────────────────────────────────────────────
//
// | 구분 | 필드 | 계산식 | 차감 위치 |
// |------|------|--------|-----------|
// | 기관 부담금 퇴직금 | c_out (c_twe) | (총 지급금액 [−식대 실비변상]) ÷ 12 | 기관공제 |
// | 지원공제 내 퇴직금 | l_jiwon 일부 | (선택된 지원공제 base) ÷ 12 | 근로자공제 |
//
// 퇴직금 산정 기준 = getHomeWageLedgerGrossPay (총 지급금액)
// - lunchExpense=true(실비변상) 시 식대(lunch) 차감
// applyRetirement 체크 + 「계산」 → c_out 산출 (60h·대표자도 체크 시 가능)
//
// 지원공제 base = senior + bosu + welfare + severe + sunim (항목 선택 가능)
// 지원공제 = base × (해당 사원에게 적용되는 보험요율만) + base÷12
// - 보험분: insuranceWageToTen (= 원 반올림 → 10원 미만 절삭)
// - 퇴직금분: floorWageToTen(Math.round(base÷12))
// - 65세 이상 등 국민연금 미적용 시: 국민연금 제외 (yeonDeduct=false)
// - 건강·장기요양보험: 나이와 무관하게 과세임금(건강)·건강보험료(장기) 기준 계산
// - 65세 이상 등 고용보험 미적용 시: 고용보험 제외 (goDeduct=false)
//
// ─────────────────────────────────────────────

/**
 * 총급여(totalPay) = 지급금액(grossPay) = 과세합계 + 비과세
 * 기본급·주휴·연차·식대·장기근속·중증가산·휴일·연장 등 모든 지급 수당 합계
 */
export function calcHomeWageTotalPay(row: HomeWageLedgerAmountRow): number {
  return getHomeWageLedgerGrossPay(row);
}

export interface HomeWageSupportDeductionRates {
  rateYeon: number;
  rateGun: number;
  rateJang: number;
  rateGo: number;
  rateSil: number;
  rateSan: number;
}

export interface HomeWageSupportDeductionInput {
  /** 장기근속수당 (senior) */
  senior: number;
  /** 보수교육비 (bosu) */
  bosu?: number;
  /** 중증수급자가산 (severe) */
  severe: number;
  /** 처우개선비 (welfare) */
  welfare?: number;
  /** 선임수당 (sunim) — 센터장/사복 */
  sunim?: number;
  rates: HomeWageSupportDeductionRates;
  /** 국민연금 공제 포함 (근로자 국민연금 0이면 false) */
  yeonDeduct?: boolean;
  /** 고용보험 공제 포함 (근로자 고용 0이면 false) — 실업급여는 goDeduct와 무관 */
  goDeduct?: boolean;
  /** 지원공제 내 퇴직금분(base÷12) 포함 여부 — 60h 미만·대표자·체크 해제 시 false */
  includeRetirement?: boolean;
}

export interface HomeWageSupportDeductionResult {
  base: number;
  yeon: number;
  gun: number;
  jang: number;
  go: number;
  sil: number;
  san: number;
  retirement: number;
  total: number;
}

/**
 * 지원공제(l_jiwon) — 공단 지원금(5종)에 대한 보험료+퇴직금을 근로자에서 공제
 *
 * base = senior + bosu + welfare + severe + sunim (호출 전 항목 선택 반영)
 * = base×국민 + base×건강 + 건강×장기 + base×고용 + base×실업 + base×산재 + base÷12
 * 단, 현재 사원에게 적용되지 않는 공제 항목(국민연금, 고용보험 등)은 제외
 */
export function calcHomeWageSupportDeduction(
  input: HomeWageSupportDeductionInput
): HomeWageSupportDeductionResult {
  const base = Math.max(
    0,
    (Number(input.senior) || 0) +
      (Number(input.bosu) || 0) +
      (Number(input.severe) || 0) +
      (Number(input.welfare) || 0) +
      (Number(input.sunim) || 0)
  );
  const empty: HomeWageSupportDeductionResult = {
    base: 0,
    yeon: 0,
    gun: 0,
    jang: 0,
    go: 0,
    sil: 0,
    san: 0,
    retirement: 0,
    total: 0,
  };
  if (base <= 0) return empty;

  const yeonDeduct = input.yeonDeduct !== false;
  const goDeduct = input.goDeduct !== false;
  const { rates } = input;

  const yeon = yeonDeduct ? insuranceWageToTen(base * rates.rateYeon) : 0;
  const gun = insuranceWageToTen(base * rates.rateGun);
  const jang = insuranceWageToTen(gun * rates.rateJang);
  const go = goDeduct ? insuranceWageToTen(base * rates.rateGo) : 0;
  const sil = insuranceWageToTen(base * rates.rateSil);
  const san = insuranceWageToTen(base * rates.rateSan);
  const retirement =
    input.includeRetirement !== false ? floorWageToTen(Math.round(base / 12)) : 0;
  const total = yeon + gun + jang + go + sil + san + retirement;

  return { base, yeon, gun, jang, go, sil, san, retirement, total };
}

export type HomeWageRetirementInapplicableReason = 'under60' | 'representative';

export interface HomeWageRetirementApplicableInput {
  /** 월 수가시간(요양보호사) 또는 근무시간(비요양) */
  workHours?: number | null;
  isRepresentative?: boolean;
}

export interface HomeWageRetirementApplicableResult {
  applicable: boolean;
  reason: HomeWageRetirementInapplicableReason | null;
  message: string;
}

/** 퇴직금 미적용 경고 메시지 */
export const HOME_WAGE_RETIREMENT_WARNINGS = {
  under60Hours: '퇴직금 적립 불가',
  representative: '⚠️ 대표자: 고용보험, 산재보험, 퇴직금 미적용',
} as const;

/** 퇴직금(c_out) 적용 가능 여부 — 60h·대표자 */
export function resolveHomeWageRetirementApplicable(
  input: HomeWageRetirementApplicableInput
): HomeWageRetirementApplicableResult {
  if (input.isRepresentative) {
    return {
      applicable: false,
      reason: 'representative',
      message: HOME_WAGE_RETIREMENT_WARNINGS.representative,
    };
  }
  const hours = Number(input.workHours);
  if (Number.isFinite(hours) && hours > 0 && hours < HOME_WAGE_INSURANCE_MIN_HOURS) {
    return {
      applicable: false,
      reason: 'under60',
      message: HOME_WAGE_RETIREMENT_WARNINGS.under60Hours,
    };
  }
  return { applicable: true, reason: null, message: '' };
}

/** 퇴직금 적용(고정) 체크박스 툴팁 */
export function getHomeWageRetirementApplyCheckboxTooltip(
  input: HomeWageRetirementApplicableInput
): string {
  const { applicable, message } = resolveHomeWageRetirementApplicable(input);
  const lines = [
    '체크 시 「계산」(지난달 데이터 가져오기 포함) 시',
    '퇴직금(c_out) 입력값은 자동계산으로 덮어쓰지 않습니다.',
    '요양보호사: 퇴직금 입력 클릭 → 지급금 유형 선택 모달',
    '  · 일반 방문요양(B1) / 방문목욕(B2) / 가족(있을 때만)',
    '  · 체크 합계 ÷12 · 미선택 시 직접 입력',
    '',
    '미체크 시 「계산」으로 자동 산출',
    '산식: 총 지급금액 ÷ 12 (중식비 실비변상 시 중식비 제외)',
  ];
  if (!applicable && message) {
    lines.push('', `자동 산출 불가 조건: ${message}`);
    lines.push('(체크 후 금액을 직접 입력·유지할 수 있습니다)');
  }
  return lines.join('\n');
}

/** DB cwhd_apply_retirement Y/N → 퇴직금 적용 체크 */
export function parseHomeWageApplyRetirementFlag(value: unknown): boolean {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  const s = String(value ?? '').trim().toUpperCase();
  if (s === 'Y' || s === 'YES' || s === 'TRUE') return true;
  if (s === 'N' || s === 'NO' || s === 'FALSE') return false;
  return true;
}

export function formatHomeWageApplyRetirementFlag(checked: boolean): 'Y' | 'N' {
  return checked ? 'Y' : 'N';
}

export interface HomeWageInstitutionRetirementOptions extends HomeWageRetirementApplicableInput {
  lunch?: number;
  lunchDirector?: number;
  /** 식대 실비변상 — true면 총 지급금액에서 식대 차감 후 ÷12 */
  lunchExpense?: boolean;
  /** UI 퇴직금 적용 체크 — false면 0, true면 법적 미적용(60h·대표)이어도 산출 */
  applyRetirement?: boolean;
}

/**
 * 퇴직금 산정 기준금액 = 총 지급금액(지급금액)
 * - lunchExpense=true(실비변상): 식대(lunch) 차감
 */
export function calcHomeWageRetirementBase(
  form: HomeWageTaxableIncomeForm & { nonTax?: number; wageTotal?: number; grossPay?: number },
  options: Pick<HomeWageInstitutionRetirementOptions, 'lunchExpense'> = {}
): number {
  let base = getHomeWageLedgerGrossPay(form);
  if (options.lunchExpense) {
    base -= calcHomeWageCombinedLunch(form.lunch, form.lunchDirector);
  }
  return Math.max(0, base);
}

/**
 * 기관 부담금 퇴직금(c_out)
 * - (총 지급금액 [− 식대 실비변상]) ÷ 12, 10원 절삭
 * - applyRetirement=false → 0
 * - applyRetirement=true → 60h·대표자 여부와 무관 산출
 * - applyRetirement 미지정 → 60h·대표자 법적 적용 가능 시만 산출
 */
export function calcHomeWageInstitutionRetirement(
  input: number | (HomeWageTaxableIncomeForm & { nonTax?: number; wageTotal?: number; grossPay?: number }),
  options: HomeWageInstitutionRetirementOptions | boolean = {}
): number {
  const opts: HomeWageInstitutionRetirementOptions =
    typeof options === 'boolean' ? { isRepresentative: options } : options;

  if (opts.applyRetirement === false) return 0;

  if (opts.applyRetirement !== true) {
    const { applicable } = resolveHomeWageRetirementApplicable({
      workHours: opts.workHours,
      isRepresentative: opts.isRepresentative,
    });
    if (!applicable) return 0;
  }

  let base: number;
  if (typeof input === 'number') {
    base = input;
    if (opts.lunchExpense) {
      base -= calcHomeWageCombinedLunch(opts.lunch, opts.lunchDirector);
    }
  } else {
    base = calcHomeWageRetirementBase(input, { lunchExpense: opts.lunchExpense });
  }

  return floorWageToTen(Math.max(0, base) / 12);
}

/** 퇴직금 산정용 청구 행 분류 — B1 일반 / B2 목욕 / 가족 */
export type HomeWageRetirementClaimCategory = 'visitCare' | 'visitBath' | 'family';

export function classifyHomeWageRetirementClaimRow(
  row: Pick<HomeWageRecipientFeeSummary, 'feeName' | 'feeCode' | 'isSevereRecipientSurcharge'>
): HomeWageRetirementClaimCategory | 'severe' {
  if (row.isSevereRecipientSurcharge) return 'severe';
  if (isFamilyCare(row.feeName)) return 'family';
  const code = String(row.feeCode || '')
    .trim()
    .toUpperCase();
  if (code.startsWith('B2')) return 'visitBath';
  return 'visitCare';
}

/** 퇴직금 산정용 — 일반 방문요양(B1) / 방문목욕(B2) / 가족방문요양 지급금액 배분 */
export interface HomeWageRetirementPaySplit {
  visitCarePay: number;
  visitBathPay: number;
  familyPay: number;
}

/**
 * 지급금액(grossPay)을 청구 수급자별 합계 비율로 배분
 * - B1 비가족 → visitCare, B2 비가족 → visitBath, 가족 → family
 * - 중증가산(severePay)은 일반 방문요양(B1) 지급금액에 포함
 */
export function calcHomeWageRetirementPaySplit(input: {
  summaryRows: Array<
    Pick<
      HomeWageRecipientFeeSummary,
      'feeName' | 'feeCode' | 'totalAmount' | 'isSevereRecipientSurcharge'
    >
  >;
  grossPay: number;
  severePay?: number;
}): HomeWageRetirementPaySplit {
  const gross = Math.max(0, Number(input.grossPay) || 0);
  const severePay = Math.max(0, Number(input.severePay) || 0);
  const splitableGross = Math.max(0, gross - severePay);

  let visitCareClaim = 0;
  let visitBathClaim = 0;
  let familyClaim = 0;

  for (const row of input.summaryRows) {
    const cat = classifyHomeWageRetirementClaimRow(row);
    if (cat === 'severe') continue;
    const amt = Number(row.totalAmount) || 0;
    if (amt <= 0) continue;
    if (cat === 'family') familyClaim += amt;
    else if (cat === 'visitBath') visitBathClaim += amt;
    else visitCareClaim += amt;
  }

  const claimTotal = visitCareClaim + visitBathClaim + familyClaim;
  if (claimTotal <= 0 || splitableGross <= 0) {
    return { visitCarePay: gross, visitBathPay: 0, familyPay: 0 };
  }

  const visitCareSplit = Math.round((splitableGross * visitCareClaim) / claimTotal);
  const visitBathSplit = Math.round((splitableGross * visitBathClaim) / claimTotal);
  const familySplit = Math.max(0, splitableGross - visitCareSplit - visitBathSplit);

  return {
    visitCarePay: visitCareSplit + severePay,
    visitBathPay: visitBathSplit,
    familyPay: familySplit,
  };
}

/** @deprecated calcHomeWageRetirementPaySplit.visitCarePay 사용 */
export interface HomeWageRetirementPaySplitLegacy {
  generalPay: number;
  familyPay: number;
}

/** 퇴직금 산정 — 지급금 유형 선택 또는 순수 수동 */
export interface HomeWageRetirementSourceSelection {
  useVisitCare: boolean;
  useVisitBath: boolean;
  useFamily: boolean;
  visitCarePay: number;
  visitBathPay: number;
  familyPay: number;
  /** 선택 항목 없을 때 c_out 직접입력값 */
  manualAmount?: number;
  lunchExpense?: boolean;
  lunch?: number;
  lunchDirector?: number;
}

export function calcHomeWageInstitutionRetirementFromSource(
  selection: HomeWageRetirementSourceSelection
): number {
  const useVisitCare = !!selection.useVisitCare;
  const useVisitBath = !!selection.useVisitBath;
  const useFamily = !!selection.useFamily;

  if (!useVisitCare && !useVisitBath && !useFamily) {
    return Math.max(0, Number(selection.manualAmount) || 0);
  }

  let base = 0;
  if (useVisitCare) base += Math.max(0, Number(selection.visitCarePay) || 0);
  if (useVisitBath) base += Math.max(0, Number(selection.visitBathPay) || 0);
  if (useFamily) base += Math.max(0, Number(selection.familyPay) || 0);

  if (selection.lunchExpense) {
    base -= calcHomeWageCombinedLunch(selection.lunch, selection.lunchDirector);
  }

  return floorWageToTen(Math.max(0, base) / 12);
}

/** 최고관리자 — 고객(급여÷12) vs 카이온 퇴직금 비교 행 */
export interface HomeWageRetirementCompareRow {
  seq: number;
  name: string;
  job: string;
  workHours: number;
  grossPay: number;
  /** 고객(센터장) 인식: 급여 ÷ 12 (1원 자리·원단위 절삭 = 10원 단위 내림) */
  customerRetirement: number;
  /** 카이온 산식. 60h 미만은 0 */
  caionRetirement: number;
  /** 화면·대장 저장 c_out */
  storedRetirement: number;
  /** 센터계산 − 시스템계산 */
  diffAmount: number;
  /** 월 60h 이상이면 퇴직금 표시, 미만이면 UI/출력에 '-' */
  retirementEligible: boolean;
  applyRetirement: boolean;
  lunchExpense: boolean;
  isRepresentative: boolean;
  /** 일치 / N원 차이 / 60h 미만 */
  note: string;
}

function pickHomeWageRetirementCompareFlag(row: Record<string, unknown>, ...keys: string[]): boolean {
  for (const key of keys) {
    const v = row[key];
    if (v === true || v === 1 || v === '1') return true;
    if (v === false || v === 0 || v === '0') return false;
    const s = String(v ?? '').trim().toUpperCase();
    if (s === 'Y' || s === 'YES' || s === 'TRUE') return true;
    if (s === 'N' || s === 'NO' || s === 'FALSE') return false;
  }
  return false;
}

function pickHomeWageRetirementCompareNumber(row: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    if (row[key] != null && row[key] !== '') {
      const n = Number(row[key]);
      if (Number.isFinite(n)) return n;
    }
  }
  return 0;
}

/**
 * 고객 계산(급여÷12)과 카이온 퇴직금 산식·저장값을 비교
 * - customer: floorWageToTen(총지급 ÷ 12) — 1원 자리(원단위) 절삭
 * - caion: 지급유형 선택 또는 (총지급[−식대실비]) ÷ 12 · 10원 절삭 (적용 체크와 무관 — 산식 결과)
 */
export function buildHomeWageRetirementCompareRow(
  row: Record<string, unknown>,
  seq: number,
  yearMonth = ''
): HomeWageRetirementCompareRow {
  const ym = String(yearMonth || row.yearMonth || row.cwhdir_ym || row.cwhd_ym || '')
    .replace(/\D/g, '')
    .slice(0, 6);
  const name = String(row.name ?? row.cwhdir_name ?? row.cwhd_name ?? '').trim() || '-';
  const job = String(row.job ?? row.cwhdir_job ?? row.cwhd_job ?? '').trim();
  const workHours = pickHomeWageRetirementCompareNumber(row, 'hour', 'cwhdir_hour', 'cwhd_hour');

  let grossPay = 0;
  if (hasHomeWageStoredCtParts(row)) {
    grossPay = sumHomeWageStoredCtPayParts(readHomeWageStoredCtParts(row, ym));
  } else {
    grossPay = getHomeWageLedgerGrossPay({
      ...row,
      yearMonth: ym,
      grossPay: pickHomeWageRetirementCompareNumber(row, 'grossPay', 'gross', 'cwhdir_gross', 'cwhdir_ct_gross'),
    } as HomeWageLedgerAmountRow);
  }

  const { lunch, lunchDirector } = resolveHomeWageRetirementLunchParts(row, ym);

  const lunchExpense = pickHomeWageRetirementCompareFlag(
    row,
    'lunchExpense',
    'cwhdir_lunch_expense',
    'cwhd_lunch_expense'
  );
  const applyRetirement = parseHomeWageApplyRetirementFlag(
    row.applyRetirement ?? row.cwhdir_apply_retirement ?? row.cwhd_apply_retirement
  );
  const isRepresentative = pickHomeWageRetirementCompareFlag(
    row,
    'isRepresentative',
    'cwhdir_is_representative',
    'cwhd_is_representative'
  );

  const useVisitCare = pickHomeWageRetirementCompareFlag(
    row,
    'retirementUseVisitCare',
    'cwhdir_retirement_use_visit_care',
    'cwhd_retirement_use_visit_care'
  );
  const useVisitBath = pickHomeWageRetirementCompareFlag(
    row,
    'retirementUseVisitBath',
    'cwhdir_retirement_use_visit_bath',
    'cwhd_retirement_use_visit_bath'
  );
  const useFamily = pickHomeWageRetirementCompareFlag(
    row,
    'retirementUseFamily',
    'cwhdir_retirement_use_family',
    'cwhd_retirement_use_family'
  );
  const visitCarePay = pickHomeWageRetirementCompareNumber(
    row,
    'retirementVisitCarePay',
    'cwhdir_retirement_visit_care_pay',
    'cwhd_retirement_visit_care_pay'
  );
  const visitBathPay = pickHomeWageRetirementCompareNumber(
    row,
    'retirementVisitBathPay',
    'cwhdir_retirement_visit_bath_pay',
    'cwhd_retirement_visit_bath_pay'
  );
  const familyPay = pickHomeWageRetirementCompareNumber(
    row,
    'retirementFamilyPay',
    'cwhdir_retirement_family_pay',
    'cwhd_retirement_family_pay'
  );
  const hasSource = useVisitCare || useVisitBath || useFamily;

  const retirementEligible = !(
    Number.isFinite(workHours)
    && workHours > 0
    && workHours < HOME_WAGE_INSURANCE_MIN_HOURS
  );

  const storedRetirement = getHomeWageLedgerRetirement({
    c_out: pickHomeWageRetirementCompareNumber(row, 'c_out', 'cwhdir_c_out', 'cwhd_c_out'),
  });

  if (!retirementEligible) {
    return {
      seq,
      name,
      job,
      workHours,
      grossPay,
      customerRetirement: 0,
      caionRetirement: 0,
      storedRetirement,
      diffAmount: 0,
      retirementEligible: false,
      applyRetirement,
      lunchExpense,
      isRepresentative,
      note: '60h 미만',
    };
  }

  const customerRetirement = floorWageToTen(Math.max(0, grossPay) / 12);

  let caionRetirement = 0;

  if (hasSource) {
    caionRetirement = calcHomeWageInstitutionRetirementFromSource({
      useVisitCare,
      useVisitBath,
      useFamily,
      visitCarePay,
      visitBathPay,
      familyPay,
      lunchExpense,
      lunch,
      lunchDirector,
    });
  } else {
    caionRetirement = calcHomeWageInstitutionRetirement(
      { grossPay, lunch, lunchDirector },
      {
        applyRetirement: true,
        lunchExpense,
        lunch,
        lunchDirector,
        workHours,
        isRepresentative,
      }
    );
  }

  const diffAmount = customerRetirement - caionRetirement;
  const note =
    diffAmount === 0
      ? '일치'
      : `${Math.abs(diffAmount).toLocaleString('ko-KR')}원 차이`;

  return {
    seq,
    name,
    job,
    workHours,
    grossPay,
    customerRetirement,
    caionRetirement,
    storedRetirement,
    diffAmount,
    retirementEligible: true,
    applyRetirement,
    lunchExpense,
    isRepresentative,
    note,
  };
}

/**
 * @deprecated calcHomeWageInstitutionRetirement(form, options) 사용
 */
export function calcHomeWageRetirement(
  grossPay: number,
  lunch?: number,
  lunchExpense?: boolean,
  isRepresentative = false
): number {
  let base = grossPay;
  if (lunchExpense) {
    base -= Number(lunch) || 0;
  }
  return calcHomeWageInstitutionRetirement(base, {
    lunch,
    lunchExpense: lunchExpense ?? false,
    isRepresentative,
  });
}

/** 비과세 소득세 절감 추정 세율 (레거시 CaregiverWage fallback) */
export const HOME_WAGE_ESTIMATED_NONTAX_TAX_RATE = 0.0726;

export interface HomeWageNontaxSavingsInput {
  nonTaxableAmount: number;
  /** (소득세+지방세)(과세+비과세) − (소득세+지방세)(과세만) */
  taxSavings: number;
  rateYeon: number;
  rateGun: number;
  rateJang: number;
  rateGo: number;
  rateSil: number;
  rateSan: number;
  applyPension: boolean;
  applyHealth: boolean;
  applyEmployment: boolean;
  /** 사복직: 근무 60h+ 시 퇴직금 절감(nonTax/12) 포함 (TabSabokWage) */
  includeRetirementSavings?: boolean;
}

/**
 * 실수령증가액 (레거시 CaregiverWage employeeTaxSavings)
 * 소득세·지방세 절감 + 4대보험 근로자부담 절감
 */
export function calcHomeWageEmployeeTaxSavings(input: HomeWageNontaxSavingsInput): number {
  const n = input.nonTaxableAmount;
  if (n <= 0) return 0;

  let taxSavings = input.taxSavings;
  if (taxSavings <= 0) {
    taxSavings = Math.round(n * HOME_WAGE_ESTIMATED_NONTAX_TAX_RATE);
  }

  const pension = input.applyPension ? floorWageToTen(n * input.rateYeon) : 0;
  const health = input.applyHealth ? floorWageToTen(n * input.rateGun) : 0;
  const jang = floorWageToTen(health * input.rateJang);
  const go = input.applyEmployment ? floorWageToTen(n * input.rateGo) : 0;

  return Math.max(0, taxSavings + pension + health + jang + go);
}

/**
 * 인건비절감액 (레거시 CaregiverWage centerTaxSavings / TabSabokWage + 퇴직금 절감)
 * 기준 비과세: calcHomeWageCenterSavingsNontaxBase (비과세합계 − 차량 − 자녀)
 */
export interface HomeWageCenterTaxSavingsItem {
  label: string;
  amount: number;
}

export function calcHomeWageCenterTaxSavingsBreakdown(
  input: HomeWageNontaxSavingsInput
): HomeWageCenterTaxSavingsItem[] {
  const n = input.nonTaxableAmount;
  if (n <= 0) return [];

  const items: HomeWageCenterTaxSavingsItem[] = [];

  const pension = input.applyPension ? floorWageToTen(n * input.rateYeon) : 0;
  if (pension > 0) items.push({ label: '국민연금', amount: pension });

  const health = floorWageToTen(n * input.rateGun);
  if (health > 0) items.push({ label: '건강보험', amount: health });

  const jang = floorWageToTen(health * input.rateJang);
  if (jang > 0) items.push({ label: '장기요양', amount: jang });

  const go = input.applyEmployment ? floorWageToTen(n * input.rateGo) : 0;
  if (go > 0) items.push({ label: '고용보험', amount: go });

  const sil = input.rateSil > 0 ? floorWageToTen(n * input.rateSil) : 0;
  if (sil > 0) items.push({ label: '실업급여', amount: sil });

  const san = input.rateSan > 0 ? floorWageToTen(n * input.rateSan) : 0;
  if (san > 0) items.push({ label: '산재보험', amount: san });

  const retirement = input.includeRetirementSavings ? floorWageToTen(n / 12) : 0;
  if (retirement > 0) items.push({ label: '퇴직금', amount: retirement });

  return items;
}

export function calcHomeWageCenterTaxSavings(input: HomeWageNontaxSavingsInput): number {
  return calcHomeWageCenterTaxSavingsBreakdown(input).reduce((sum, item) => sum + item.amount, 0);
}

/** 인건비 절감 모달 — 요보: 식대+연야휴 비과세 / 사복·센터장: 식대만 */
export interface HomeWagePolicyNontaxSavingsParts {
  isCaregiver: boolean;
  lunch: number;
  premiumNontax: number;
  total: number;
}

export function calcHomeWagePolicyNontaxSavingsParts(
  row: {
    job?: string | null;
    lunch?: number | null;
    overtime?: number | null;
    night?: number | null;
    holiday?: number | null;
    overtimeNontax?: number | null;
    nightNontax?: number | null;
    holidayNontax?: number | null;
    productionNontax?: string | boolean | null;
  }
): HomeWagePolicyNontaxSavingsParts {
  const isCaregiver = isHomeWageCaregiverJob(row.job);
  const lunch = calcHomeWageCombinedLunch(
    pickWageRowField(row, 'lunch', 'cwhd_lunch'),
    pickWageRowField(row, 'lunchDirector', 'cwhd_lunch_director')
  );
  let premiumNontax = 0;
  if (isCaregiver) {
    // 연야휴 반영분 — 생산직 플래그와 무관하게 월 20만 비과세 (가족 포함)
    premiumNontax = calcHomeWagePremiumNonTaxAllowance(
      {
        overtime: Number(row.overtime) || 0,
        night: Number(row.night) || 0,
        holiday: Number(row.holiday) || 0,
        overtimeNontax: Number(row.overtimeNontax) || 0,
        nightNontax: Number(row.nightNontax) || 0,
        holidayNontax: Number(row.holidayNontax) || 0,
      },
      true
    );
  }
  return { isCaregiver, lunch, premiumNontax, total: lunch + premiumNontax };
}

export interface HomeWagePolicySavingsRateInput {
  rateYeon: number;
  rateGun: number;
  rateJang: number;
  rateGo: number;
  rateSil: number;
  rateSan: number;
}

export interface HomeWagePolicySavingsRowInput {
  job?: string | null;
  birth?: string | null;
  hour?: number | string | null;
  lunch?: number | null;
  overtime?: number | null;
  night?: number | null;
  holiday?: number | null;
  overtimeNontax?: number | null;
  nightNontax?: number | null;
  holidayNontax?: number | null;
  productionNontax?: string | boolean | null;
  joinDate?: string | null;
}

function buildHomeWagePolicyCenterSavingsInput(
  row: HomeWagePolicySavingsRowInput,
  year: number,
  month: number,
  rates: HomeWagePolicySavingsRateInput,
  joinDate: string | null | undefined,
  nonTaxableAmount: number,
  isCaregiver: boolean
): HomeWageNontaxSavingsInput {
  const ageExempt = resolveHomeWageAgeInsurance(
    row.birth || '',
    year,
    month,
    joinDate ?? row.joinDate ?? null
  );
  const rateYeon = ageExempt.pensionExempt ? 0 : rates.rateYeon;
  const rateGo = ageExempt.employmentExempt ? 0 : rates.rateGo;
  const workHours =
    typeof row.hour === 'number'
      ? row.hour
      : parseFloat(String(row.hour ?? '').replace(/,/g, '')) || 0;

  return {
    nonTaxableAmount,
    taxSavings: 0,
    rateYeon,
    rateGun: rates.rateGun,
    rateJang: rates.rateJang,
    rateGo,
    rateSil: rates.rateSil,
    rateSan: rates.rateSan,
    applyPension: rateYeon > 0,
    applyHealth: rates.rateGun > 0,
    applyEmployment: rateGo > 0,
    includeRetirementSavings:
      !isCaregiver && workHours >= HOME_WAGE_INSURANCE_MIN_HOURS,
  };
}

/** 사원별 비과세 항목·절감액 (관리자 상세) */
export interface HomeWagePolicyNontaxSavingLine {
  nontaxLabel: string;
  nontaxAmount: number;
  savingAmount: number;
}

export interface HomeWagePolicyEmployeeSavingsDetail {
  name: string;
  job: string;
  lines: HomeWagePolicyNontaxSavingLine[];
  totalNontax: number;
  totalSaving: number;
}

function collectHomeWagePolicyNontaxLines(
  row: HomeWagePolicySavingsRowInput,
  parts: HomeWagePolicyNontaxSavingsParts
): Array<{ label: string; amount: number }> {
  const lines: Array<{ label: string; amount: number }> = [];
  if (parts.lunch > 0) lines.push({ label: HOME_WAGE_NONTAX_TERM.lunch, amount: parts.lunch });

  if (parts.isCaregiver && parts.premiumNontax > 0) {
    const ot = Number(row.overtimeNontax) || 0;
    const nt = Number(row.nightNontax) || 0;
    const ht = Number(row.holidayNontax) || 0;
    if (ot > 0) lines.push({ label: '연장 비과세', amount: ot });
    if (nt > 0) lines.push({ label: '야간 비과세', amount: nt });
    if (ht > 0) lines.push({ label: '휴일 비과세', amount: ht });
    if (ot + nt + ht === 0) {
      lines.push({ label: '연야휴 비과세', amount: parts.premiumNontax });
    }
  }

  return lines;
}

export function calcHomeWagePolicyEmployeeSavingsDetail(
  row: HomeWagePolicySavingsRowInput & { name?: string | null },
  year: number,
  month: number,
  rates: HomeWagePolicySavingsRateInput,
  joinDate?: string | null
): HomeWagePolicyEmployeeSavingsDetail {
  const parts = calcHomeWagePolicyNontaxSavingsParts(row);
  const name = String(row.name ?? '').trim();
  const job = String(row.job ?? '').trim();
  const nontaxLines = collectHomeWagePolicyNontaxLines(row, parts);

  const savingLines: HomeWagePolicyNontaxSavingLine[] = [];
  for (const line of nontaxLines) {
    if (line.amount <= 0) continue;
    const savingAmount = calcHomeWageCenterTaxSavings(
      buildHomeWagePolicyCenterSavingsInput(
        row,
        year,
        month,
        rates,
        joinDate,
        line.amount,
        parts.isCaregiver
      )
    );
    if (savingAmount > 0) {
      savingLines.push({
        nontaxLabel: line.label,
        nontaxAmount: line.amount,
        savingAmount,
      });
    }
  }

  const totalSaving =
    parts.total > 0
      ? calcHomeWageCenterTaxSavings(
          buildHomeWagePolicyCenterSavingsInput(
            row,
            year,
            month,
            rates,
            joinDate,
            parts.total,
            parts.isCaregiver
          )
        )
      : 0;

  return {
    name,
    job,
    lines: savingLines,
    totalNontax: parts.total,
    totalSaving,
  };
}

/** 급여대장 1인 — 정책 비과세(식대·연야휴) 기준 기관 절감액 */
export function calcHomeWagePolicyCenterSavings(
  row: HomeWagePolicySavingsRowInput,
  year: number,
  month: number,
  rates: HomeWagePolicySavingsRateInput,
  joinDate?: string | null
): number {
  const parts = calcHomeWagePolicyNontaxSavingsParts(row);
  if (parts.total <= 0) return 0;

  return calcHomeWageCenterTaxSavings(
    buildHomeWagePolicyCenterSavingsInput(
      row,
      year,
      month,
      rates,
      joinDate,
      parts.total,
      parts.isCaregiver
    )
  );
}

/**
 * 기관수익 = 청구 수가금액 합계 − 인건비(지급금액 + 기관공제)
 * 수가(청구) 데이터 없으면 0 (TabSabokWage)
 */
export function calcHomeWageCenterProfit(
  sugaTotal: number,
  grossPay: number,
  centerDeductionTotal: number
): number {
  if (sugaTotal <= 0) return 0;
  return sugaTotal - (grossPay + centerDeductionTotal);
}

// ─────────────────────────────────────────────
// 9-1. 급여대장 표시 금액 (caion_wage_home_data — 단일 소스)
// ─────────────────────────────────────────────
//
// ★★★ 모든 화면·출력은 아래 함수만 사용 ★★★
// - 프론트 상세/목록, 임금대장 엑셀, 인건비 지출내역
// - 백엔드: nodejs_caion/utils/homeWageLedgerDisplay.js (명세서 PDF 등)
//
// | 항목 | 기준 |
// |------|------|
// | 지급금액/임금합계/월급여 | cwhd_wage_total + cwhd_non_tax (API grossPay) |
// | 실수령액 | cwhd_net (저장값, 재계산 fallback 금지) |
// | 명세서·고객용 임금대장 연차 | 일반: cwhd_annual + cwhd_lunch (+ 60h 미만 시 연·야·휴 합산) / 가족요양 전용: 연차 0, 식대→기본급 |
// | 명세서·고객용 임금대장 휴일 | 일반: cwhd_holiday + cwhd_holiday_nontax (60h 미만 시 0, 연차 합산) / 가족요양 전용: 휴일 0, 금액→기본급 |
// | 기관수익 | Σ(수가금액) − (지급금액 + 기관공제합계) |
// | 기관부담 보험 | cwhd_c_yeon + … + cwhd_c_san (cwhd_c_total − cwhd_c_out 은 fallback) |
// | 퇴직적립금 | cwhd_c_out |
//

/** API/DB 급여대장 행 — 금액 표시용 최소 필드 */
export interface HomeWageLedgerAmountRow {
  wageTotal?: number | null;
  nonTax?: number | null;
  grossPay?: number | null;
  net?: number | null;
  l_total?: number | null;
  cwhd_l_total?: number | null;
}

export interface HomeWageLedgerNetPayOptions {
  /** 이미 산출된 지급금액(명세서·임금대장 표시 gross) */
  grossPay?: number;
  yearMonth?: string;
  /** accounting=회계처리용 / client=센터장·고객용 slip */
  reportType?: 'accounting' | 'client';
}

/** DB 저장값 → 숫자 (null/undefined/문자열 → 0) */
export function dbWageAmount(value: number | string | null | undefined): number {
  return Number(value) || 0;
}

/** 지급금액 — 정본이면 센터장 저장칸 합, 아니면 API grossPay */
export function getHomeWageLedgerGrossPay(row: HomeWageLedgerAmountRow): number {
  const rec = row as Record<string, unknown>;
  if (hasHomeWageStoredCtParts(rec)) {
    return sumHomeWageStoredCtPayParts(
      readHomeWageStoredCtParts(
        rec,
        String(rec.yearMonth ?? rec.cwhdir_ym ?? rec.cwhd_ym ?? '')
      )
    );
  }
  return dbWageAmount(row.grossPay);
}

/**
 * 관리자 시점 임금설정과 동일한 지급 칸
 * — 회계처리용 대장·명세서가 이 값을 그대로 쓴다
 */
export type HomeWageAdminSettingDisplayParts = {
  basic: number;
  weekend: number;
  annual: number;
  laborDay: number;
  overtime: number;
  overtimeNontax: number;
  night: number;
  nightNontax: number;
  holiday: number;
  holidayNontax: number;
  senior: number;
  sunim: number;
  bosu: number;
  severe: number;
  bonus: number;
  lunchAuto: number;
  lunchDirector: number;
  car: number;
  child: number;
  welfare: number;
  other: number;
};

/** 관리자 폼 연차·중식(자동)과 동일 — 흡수 고스트는 중식 0, 연차 추출만 분리 */
export function getHomeWageAdminSettingLunchAuto(row: Record<string, unknown>): number {
  if (hasHomeWageStoredAdParts(row)) {
    return readHomeWageStoredAdminAutoLunch(row);
  }
  if (isHomeWageDirectorCanonicalRow(row)) {
    return getHomeWageAdminPartsFromDirectorStore(
      mapHomeWageRowToDirectorStore(row),
      String(row.yearMonth ?? row.cwhd_ym ?? row.cwhdir_ym ?? '')
    ).lunchAuto;
  }
  const job = String(row.job ?? row.cwhd_job ?? '');
  const lunchRaw = pickHomeWageRowNumber(row, 'lunch', 'cwhd_lunch');
  if (!isHomeWageCaregiverJob(job)) {
    return resolveHomeWageStaffAccountingAutoLunch(row);
  }
  const annualRaw = pickHomeWageRowNumber(row, 'annual', 'cwhd_annual');
  const extractCtx = buildHomeWageLunchExtractContext(row);
  if (shouldExtractAutoLunchFromAnnualField(annualRaw, lunchRaw, extractCtx)) {
    return lunchRaw;
  }
  return resolveHomeWageFundedAutoLunch(row);
}

export function getHomeWageAdminSettingAnnual(row: Record<string, unknown>): number {
  if (hasHomeWageStoredAdParts(row)) {
    return readHomeWageStoredAdParts(row).annual;
  }
  if (isHomeWageDirectorCanonicalRow(row)) {
    return getHomeWageAdminPartsFromDirectorStore(
      mapHomeWageRowToDirectorStore(row),
      String(row.yearMonth ?? row.cwhd_ym ?? row.cwhdir_ym ?? '')
    ).annual;
  }
  const annualRaw = pickHomeWageRowNumber(row, 'annual', 'cwhd_annual');
  const lunchRaw = pickHomeWageRowNumber(row, 'lunch', 'cwhd_lunch');
  const extractCtx = buildHomeWageLunchExtractContext(row);
  if (shouldExtractAutoLunchFromAnnualField(annualRaw, lunchRaw, extractCtx)) {
    return resolveHomeWageAdminAnnualForSlip(annualRaw, lunchRaw, extractCtx);
  }
  return annualRaw;
}

export function getHomeWageAdminSettingLunchDirector(row: Record<string, unknown>): number {
  if (hasHomeWageStoredAdParts(row) || hasHomeWageStoredCtParts(row)) {
    return pickHomeWageRowNumber(
      row,
      'adLunch',
      'cwhdir_ad_lunch',
      'ctLunch',
      'cwhdir_ct_lunch',
      'lunchDirector'
    );
  }
  const job = String(row.job ?? row.cwhd_job ?? '');
  if (!isHomeWageCaregiverJob(job)) {
    return resolveHomeWageStaffEffectiveLunch(row).lunchDirector;
  }
  return clampHomeWageLunchDirectorInput(
    row.lunchDirector ?? row.cwhd_lunch_director
  );
}

export function getHomeWageAdminSettingDisplayParts(
  row: Record<string, unknown>,
  yearMonth = ''
): HomeWageAdminSettingDisplayParts {
  let parts: HomeWageAdminSettingDisplayParts;
  if (hasHomeWageStoredAdParts(row)) {
    parts = readHomeWageStoredAdParts(row, yearMonth);
  } else if (isHomeWageDirectorCanonicalRow(row)) {
    parts = getHomeWageAdminPartsFromDirectorStore(
      mapHomeWageRowToDirectorStore(row, yearMonth),
      yearMonth
    );
  } else {
    const ym = String(yearMonth || row.yearMonth || row.cwhd_ym || '')
      .replace(/\D/g, '')
      .slice(0, 6);
    const n = (...keys: string[]) => pickHomeWageRowNumber(row, ...keys);
    parts = {
      basic: getHomeWageAccountingDisplayBasic(row),
      weekend: n('weekend', 'cwhd_weekend'),
      annual: getHomeWageAdminSettingAnnual(row),
      laborDay: isHomeWageLaborDayMonth(ym) ? n('laborDay', 'cwhd_labor_day') : 0,
      overtime: n('overtime', 'cwhd_overtime'),
      overtimeNontax: n('overtimeNontax', 'cwhd_overtime_nontax'),
      night: n('night', 'cwhd_night'),
      nightNontax: n('nightNontax', 'cwhd_night_nontax'),
      holiday: n('holiday', 'cwhd_holiday'),
      holidayNontax: n('holidayNontax', 'cwhd_holiday_nontax'),
      senior: n('senior', 'cwhd_senior'),
      sunim: n('sunim', 'cwhd_sunim'),
      bosu: n('bosu', 'cwhd_bosu'),
      severe: n('severe', 'cwhd_severe'),
      bonus: n('bonus', 'cwhd_bonus'),
      lunchAuto: getHomeWageAdminSettingLunchAuto(row),
      lunchDirector: getHomeWageAdminSettingLunchDirector(row),
      car: n('car', 'cwhd_car'),
      child: n('child', 'cwhd_child'),
      welfare: n('welfare', 'cwhd_welfare'),
      other: n('other', 'cwhd_other'),
    };
  }
  return applyHomeWageAccountingPremiumMonthlyNontax(parts);
}

export function sumHomeWageAdminSettingDisplayPayParts(
  row: Record<string, unknown>,
  yearMonth = ''
): number {
  const p = getHomeWageAdminSettingDisplayParts(row, yearMonth);
  return (
    p.basic
    + p.weekend
    + p.annual
    + p.laborDay
    + p.overtime
    + p.overtimeNontax
    + p.night
    + p.nightNontax
    + p.holiday
    + p.holidayNontax
    + p.senior
    + p.sunim
    + p.bosu
    + p.severe
    + p.bonus
    + p.lunchAuto
    + p.lunchDirector
    + p.car
    + p.child
    + p.welfare
    + p.other
  );
}

/** 관리자 참고바 지급금액과 동일 — 요보=통합 slip, 그 외=관리자 과세+비과세 */
export function getHomeWageAdminSettingGrossPay(
  row: Record<string, unknown>,
  yearMonth = ''
): number {
  const ym = String(yearMonth || row.yearMonth || row.cwhd_ym || '')
    .replace(/\D/g, '')
    .slice(0, 6);
  if (isHomeWageDirectorCanonicalRow(row)) {
    return sumHomeWageAdminSettingDisplayPayParts(row, ym);
  }
  const job = String(row.job ?? row.cwhd_job ?? '');
  if (isHomeWageCaregiverJob(job)) {
    return calcHomeWageStoredUnifiedGrossPay(row, ym);
  }
  return getHomeWageStaffLedgerGrossPay(row);
}

/**
 * 회계처리용 — 관리자 시점 임금설정 칸 합
 */
export function sumHomeWageAccountingDisplayPayParts(
  row: Record<string, unknown>,
  yearMonth = ''
): number {
  return sumHomeWageAdminSettingDisplayPayParts(row, yearMonth);
}

/**
 * 회계처리용 임금대장·명세서 지급합계
 * ★ 관리자 시점 지급금액과 동일
 */
export function getHomeWageLedgerAccountingGrossPay(
  row: Record<string, unknown>,
  yearMonth = ''
): number {
  return getHomeWageAdminSettingGrossPay(row, yearMonth);
}

/** 고객용(센터장) 임금대장·명세서 지급합계 — 정본이면 ct_ 칸 합 */
export function getHomeWageLedgerClientGrossPay(
  row: HomeWageClientPayDisplayRow & HomeWageClientReportRow | Record<string, unknown>,
  yearMonth: string
): number {
  const rec = row as Record<string, unknown>;
  if (hasHomeWageStoredCtParts(rec)) {
    return sumHomeWageStoredCtPayParts(readHomeWageStoredCtParts(rec, yearMonth));
  }
  return calcHomeWageClientReportGrossPay(row as HomeWageClientReportRow, yearMonth);
}

/** 실수령액 = 지급금액 − 근로자공제 (프론트 참고바·명세서·임금대장 동일) */
export function calcHomeWageLedgerNetPayFromGross(
  grossPay: number,
  laborDeductionTotal: number
): number {
  return Math.max(0, dbWageAmount(grossPay) - dbWageAmount(laborDeductionTotal));
}

/**
 * 실수령액 — DB cwhd_net 레거시(가족요양 중식비 누락 등) 보정
 * grossPay 옵션이 있으면 그 값 기준, 없으면 reportType에 맞는 지급금액 재산출
 */
export function getHomeWageLedgerNetPay(
  row: HomeWageLedgerAmountRow & Record<string, unknown>,
  options?: HomeWageLedgerNetPayOptions
): number {
  const lTotal = dbWageAmount(row.l_total ?? row.cwhd_l_total);

  if (options?.grossPay != null) {
    return calcHomeWageLedgerNetPayFromGross(options.grossPay, lTotal);
  }

  const yearMonth = String(
    options?.yearMonth ?? row.yearMonth ?? row.cwhdir_ym ?? row.cwhd_ym ?? ''
  );
  const reportType = options?.reportType ?? 'accounting';

  const grossPay =
    reportType === 'client'
      ? getHomeWageLedgerClientGrossPay(row, yearMonth)
      : getHomeWageLedgerAccountingGrossPay(
          row as Parameters<typeof getHomeWageLedgerAccountingGrossPay>[0],
          yearMonth
        );

  return calcHomeWageLedgerNetPayFromGross(grossPay, lTotal);
}

/** 기관공제 행 — 보험·퇴직 표시용 */
export interface HomeWageLedgerCorpDeductRow {
  c_total?: number | null;
  c_out?: number | null;
  c_yeon?: number | null;
  c_gun?: number | null;
  c_jang?: number | null;
  c_go?: number | null;
  c_sil?: number | null;
  c_san?: number | null;
}

function sumHomeWageLedgerCorpInsuranceComponents(row: HomeWageLedgerCorpDeductRow): number | null {
  const keys: (keyof HomeWageLedgerCorpDeductRow)[] = [
    'c_yeon', 'c_gun', 'c_jang', 'c_go', 'c_sil', 'c_san',
  ];
  let hasComponentField = false;
  let sum = 0;
  for (const key of keys) {
    if (row[key] != null && row[key] !== '') {
      hasComponentField = true;
    }
    sum += dbWageAmount(row[key] as number | null | undefined);
  }
  return hasComponentField ? sum : null;
}

/** 기관부담 보험 = cwhd_c_yeon + … + cwhd_c_san (항목 합계 우선, 없으면 cwhd_c_total − cwhd_c_out) */
export function getHomeWageLedgerEmployerInsurance(row: HomeWageLedgerCorpDeductRow): number {
  const fromComponents = sumHomeWageLedgerCorpInsuranceComponents(row);
  if (fromComponents != null) {
    return fromComponents;
  }
  return Math.max(0, dbWageAmount(row.c_total) - dbWageAmount(row.c_out));
}

/** 기관공제합계 = 보험 항목 + cwhd_c_out (항목 합계 우선) */
export function getHomeWageLedgerCenterDeductionTotal(row: HomeWageLedgerCorpDeductRow): number {
  const fromComponents = sumHomeWageLedgerCorpInsuranceComponents(row);
  const cOut = dbWageAmount(row.c_out);
  if (fromComponents != null) {
    return fromComponents + cOut;
  }
  return dbWageAmount(row.c_total);
}

/** 퇴직적립금 = cwhd_c_out (DB 스냅샷) */
export function getHomeWageLedgerRetirement(row: HomeWageLedgerCorpDeductRow): number {
  return dbWageAmount(row.c_out);
}

/**
 * 대장·명세서 표시용 퇴직금
 * - 수동 고정(적용 ON · 지급유형 미선택): 저장 c_out
 * - 그 외: 실비변상 식대 차감 산식 (센터장 UI에서 lunch=0 이어도 자동중식 반영)
 * ★ 저장 c_out 이 구산식(식대 미차감)이어도 화면·대장 표시는 현행 정책과 맞춤
 */
export function resolveHomeWageDisplayRetirementAmount(
  row: Record<string, unknown>,
  yearMonth = ''
): number {
  const ym = String(yearMonth || row.yearMonth || row.cwhdir_ym || row.cwhd_ym || '')
    .replace(/\D/g, '')
    .slice(0, 6);
  const stored = getHomeWageLedgerRetirement({
    c_out: pickHomeWageRetirementCompareNumber(row, 'c_out', 'cwhdir_c_out', 'cwhd_c_out'),
  });
  const applyRetirement = parseHomeWageApplyRetirementFlag(
    row.applyRetirement ?? row.cwhdir_apply_retirement ?? row.cwhd_apply_retirement
  );
  const useVisitCare = pickHomeWageRetirementCompareFlag(
    row,
    'retirementUseVisitCare',
    'cwhdir_retirement_use_visit_care',
    'cwhd_retirement_use_visit_care'
  );
  const useVisitBath = pickHomeWageRetirementCompareFlag(
    row,
    'retirementUseVisitBath',
    'cwhdir_retirement_use_visit_bath',
    'cwhd_retirement_use_visit_bath'
  );
  const useFamily = pickHomeWageRetirementCompareFlag(
    row,
    'retirementUseFamily',
    'cwhdir_retirement_use_family',
    'cwhd_retirement_use_family'
  );
  const hasSource = useVisitCare || useVisitBath || useFamily;

  // 수동 직접입력 고정 — 저장값 유지
  if (applyRetirement && !hasSource) {
    return stored;
  }

  const lunchParts = resolveHomeWageRetirementLunchParts(row, ym);
  const lunchExpense = pickHomeWageRetirementCompareFlag(
    row,
    'lunchExpense',
    'cwhdir_lunch_expense',
    'cwhd_lunch_expense'
  );
  const workHours = pickHomeWageRetirementCompareNumber(row, 'hour', 'cwhdir_hour', 'cwhd_hour');
  const isRepresentative = pickHomeWageRetirementCompareFlag(
    row,
    'isRepresentative',
    'cwhdir_is_representative',
    'cwhd_is_representative'
  );

  if (hasSource) {
    return calcHomeWageInstitutionRetirementFromSource({
      useVisitCare,
      useVisitBath,
      useFamily,
      visitCarePay: pickHomeWageRetirementCompareNumber(
        row,
        'retirementVisitCarePay',
        'cwhdir_retirement_visit_care_pay',
        'cwhd_retirement_visit_care_pay'
      ),
      visitBathPay: pickHomeWageRetirementCompareNumber(
        row,
        'retirementVisitBathPay',
        'cwhdir_retirement_visit_bath_pay',
        'cwhd_retirement_visit_bath_pay'
      ),
      familyPay: pickHomeWageRetirementCompareNumber(
        row,
        'retirementFamilyPay',
        'cwhdir_retirement_family_pay',
        'cwhd_retirement_family_pay'
      ),
      lunchExpense,
      lunch: lunchParts.lunch,
      lunchDirector: lunchParts.lunchDirector,
    });
  }

  let grossPay = 0;
  if (hasHomeWageStoredCtParts(row)) {
    grossPay = sumHomeWageStoredCtPayParts(readHomeWageStoredCtParts(row, ym));
  } else {
    grossPay = getHomeWageLedgerGrossPay({
      ...row,
      yearMonth: ym,
      grossPay: pickHomeWageRetirementCompareNumber(
        row,
        'grossPay',
        'gross',
        'cwhdir_gross',
        'cwhdir_ct_gross'
      ),
    } as HomeWageLedgerAmountRow);
  }

  return calcHomeWageInstitutionRetirement(
    {
      grossPay,
      lunch: lunchParts.lunch,
      lunchDirector: lunchParts.lunchDirector,
    },
    {
      // 적용 체크 OFF(자동) — 60h·대표자 게이트 유지. ON+소스선택은 위에서 처리
      applyRetirement: applyRetirement ? true : undefined,
      lunchExpense,
      lunch: lunchParts.lunch,
      lunchDirector: lunchParts.lunchDirector,
      workHours,
      isRepresentative,
    }
  );
}

/** 퇴직금 식대 차감용 — (자동중식, 센터장중식). UI form.lunch=0 이어도 row 자동중식 사용 */
export function resolveHomeWageRetirementLunchParts(
  row: Record<string, unknown>,
  yearMonth = ''
): { lunch: number; lunchDirector: number } {
  const ym = String(yearMonth || row.yearMonth || row.cwhdir_ym || row.cwhd_ym || '')
    .replace(/\D/g, '')
    .slice(0, 6);
  const lunchAuto = Math.max(
    0,
    getHomeWageAdminSettingLunchAuto(row),
    pickHomeWageRetirementCompareNumber(
      row,
      'lunchAuto',
      'cwhdir_lunch_auto',
      'cwhd_lunch_auto'
    ),
    // 폼·레거시: lunch 칸에 자동중식만 있는 경우
    !hasHomeWageStoredCtParts(row)
      ? pickHomeWageRetirementCompareNumber(row, 'lunch', 'cwhd_lunch', 'cwhdir_lunch')
      : 0
  );
  const lunchDirector = Math.max(
    0,
    hasHomeWageStoredCtParts(row)
      ? readHomeWageStoredCtParts(row, ym).lunch
      : 0,
    pickHomeWageRetirementCompareNumber(
      row,
      'lunchDirector',
      'cwhdir_lunch_director',
      'cwhd_lunch_director',
      'ctLunch',
      'cwhdir_ct_lunch'
    )
  );
  return { lunch: lunchAuto, lunchDirector };
}

/** 명세서·고객용 임금대장 표시 — 연차+식대 합산 (DB cwhd_annual·cwhd_lunch는 분리 저장) */
export interface HomeWageDisplayAnnualLunchRow {
  basic?: number | null;
  weekend?: number | null;
  annual?: number | null;
  lunch?: number | null;
  lunchDirector?: number | null;
  night?: number | null;
  nightNontax?: number | null;
  nightHours?: number | string | null;
  holiday?: number | null;
  holidayNontax?: number | null;
  holidayHours?: number | string | null;
  overtime?: number | null;
  overtimeNontax?: number | null;
  overtimeHours?: number | string | null;
  hour?: number | string | null;
  /** 지급계산시급 — 사복/운전원 시급×시간 풀 산출 */
  payCalcHourly?: number | string | null;
  svc?: string | null;
  job?: string | null;
  /** 화면 청구 기준 가족요양 전용 — svc 없을 때 slip 표시용 */
  forceFamilyCareOnly?: boolean;
  /** 센터장 연장수당 지급 override — true/false/null(60h 정책 기본) */
  payOvertime?: boolean | string | null;
  /** 센터장 휴일수당 지급 override — true/false/null(60h 정책 기본) */
  payHoliday?: boolean | string | null;
  /** 센터장 야간수당 지급 override — true/false/null(60h 정책 기본) */
  payNight?: boolean | string | null;
  /**
   * 센터장 UI — 연차에 자동 중식대만 합산, 연·야·휴 pool 흡수 금지
   * (관리자 wageTotal 기준 금액 유지)
   */
  skipPremiumAnnualAbsorb?: boolean;
  /** 수급자별 합계(중증 제외) — 연차 cap 산출용 */
  recipientTotal?: number | null;
  /** 목록 월급여 대조용 — 자동중식 실지급 판별 */
  wageTotal?: number | null;
  nonTax?: number | null;
  grossPay?: number | null;
  cwhd_wage_total?: number | null;
  cwhd_non_tax?: number | null;
  /** 센터장 정본 — 연차에 자동중식 포함, slip에서 재합산 금지 */
  isDirectorCanonical?: boolean;
}

/** 센터장(client) 표시용 — cwhd_hour 등 추가 필드 */
export type HomeWageClientPayDisplayRow = HomeWageDisplayAnnualLunchRow;

/** 명세서·고객용 임금대장 — 연장수당(과세+비과세) 합계 */
export function getHomeWageOvertimeDisplayTotal(
  row: Pick<HomeWageDisplayAnnualLunchRow, 'overtime' | 'overtimeNontax'>
): number {
  return dbWageAmount(row.overtime) + dbWageAmount(row.overtimeNontax);
}

/** 명세서·고객용 임금대장 — 휴일수당(과세+비과세) 합계 */
export function getHomeWageHolidayDisplayTotal(
  row: Pick<HomeWageDisplayAnnualLunchRow, 'holiday' | 'holidayNontax'>
): number {
  return dbWageAmount(row.holiday) + dbWageAmount(row.holidayNontax);
}

/** 명세서·고객용 임금대장 — 야간수당(과세+비과세) 합계 */
export function getHomeWageNightDisplayTotal(
  row: Pick<HomeWageDisplayAnnualLunchRow, 'night' | 'nightNontax'>
): number {
  return dbWageAmount(row.night) + dbWageAmount(row.nightNontax);
}

function parseHomeWageHolidayHours(value: number | string | null | undefined): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function isHomeWageCaregiverJob(job?: string | null): boolean {
  return String(job || '').includes('요양보호사');
}

/** cwhd_svc 스냅샷 기준 가족요양 전용 (중증가산 제외) */
export function isHomeWageRowAllFamilyCareFromSvc(svc?: string | null): boolean {
  const rows = parseHomeWageRecipientSummaryFromSvc(svc);
  const nonSevere = rows.filter((r) => !r.isSevereRecipientSurcharge);
  if (nonSevere.length > 0) {
    return isAllFamilyCaregiver(
      nonSevere.map((r) => ({ feeName: r.feeName, sugaName: r.feeName }))
    );
  }
  return false;
}

/** svc 없을 때 DB 값으로 가족요양 전용 추정 (연차·주휴 0, 기본급 있음) */
export function inferHomeWageFamilyCareOnlyFromPayRow(
  row: Pick<HomeWageDisplayAnnualLunchRow, 'annual' | 'weekend' | 'basic'>
): boolean {
  return (
    dbWageAmount(row.annual) === 0 &&
    dbWageAmount(row.weekend) === 0 &&
    dbWageAmount(row.basic) > 0
  );
}

export interface HomeWageCaregiverSlipPayDisplay {
  basic: number;
  weekend: number;
  annual: number;
  /** 센터장 입력 중식비 (cwhd_lunch_director) */
  lunch: number;
  /** 자동 산출 중식비 (cwhd_lunch) — 일반 요양보호사·skipPremiumAnnualAbsorb 시 별도 표시 */
  lunchAuto: number;
  /** 명세서·고객용 — 연장수당(과세+비과세, 60h 미만 등 마스킹 시 0) */
  overtime: number;
  /** 명세서·고객용 임금대장 표시 야간수당 (가족요양 전용 시 0) */
  night: number;
  /** 급여산출방법 야간근로시간 (가족요양 전용 시 0) */
  nightHours: number;
  /** 명세서·고객용 임금대장 표시 휴일수당 (가족요양 전용 시 0) */
  holiday: number;
  /** 급여산출방법 휴일근로시간 (가족요양 전용 시 0) */
  holidayHours: number;
  isFamilyCareOnly: boolean;
  /** 센터장 표시 — 연·야·휴 금액 숨김(60h 미만·가족요양 전용 등) */
  maskPremiumDisplay: boolean;
}

function parseHomeWageNightHours(value: number | string | null | undefined): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** DB cwhd_hour — 월 근무·수가시간 */
export function parseHomeWageRowWorkHours(
  hour: number | string | null | undefined
): number {
  const n = Number(hour);
  if (Number.isFinite(n) && n > 0) return n;
  const s = String(hour ?? '')
    .replace(/,/g, '')
    .replace(/시간/g, '')
    .trim();
  const p = parseFloat(s);
  return Number.isFinite(p) && p > 0 ? p : 0;
}

/** 연·야·휴(과세+비과세) 합계 — DB 저장값 기준 */
export function getHomeWagePremiumFullTotalFromRow(
  row: Pick<
    HomeWageClientPayDisplayRow,
    'overtime' | 'overtimeNontax' | 'night' | 'nightNontax' | 'holiday' | 'holidayNontax'
  >
): number {
  return (
    getHomeWageOvertimeDisplayTotal(row)
    + getHomeWageNightDisplayTotal(row)
    + getHomeWageHolidayDisplayTotal(row)
  );
}

/**
 * 센터장(client) 표시 — 연·야·휴 숨김 여부
 * - 요양보호사: 가족요양 전용 또는 월 60h 미만
 * - 센터장/사복: 월 근무시간 60h 미만
 */
export function shouldHomeWageMaskPremiumClientDisplay(row: HomeWageClientPayDisplayRow): boolean {
  const hours = parseHomeWageRowWorkHours(row.hour);
  if (isHomeWageCaregiverJob(row.job)) {
    const isFamilyOnly =
      isHomeWageRowAllFamilyCareFromSvc(row.svc)
      || (!row.svc && inferHomeWageFamilyCareOnlyFromPayRow(row));
    if (isFamilyOnly) return true;
    return hours > 0 && hours < HOME_WAGE_INSURANCE_MIN_HOURS;
  }
  return hours > 0 && hours < HOME_WAGE_INSURANCE_MIN_HOURS;
}

/** 센터장 표시 — 연·야·휴 항목별 마스킹 (연·휴는 override 우선) */
function applyHomeWageClientPremiumMask(
  display: HomeWageCaregiverSlipPayDisplay,
  row: HomeWageClientPayDisplayRow
): HomeWageCaregiverSlipPayDisplay {
  if (!isHomeWageCaregiverJob(row.job)) {
    if (!shouldHomeWageMaskPremiumClientDisplay(row)) {
      return { ...display, maskPremiumDisplay: false };
    }
    const premiumTotal = getHomeWagePremiumFullTotalFromRow(row);
    return {
      ...display,
      maskPremiumDisplay: true,
      overtime: 0,
      night: 0,
      nightHours: 0,
      holiday: 0,
      holidayHours: 0,
      annual: display.annual + premiumTotal,
    };
  }

  if (display.isFamilyCareOnly) {
    return { ...display, maskPremiumDisplay: true };
  }

  const workHours = parseHomeWageRowWorkHours(row.hour);
  const payOvertimeOverride = parseHomeWageDirectorPremiumPayFlag(row.payOvertime);
  const payHolidayOverride = parseHomeWageDirectorPremiumPayFlag(row.payHoliday);
  const payNightOverride = parseHomeWageDirectorPremiumPayFlag(row.payNight);
  const payOvertime = resolveDirectorPremiumPayEnabled({
    type: 'overtime',
    payOverride: payOvertimeOverride,
    totalSugaHours: workHours,
  });
  const payHoliday = resolveDirectorPremiumPayEnabled({
    type: 'holiday',
    payOverride: payHolidayOverride,
    totalSugaHours: workHours,
  });
  const payNight = resolveDirectorPremiumPayEnabled({
    type: 'night',
    payOverride: payNightOverride,
    totalSugaHours: workHours,
  });

  const overtime = payOvertime ? display.overtime : 0;
  const holiday = payHoliday ? display.holiday : 0;
  const night = payNight ? display.night : 0;
  const nightHours = payNight ? display.nightHours : 0;
  const holidayHours = payHoliday ? display.holidayHours : 0;

  /** 명시적 미지급(N)은 연차 합산 없이 총액에서 제외 — 60h 기본 미지급만 연차 합산 */
  const absorbUnpaidPremiumToAnnual = (
    amount: number,
    payEnabled: boolean,
    payOverride: HomeWageDirectorPremiumPayOverride
  ) => {
    if (payEnabled || amount <= 0) return 0;
    if (payOverride === false) return 0;
    return amount;
  };

  /**
   * skipPremiumAnnualAbsorb여도 숨긴 연·야·휴는 연차에 합산
   * (칸만 숨기고 합계에서 빼면 기관용 < 신고용 — 60h 미만 ot/hol 비과세)
   */
  let annual = display.annual;
  if (!payOvertime) {
    annual += absorbUnpaidPremiumToAnnual(
      display.overtime,
      payOvertime,
      payOvertimeOverride
    );
  }
  if (!payHoliday) {
    annual += absorbUnpaidPremiumToAnnual(
      display.holiday,
      payHoliday,
      payHolidayOverride
    );
  }
  if (!payNight) {
    annual += absorbUnpaidPremiumToAnnual(
      display.night,
      payNight,
      payNightOverride
    );
  }

  return {
    ...display,
    overtime,
    holiday,
    night,
    nightHours,
    holidayHours,
    annual,
    maskPremiumDisplay: !payOvertime && !payHoliday && !payNight,
  };
}

/**
 * 요양보호사 명세서·고객용 임금대장(센터장) 표시용
 * - 식대 항목: 센터장 입력(cwhd_lunch_director)만 표시 (자동 식대는 연차에 합산)
 * - 요양보호사 일반(skipPremiumAnnualAbsorb): 자동 식대(cwhd_lunch) → 연차수당 합산
 * - 가족요양 전용: 자동 식대·야간·휴일수당 → 기본급 합산, 연차·야간·휴일 0
 * - 60h 미만·가족요양: 연·야·휴 숨김 → 일반 요양보호사는 연차에 합산(구규칙)
 * - 요양보호사 외: DB 값 + 60h 미만 시 연·야·휴 숨김
 */
function inferHomeWageSlipFamilyCareOnly(row: HomeWageClientPayDisplayRow): boolean {
  return (
    row.forceFamilyCareOnly === true
    || isHomeWageRowAllFamilyCareFromSvc(row.svc)
    || (!row.svc && inferHomeWageFamilyCareOnlyFromPayRow(row))
  );
}

/** 정본·센터장 저장칸 — 접기·60h 마스킹 없이 그대로 */
function buildHomeWageStoredClientSlipPayDisplay(
  row: HomeWageClientPayDisplayRow
): HomeWageCaregiverSlipPayDisplay {
  const rec = row as Record<string, unknown>;
  if (hasHomeWageStoredCtParts(rec)) {
    const ct = readHomeWageStoredCtParts(
      rec,
      String(rec.yearMonth ?? rec.cwhdir_ym ?? rec.cwhd_ym ?? '')
    );
    return {
      basic: ct.basic,
      weekend: ct.weekend,
      annual: ct.annual,
      lunch: ct.lunch,
      lunchAuto: 0,
      overtime: ct.overtime,
      night: ct.night,
      nightHours: ct.nightHours,
      holiday: ct.holiday,
      holidayHours: ct.holidayHours,
      isFamilyCareOnly: inferHomeWageSlipFamilyCareOnly(row),
      maskPremiumDisplay: false,
    };
  }
  return {
    basic: dbWageAmount(row.basic),
    weekend: dbWageAmount(row.weekend),
    annual: dbWageAmount(row.annual),
    lunch: dbWageAmount(row.lunchDirector),
    lunchAuto: 0,
    overtime: getHomeWageOvertimeDisplayTotal(row),
    night: getHomeWageNightDisplayTotal(row),
    nightHours: parseHomeWageNightHours(row.nightHours),
    holiday: getHomeWageHolidayDisplayTotal(row),
    holidayHours: parseHomeWageHolidayHours(row.holidayHours),
    isFamilyCareOnly: inferHomeWageSlipFamilyCareOnly(row),
    maskPremiumDisplay: false,
  };
}

export function getHomeWageCaregiverSlipPayDisplay(
  row: HomeWageClientPayDisplayRow
): HomeWageCaregiverSlipPayDisplay {
  if (
    hasHomeWageStoredCtParts(row as Record<string, unknown>)
    || isHomeWageDirectorCanonicalRow(row as Record<string, unknown>)
  ) {
    return buildHomeWageStoredClientSlipPayDisplay(row);
  }
  const basic = dbWageAmount(row.basic);
  const weekend = dbWageAmount(row.weekend);
  const annual = dbWageAmount(row.annual);
  const lunchAutoRaw = dbWageAmount(row.lunch);
  const lunchDirector = dbWageAmount(row.lunchDirector);
  const overtimeTotal = getHomeWageOvertimeDisplayTotal(row);
  const nightTotal = getHomeWageNightDisplayTotal(row);
  const nightHours = parseHomeWageNightHours(row.nightHours);
  const holidayTotal = getHomeWageHolidayDisplayTotal(row);
  const holidayHours = parseHomeWageHolidayHours(row.holidayHours);

  if (!isHomeWageCaregiverJob(row.job)) {
    // 요양보호사 외 — 센터장 표시
    // 기본급 = 시급×시간(풀). 자동중식(20만 채우기)은 숨김
    // 중식비 = 센터장 입력 (+ 레거시 자동칸 전액 승격분)
    const rowRec = row as Record<string, unknown>;
    const hourPool = resolveHomeWageStaffHourPool(rowRec);
    const effLunch = resolveHomeWageStaffEffectiveLunch(rowRec);
    const unfunded = calcHomeWageUnfundedAutoLunch(rowRec);
    const directorBasic =
      hourPool > 0
        ? hourPool
        : unfunded > 0
          ? basic
          : basic + lunchAutoRaw;
    const base: HomeWageCaregiverSlipPayDisplay = {
      basic: directorBasic,
      weekend,
      annual,
      lunch: effLunch.lunchDirector,
      lunchAuto: 0,
      overtime: overtimeTotal,
      night: nightTotal,
      nightHours,
      holiday: holidayTotal,
      holidayHours,
      isFamilyCareOnly: false,
      maskPremiumDisplay: false,
    };
    return applyHomeWageClientPremiumMask(base, row);
  }

  const isFamilyOnly =
    row.forceFamilyCareOnly === true
    || isHomeWageRowAllFamilyCareFromSvc(row.svc)
    || (!row.svc && inferHomeWageFamilyCareOnlyFromPayRow(row));

  if (isFamilyOnly) {
    return applyHomeWageClientPremiumMask(
      {
        basic: basic + lunchAutoRaw + nightTotal + holidayTotal + overtimeTotal,
        weekend,
        annual: 0,
        lunch: lunchDirector,
        lunchAuto: 0,
        overtime: 0,
        night: 0,
        nightHours: 0,
        holiday: 0,
        holidayHours: 0,
        isFamilyCareOnly: true,
        maskPremiumDisplay: true,
      },
      row
    );
  }

  /**
   * 센터장 — 기본급·주휴·연차는 저장/입력값 그대로
   * 자동중식은 관리자 시점 전용. 중식비는 센터장 입력분만.
   */
  return applyHomeWageClientPremiumMask(
    {
      basic,
      weekend,
      annual,
      lunch: lunchDirector,
      lunchAuto: 0,
      overtime: overtimeTotal,
      night: nightTotal,
      nightHours,
      holiday: holidayTotal,
      holidayHours,
      isFamilyCareOnly: false,
      maskPremiumDisplay: false,
    },
    row
  );
}

/** cwhd_svc — 중증수급자가산 제외 수급자별 합계 */
export function calcHomeWageNonSevereRecipientTotalFromSvc(
  svc?: string | null
): number {
  return parseHomeWageRecipientSummaryFromSvc(svc)
    .filter((r) => !r.isSevereRecipientSurcharge)
    .reduce((sum, r) => sum + (Number(r.totalAmount) || 0), 0);
}

/**
 * 센터장 — 연차 = 수급자별 합계(중증 제외) − 기본급 − 주휴
 * 자동중식·수당은 개입하지 않음. 수당은 센터장 직접 입력.
 */
export function resolveHomeWageDirectorAnnualFromRecipientCap(input: {
  recipientTotal: number;
  basic: number;
  weekend: number;
  proposedAnnual: number;
}): number {
  const recipientTotal = Math.max(0, Math.round(Number(input.recipientTotal) || 0));
  const basic = Math.max(0, Math.round(Number(input.basic) || 0));
  const weekend = Math.max(0, Math.round(Number(input.weekend) || 0));
  if (recipientTotal <= 0) {
    return Math.max(0, Math.round(Number(input.proposedAnnual) || 0));
  }
  return Math.max(0, recipientTotal - basic - weekend);
}

/**
 * 요양보호사 외(시설장·사복·운전원·나머지) — 시급×근무시간(풀)
 *
 * ★ 직종 갈래: 요양보호사 / 요양보호사 외
 * ★ 요양보호사 외 중식·기본급 (확정)
 *   · 센터장: 시급×시간 = 기본급, 중식 = 센터장 입력분만
 *   · 관리자: 시급×시간 = 기본급 + 중식비(자동)  ← 월 20만원 채우기
 *     예) 센터장 입력 2만 → 자동 18만, 기본급 = 풀 − 18만
 *   · 센터장 입력 중식은 풀 밖 추가지급 (총액 = 풀 + 센터장중식 + 기타)
 */
export function resolveHomeWageStaffHourPool(row: Record<string, unknown>): number {
  const hourly = dbWageAmount(
    (row.payCalcHourly ?? row.cwhd_pay_calc_hourly) as number | null | undefined
  );
  const hours = parseHomeWageRowWorkHours(
    (row.hour ?? row.cwhd_hour) as number | string | null | undefined
  );
  if (hourly > 0 && hours > 0) {
    return Math.floor(hourly * hours);
  }

  /**
   * hour/시급 스냅샷 없을 때(목록 draft 등) — 기본급·중식으로 풀 복원
   * · 이중차감(안태근): basic = 풀−자동−센터장, nonTax에 자동 미포함 → 풀 = basic+자동+센터장
   * · 정상 관리자 차감: basic = 풀−자동 → 풀 = basic+자동
   * 운전원·사복·시설장 동일
   */
  const basic = dbWageAmount(
    (row.basic ?? row.cwhd_basic) as number | null | undefined
  );
  const auto = dbWageAmount(
    (row.lunch ?? row.cwhd_lunch) as number | null | undefined
  );
  const director = dbWageAmount(
    (row.lunchDirector ?? row.cwhd_lunch_director) as number | null | undefined
  );
  if (basic <= 0) return 0;

  if (auto > 0 && director > 0) {
    const car = dbWageAmount((row.car ?? row.cwhd_car) as number | null | undefined);
    const child = dbWageAmount((row.child ?? row.cwhd_child) as number | null | undefined);
    const nonTax = dbWageAmount(
      (row.nonTax ?? row.cwhd_non_tax) as number | null | undefined
    );
    const doublePool = basic + auto + director;
    const fillDouble = resolveHomeWageStaffAutoLunchFill(director, doublePool);
    const nontaxWithoutAuto = director + car + child;
    const nontaxWithAuto = auto + director + car + child;
    if (
      Math.abs(auto - fillDouble) <= 1
      && Math.abs(nonTax - nontaxWithoutAuto) <= 1
      && Math.abs(nonTax - nontaxWithAuto) > 1
    ) {
      return doublePool;
    }
    const onlyAutoPool = basic + auto;
    const fillOnly = resolveHomeWageStaffAutoLunchFill(director, onlyAutoPool);
    if (Math.abs(auto - fillOnly) <= 1) {
      return onlyAutoPool;
    }
  }

  if (auto > 0) {
    return basic + auto;
  }

  return 0;
}

/**
 * 요양보호사 외 — 중식비(자동) 월 20만원 채우기
 * auto = min(max(0, 20만 − 센터장입력), pool)
 */
export function resolveHomeWageStaffAutoLunchFill(
  directorLunch: number | null | undefined,
  pool: number,
  monthlyLimit: number = HOME_WAGE_LUNCH_MONTHLY_LIMIT
): number {
  const director = clampHomeWageLunchDirectorInput(directorLunch);
  const p = Math.max(0, Math.round(Number(pool) || 0));
  const limit = Math.max(0, Math.round(Number(monthlyLimit) || 0));
  return Math.min(Math.max(0, limit - director), p);
}

/** 근무달력과 동일 — 12:00~13:00, 종료 12:00 이상, 중증 제외 */
export function calcHomeWageCalendarLunchWorkDayCountFromClaims(
  rows: HomeWageClaimRow[]
): number {
  const dates = new Set<string>();
  for (const row of filterNonSevereClaimRows(rows || [])) {
    const start = parseClaimTimeToMinutes(row.plan_start_time);
    const end = parseClaimTimeToMinutes(row.plan_end_time);
    if (start == null || end == null) continue;
    if (!(start < 13 * 60 && end >= 12 * 60)) continue;
    const dateKey = String(row.date || '').trim().split(/[T\s]/)[0];
    if (dateKey) dates.add(dateKey);
  }
  return dates.size;
}

export interface HomeWageDirectorAdminAutoLunchInput {
  job: string;
  svc?: string | null;
  lunchExpense: boolean;
  ctAnnual: number;
  ctBasic: number;
  ctWeekend?: number;
  ctLunch: number;
  payCalcHourly: number;
  hour: number | string | null | undefined;
  days: number;
  yearMonth: string;
  joinDate?: string | null;
  claimRows?: HomeWageClaimRow[];
  lunchWorkDayCount?: number;
}

/**
 * 관리자 자동중식 — 센터장 칸은 그대로 두고 관리자 칸에서만 쓸 금액
 * 합계 불변: 일반요보=연차에서, 가족·시설사복=기본급에서 같은 금액을 뺀다
 */
export function calcHomeWageDirectorAdminAutoLunch(
  input: HomeWageDirectorAdminAutoLunchInput
): number {
  if (!input.lunchExpense) return 0;

  const job = String(input.job || '');
  const ctBasic = Math.max(0, Math.round(Number(input.ctBasic) || 0));
  const ctAnnual = Math.max(0, Math.round(Number(input.ctAnnual) || 0));
  const ctLunch = clampHomeWageLunchDirectorInput(input.ctLunch);

  if (!isHomeWageCaregiverJob(job)) {
    const hourly = dbWageAmount(input.payCalcHourly);
    const hours = parseHomeWageRowWorkHours(input.hour);
    const pool = hourly > 0 && hours > 0 ? Math.floor(hourly * hours) : ctBasic;
    return Math.min(resolveHomeWageStaffAutoLunchFill(ctLunch, pool), ctBasic);
  }

  const family =
    isHomeWageRowAllFamilyCareFromSvc(input.svc)
    || inferHomeWageFamilyCareOnlyFromPayRow({
      annual: ctAnnual,
      weekend: input.ctWeekend,
      basic: ctBasic,
    });
  const summary = parseHomeWageRecipientSummaryFromSvc(input.svc);
  const recipient = calcHomeWageNonSevereRecipientTotalFromSummaryRows(summary);
  const lunchDays =
    input.lunchWorkDayCount != null
      ? Number(input.lunchWorkDayCount) || 0
      : calcHomeWageCalendarLunchWorkDayCountFromClaims(input.claimRows || []);
  const ym = String(input.yearMonth || '').replace(/\D/g, '').slice(0, 6);
  const year = Number(ym.slice(0, 4)) || 0;
  const month = Number(ym.slice(4, 6)) || 0;
  const hours =
    calcTotalSugaHoursFromSummary(summary) || parseHomeWageRowWorkHours(input.hour);
  const linked = calcHomeWageHourlyLinkedPay({
    payCalcHourlyWage: dbWageAmount(input.payCalcHourly),
    totalSugaHours: hours,
    workDays: Number(input.days) || 0,
    annualLeaveDays: getAnnualLeaveDays(input.joinDate, year, month),
  });
  const expected = calcHomeWageAutoLunch({
    lunchExpense: true,
    isFamilyCareOnly: family,
    nonSevereTarget: recipient,
    lunchWorkDayCount: lunchDays,
    annualLeaveBeforeLunch: family ? 0 : ctAnnual,
    linkedMinAnnual: linked.annual,
  }).autoLunch;
  const capped = resolveHomeWageAdjustedAutoLunch(expected, ctLunch);
  return Math.min(capped, family ? ctBasic : ctAnnual);
}

/**
 * 요양보호사 외 — 중식 필드 정규화
 * ★ 레거시(5월 시설장·사복): 기본급≈풀 + 센터장중식=0 + 자동칸>0
 *   → 자동칸 전액을 센터장 중식(추가지급)으로 본다 (6월 lunch_director와 동일)
 * ★ 정상: 센터장중식 있으면 자동 = 20만 채우기 잔여(풀 안)
 */
export function resolveHomeWageStaffEffectiveLunch(row: Record<string, unknown>): {
  lunch: number;
  lunchDirector: number;
  legacyAutoAsDirector: boolean;
} {
  const auto = dbWageAmount(
    (row.lunch ?? row.cwhd_lunch) as number | null | undefined
  );
  const director = dbWageAmount(
    (row.lunchDirector ?? row.cwhd_lunch_director) as number | null | undefined
  );
  const basic = dbWageAmount(
    (row.basic ?? row.cwhd_basic) as number | null | undefined
  );
  const pool = resolveHomeWageStaffHourPool(row);
  if (
    pool > 0
    && director <= 0
    && auto > 0
    && Math.abs(basic - pool) <= 1
  ) {
    return {
      lunch: 0,
      lunchDirector: clampHomeWageLunchDirectorInput(auto),
      legacyAutoAsDirector: true,
    };
  }
  return { lunch: auto, lunchDirector: director, legacyAutoAsDirector: false };
}

/**
 * 요양보호사 외 — 시급×시간 풀을 기본급 + 자동중식(20만 채우기)으로 분리
 */
export function splitHomeWageStaffPoolByDirectorLunch(
  pool: number,
  directorLunch: number | null | undefined
): { pool: number; basic: number; lunch: number; lunchDirector: number } {
  const p = Math.max(0, Math.round(Number(pool) || 0));
  const lunchDirector = clampHomeWageLunchDirectorInput(directorLunch);
  const lunch = resolveHomeWageStaffAutoLunchFill(lunchDirector, p);
  return { pool: p, basic: Math.max(0, p - lunch), lunch, lunchDirector };
}

/**
 * @deprecated splitHomeWageStaffPoolByDirectorLunch 사용 (20만 채우기)
 * 사복·운전원 — 시급×시간 풀에서 자동중식 분리
 */
export function splitHomeWageStaffPoolAndAutoLunch(
  pool: number,
  autoLunch: number
): { pool: number; basic: number; lunch: number } {
  const p = Math.max(0, Math.round(Number(pool) || 0));
  const lunch = Math.min(
    Math.max(0, Math.round(Number(autoLunch) || 0)),
    p,
    HOME_WAGE_LUNCH_MONTHLY_LIMIT
  );
  return { pool: p, lunch, basic: Math.max(0, p - lunch) };
}

/**
 * 자동중식 중 「연차·기본급에서 빠지지 않고」 올라간 초과분
 * — 요보: (기본+주휴+순수연차+자동중식) − 수급자(비중증)합, 없으면 미추출 중식 전액
 * — 요양보호사 외: 시급×시간(풀)과 비교
 *   · 레거시(자동칸=센터장중식): 0 (추가지급 유지 — 5월 시설장·사복)
 *   · basic+자동중식≈풀 → 0(정상: 20만 채우기 차감 저장)
 *   · basic≈풀 + 센터장중식>0 + 자동>0 → 자동분 초과(안태근, 이중계산 제거)
 * ★ 센터장 입력 중식(lunchDirector)은 풀 밖 추가지급 → 초과분 아님
 */
export function calcHomeWageUnfundedAutoLunch(row: Record<string, unknown>): number {
  const autoLunch = dbWageAmount(
    (row.lunch ?? row.cwhd_lunch) as number | null | undefined
  );
  if (autoLunch <= 0) return 0;

  const annualRaw = dbWageAmount(
    (row.annual ?? row.cwhd_annual) as number | null | undefined
  );
  const basic = dbWageAmount(
    (row.basic ?? row.cwhd_basic) as number | null | undefined
  );
  const weekend = dbWageAmount(
    (row.weekend ?? row.cwhd_weekend) as number | null | undefined
  );
  const job = String(row.job ?? row.cwhd_job ?? '');
  const svc = (row.svc ?? row.cwhd_svc) as string | null | undefined;
  const extractCtx = buildHomeWageLunchExtractContext(row);
  const pureAnnual = shouldExtractAutoLunchFromAnnualField(
    annualRaw,
    autoLunch,
    extractCtx
  )
    ? Math.max(0, annualRaw - autoLunch)
    : annualRaw;

  if (!isHomeWageCaregiverJob(job)) {
    // 5월형: 자동칸만 있는 중식 = 센터장 추가지급 → 깎지 않음
    if (resolveHomeWageStaffEffectiveLunch(row).legacyAutoAsDirector) return 0;

    const hourPool = resolveHomeWageStaffHourPool(row);
    if (hourPool > 0) {
      // 정상: 기본급 + 자동중식 = 시급×시간
      if (Math.abs(basic + autoLunch - hourPool) <= 1) return 0;
      // 미차감 + 센터장중식 있음(안태근): 자동분은 풀 안
      if (Math.abs(basic - hourPool) <= 1) return autoLunch;
      return Math.max(0, basic + autoLunch - hourPool);
    }
    return autoLunch;
  }

  const rt =
    dbWageAmount(row.recipientTotal as number | null | undefined)
    || calcHomeWageNonSevereRecipientTotalFromSvc(svc);
  const pool = basic + weekend + pureAnnual + autoLunch;
  if (rt > 0) return Math.max(0, pool - rt);

  // 수급자합 없으면 미추출 중식만 초과로 본다
  return shouldExtractAutoLunchFromAnnualField(annualRaw, autoLunch, extractCtx)
    ? 0
    : autoLunch;
}

/**
 * 자동중식 실지급 판별용 — 표시/차감 없이 DB·폼 항목 원합
 */
function sumHomeWageRawPayPartsForLunchProbe(row: Record<string, unknown>): number {
  const n = (...keys: string[]) => pickHomeWageRowNumber(row, ...keys);
  return (
    n('basic', 'cwhd_basic')
    + n('weekend', 'cwhd_weekend')
    + n('annual', 'cwhd_annual')
    + n('laborDay', 'cwhd_labor_day')
    + n('overtime', 'cwhd_overtime')
    + n('overtimeNontax', 'cwhd_overtime_nontax')
    + n('night', 'cwhd_night')
    + n('nightNontax', 'cwhd_night_nontax')
    + n('holiday', 'cwhd_holiday')
    + n('holidayNontax', 'cwhd_holiday_nontax')
    + n('senior', 'cwhd_senior')
    + n('sunim', 'cwhd_sunim')
    + n('bosu', 'cwhd_bosu')
    + n('severe', 'cwhd_severe')
    + n('bonus', 'cwhd_bonus')
    + n('lunch', 'cwhd_lunch')
    + n('lunchDirector', 'cwhd_lunch_director')
    + n('car', 'cwhd_car')
    + n('child', 'cwhd_child')
    + n('welfare', 'cwhd_welfare')
    + n('other', 'cwhd_other')
  );
}

/**
 * 요보 자동중식 중 실지급분
 * — 목록 월급여(gross=wageTotal+nonTax)와 항목합을 대조
 * — 중식을 빼야 gross에 더 가까우면 orphan(김순희 6월), 넣어야 가까우면 실지급(김자현 5월)
 */
export function resolveHomeWagePayableAutoLunch(row: Record<string, unknown>): number {
  const autoLunch = dbWageAmount(
    (row.lunch ?? row.cwhd_lunch) as number | null | undefined
  );
  if (autoLunch <= 0) return 0;

  const listGross =
    dbWageAmount(row.grossPay as number | null | undefined)
    || (
      pickHomeWageRowNumber(row, 'wageTotal', 'cwhd_wage_total')
      + pickHomeWageRowNumber(row, 'nonTax', 'cwhd_non_tax')
    );

  if (listGross <= 0) {
    // 저장 총액 없으면 수급자합 휴리스틱
    return Math.max(0, autoLunch - calcHomeWageUnfundedAutoLunch(row));
  }

  const partsWith = sumHomeWageRawPayPartsForLunchProbe(row);
  const partsWithout = partsWith - autoLunch;
  const errWith = Math.abs(listGross - partsWith);
  const errWithout = Math.abs(listGross - partsWithout);
  if (errWithout + 1 < errWith) return 0;
  return autoLunch;
}

/**
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * 중식비 함수 역할 분리 (★ 꼬임 방지 — .cursor/rules/home-wage-setting-policy.mdc 동기)
 *
 * | 함수 | 용도 |
 * | resolveHomeWageFundedAutoLunch | 실지급·폼·비과세 (흡수 고스트=0) |
 * | isHomeWageAutoLunchAbsorbedInAnnual | 연차에만 있는 cwhd_lunch 고스트 판별 |
 * | getHomeWageAdminSettingLunchAuto | 관리자 폼·회계대장 중식(자동) — 흡수 고스트=0 |
 * | getHomeWageAdminSettingAnnual | 관리자 폼·회계대장 연차 — 추출 시에만 분리 |
 * | getHomeWageAccountingDisplayAutoLunch | 레거시 신고용 중식(자동분) — 흡수분 포함 |
 * | getHomeWageAccountingDisplayAnnual | 레거시 신고용 연차 — 흡수·추출 시 중식 차감 |
 * | calcHomeWageCombinedLunch | 자동+센터장 ≤20만 (기관용 합산 참고) |
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

/**
 * 지급·연차합산·관리자 폼에 넣을 자동중식 (실지급분만)
 * — 요보 일반: 목록 gross 대조. 연차 흡수 고스트는 0
 * — 요보 가족요양: 전액
 * — 사복·시설장·운전원: 레거시 승격분은 0(센터장칸), 그 외 전액
 */
export function resolveHomeWageFundedAutoLunch(row: Record<string, unknown>): number {
  const autoLunch = dbWageAmount(
    (row.lunch ?? row.cwhd_lunch) as number | null | undefined
  );
  if (autoLunch <= 0) return 0;
  const job = String(row.job ?? row.cwhd_job ?? '');
  if (!isHomeWageCaregiverJob(job)) {
    // 레거시: 자동칸 → 센터장중식 승격 → 회계 자동칸 0
    if (resolveHomeWageStaffEffectiveLunch(row).legacyAutoAsDirector) return 0;
    return autoLunch;
  }

  const svc = (row.svc ?? row.cwhd_svc) as string | null | undefined;
  const isFamilyOnly =
    row.forceFamilyCareOnly === true
    || isHomeWageRowAllFamilyCareFromSvc(svc)
    || (!svc && inferHomeWageFamilyCareOnlyFromPayRow(row as HomeWageClientPayDisplayRow));
  // 가족요양: non_tax=0 이어도 중식은 실지급 → 회계 중식 칸에 표시
  if (isFamilyOnly) return autoLunch;

  const annualRaw = dbWageAmount(
    (row.annual ?? row.cwhd_annual) as number | null | undefined
  );
  const extractCtx = buildHomeWageLunchExtractContext(row);
  // 연차에 중식이 합산 저장된 레거시 → 분리 후 다시 합산해야 하므로 전액
  if (shouldExtractAutoLunchFromAnnualField(annualRaw, autoLunch, extractCtx)) {
    return autoLunch;
  }
  return resolveHomeWagePayableAutoLunch(row);
}

/**
 * 자동중식이 연차 칸에만 흡수된 상태 (cwhd_lunch 고스트)
 * — 6월 고영이·박소라형: payable=0 + 연차>중식 · 금액은 연차·총지급에 이미 포함
 * — 5월 최점숙·김영남형 분리 저장은 제외
 * ★ 신고용: getHomeWageAccountingDisplay* 가 중식/연차 칸으로 분리
 * ★ 관리자 폼·funded: 0 처리 (비과세에 고스트 금지)
 */
export function isHomeWageAutoLunchAbsorbedInAnnual(
  row: Record<string, unknown>
): boolean {
  const lunch = dbWageAmount(
    (row.lunch ?? row.cwhd_lunch) as number | null | undefined
  );
  const annual = dbWageAmount(
    (row.annual ?? row.cwhd_annual) as number | null | undefined
  );
  if (lunch <= 0 || annual <= lunch) return false;
  const job = String(row.job ?? row.cwhd_job ?? '');
  if (!isHomeWageCaregiverJob(job)) return false;
  // 이미 실지급·추출 대상이면 흡수 아님
  if (resolveHomeWagePayableAutoLunch(row) > 0) return false;
  const extractCtx = buildHomeWageLunchExtractContext(row);
  if (shouldExtractAutoLunchFromAnnualField(annual, lunch, extractCtx)) {
    return false;
  }
  const basic = dbWageAmount(extractCtx.basic);
  const weekend = dbWageAmount(extractCtx.weekend);
  const rt =
    dbWageAmount(extractCtx.recipientTotal)
    || (extractCtx.svc
      ? calcHomeWageNonSevereRecipientTotalFromSvc(extractCtx.svc)
      : 0);
  if (rt > 0) {
    const errSeparateLunch = Math.abs(basic + weekend + annual + lunch - rt);
    const errAnnualOnly = Math.abs(basic + weekend + annual - rt);
    // 분리 저장(중식을 연차 밖에 더하는 편이 수급자합에 더 가까움)
    if (errSeparateLunch < errAnnualOnly) return false;
  }
  return true;
}

/**
 * 레거시 신고용 — 중식비(자동) 칸 (흡수분 포함)
 * ★ 회계처리용 대장은 getHomeWageAdminSettingLunchAuto 사용 (여기 쓰지 말 것)
 */
export function getHomeWageAccountingDisplayAutoLunch(
  row: Record<string, unknown>
): number {
  const lunch = dbWageAmount(
    (row.lunch ?? row.cwhd_lunch) as number | null | undefined
  );
  const job = String(row.job ?? row.cwhd_job ?? '');
  // 요양보호사 외 — DisplayBasic이 깎은 20만 채우기와 동일 금액 (DB lunch=0·센터장중식만 있어도)
  if (!isHomeWageCaregiverJob(job)) {
    return resolveHomeWageStaffAccountingAutoLunch(row);
  }
  if (lunch <= 0) return 0;
  const annual = dbWageAmount(
    (row.annual ?? row.cwhd_annual) as number | null | undefined
  );
  const extractCtx = buildHomeWageLunchExtractContext(row);
  if (
    shouldExtractAutoLunchFromAnnualField(annual, lunch, extractCtx)
    || isHomeWageAutoLunchAbsorbedInAnnual(row)
  ) {
    return lunch;
  }
  return resolveHomeWageFundedAutoLunch(row);
}

/**
 * 레거시 신고용 — 연차수당 칸 (흡수 분리 + 한도 복원)
 * ★ 회계처리용 대장은 getHomeWageAdminSettingAnnual 사용 (여기 쓰지 말 것)
 */
export function getHomeWageAccountingDisplayAnnual(
  row: Record<string, unknown>
): number {
  const annual = dbWageAmount(
    (row.annual ?? row.cwhd_annual) as number | null | undefined
  );
  const lunch = dbWageAmount(
    (row.lunch ?? row.cwhd_lunch) as number | null | undefined
  );
  const extractCtx = buildHomeWageLunchExtractContext(row);
  const split =
    shouldExtractAutoLunchFromAnnualField(annual, lunch, extractCtx)
    || isHomeWageAutoLunchAbsorbedInAnnual(row);
  const adminAnnual = split ? Math.max(0, annual - lunch) : annual;
  const restore = resolveHomeWageLunchDirectorCapAnnualRestore({
    recipientTotal: extractCtx.recipientTotal,
    svc: extractCtx.svc,
    basic: extractCtx.basic,
    weekend: extractCtx.weekend,
    annual: adminAnnual,
    lunchDirector: (row.lunchDirector ?? row.cwhd_lunch_director) as
      | number
      | null
      | undefined,
  });
  return adminAnnual + restore;
}

/**
 * 요양보호사 외 — 관리자·회계 표시용 자동중식(20만 채우기)
 * ★ getHomeWageAccountingDisplayBasic 과 동일 산식 (칸 합 = 합계)
 * ★ 센터장 slip/client 경로는 사용하지 않음
 */
export function resolveHomeWageStaffAccountingAutoLunch(
  row: Record<string, unknown>
): number {
  const effLunch = resolveHomeWageStaffEffectiveLunch(row);
  if (effLunch.legacyAutoAsDirector) return 0;
  const hourPool = resolveHomeWageStaffHourPool(row);
  const storedAuto = dbWageAmount(
    (row.lunch ?? row.cwhd_lunch) as number | null | undefined
  );
  // DisplayBasic과 동일: 자동 또는 센터장중식이 있을 때만 20만 채우기
  if (hourPool > 0 && (storedAuto > 0 || effLunch.lunchDirector > 0)) {
    return resolveHomeWageStaffAutoLunchFill(effLunch.lunchDirector, hourPool);
  }
  return storedAuto;
}

/**
 * 회계·관리자 표시 기본급 (요양보호사 외)
 * — DB에 중식(자동/센터장)이 있을 때만 시급×시간 − 자동중식(20만 채우기)
 * — 중식 없으면 기본급 = 시급×시간 (없는 20만을 깎지 않음)
 */
export function getHomeWageAccountingDisplayBasic(row: Record<string, unknown>): number {
  const basic = dbWageAmount((row.basic ?? row.cwhd_basic) as number | null | undefined);
  const job = String(row.job ?? row.cwhd_job ?? '');
  if (isHomeWageCaregiverJob(job)) return basic;
  const hourPool = resolveHomeWageStaffHourPool(row);
  const effLunch = resolveHomeWageStaffEffectiveLunch(row);
  // 레거시(자동→센터장 승격): 기본급=풀, 중식은 센터장칸
  if (effLunch.legacyAutoAsDirector) {
    return hourPool > 0 ? hourPool : basic;
  }
  const autoFill = resolveHomeWageStaffAccountingAutoLunch(row);
  // 중식 실데이터가 있을 때만 20만 채우기 차감
  if (hourPool > 0 && autoFill > 0) {
    return Math.max(0, hourPool - autoFill);
  }
  if (hourPool > 0) return hourPool;
  return Math.max(0, basic - calcHomeWageUnfundedAutoLunch(row));
}

/**
 * 요양보호사 외 지급금액 (시설장·사복·운전원 동일)
 * — 총지급 = 시급×시간(풀) + 센터장중식 + 차량·자녀 등 + (wageTotal−basic 과세 가산)
 * — 미차감·이중차감·draft(hour 누락)여도 위 정책 합계를 우선
 */
export function getHomeWageStaffLedgerGrossPay(row: Record<string, unknown>): number {
  if (hasHomeWageStoredCtParts(row)) {
    return sumHomeWageStoredCtPayParts(
      readHomeWageStoredCtParts(
        row,
        String(row.yearMonth ?? row.cwhdir_ym ?? row.cwhd_ym ?? '')
      )
    );
  }
  const wageTotal = pickHomeWageRowNumber(row, 'wageTotal', 'cwhd_wage_total');
  const nonTax = pickHomeWageRowNumber(row, 'nonTax', 'cwhd_non_tax');
  const storedGross =
    dbWageAmount(row.grossPay as number | null | undefined)
    || (wageTotal + nonTax);
  const job = String(row.job ?? row.cwhd_job ?? '');
  if (isHomeWageCaregiverJob(job)) return storedGross;
  const unfunded = calcHomeWageUnfundedAutoLunch({
    ...row,
    wageTotal,
    nonTax,
    lunch: row.lunch ?? row.cwhd_lunch,
    basic: row.basic ?? row.cwhd_basic,
    job,
  });
  const adjusted =
    wageTotal > 0 || nonTax > 0
      ? Math.max(0, wageTotal - unfunded) + nonTax
      : Math.max(0, storedGross - unfunded);

  const ym = String(row.yearMonth ?? row.cwhd_ym ?? '').replace(/\D/g, '').slice(0, 6);
  const policyGross = calcHomeWageClientReportGrossPay(
    buildHomeWageClientReportRowFromDb(row),
    ym
  );

  const hourPool = resolveHomeWageStaffHourPool(row);
  const effLunch = resolveHomeWageStaffEffectiveLunch(row);
  const basic = pickHomeWageRowNumber(row, 'basic', 'cwhd_basic');
  const car = pickHomeWageRowNumber(row, 'car', 'cwhd_car');
  const child = pickHomeWageRowNumber(row, 'child', 'cwhd_child');
  const taxExtras = Math.max(0, wageTotal - basic);
  const poolGross =
    hourPool > 0
      ? hourPool + effLunch.lunchDirector + car + child + taxExtras
      : 0;

  return Math.max(adjusted, policyGross, poolGross);
}

/**
 * 센터장 중식 때문에 자동중식이 월 20만 합산 한도에 깎인 분 → 연차 복원
 * restore = max(0, (수급자합 − 기본 − 주휴 − 관리자연차) + 센터장중식 − 20만)
 *
 * 염은경 7월: pool 70,000 + 센터장 144,000 − 200,000 = 14,000
 * 김영남 7월(수정 후): pool 52,000 + 148,000 − 200,000 = 0 (이중 복원 없음)
 * 고영이형 흡수: lunchDirector=0 → 0 (구멍을 연차로 메우지 않음)
 * ★ 센터장 중식비 자체는 연차에 더하지 않음
 */
export function resolveHomeWageLunchDirectorCapAnnualRestore(input: {
  recipientTotal?: number | null;
  svc?: string | null;
  basic?: number | null;
  weekend?: number | null;
  annual?: number | null;
  lunchDirector?: number | null;
}): number {
  const lunchDirector = clampHomeWageLunchDirectorInput(input.lunchDirector);
  if (lunchDirector <= 0) return 0;
  const claim =
    dbWageAmount(input.recipientTotal)
    || calcHomeWageNonSevereRecipientTotalFromSvc(input.svc);
  if (claim <= 0) return 0;
  const pool =
    claim
    - dbWageAmount(input.basic)
    - dbWageAmount(input.weekend)
    - dbWageAmount(input.annual);
  if (pool <= 0) return 0;
  return Math.max(
    0,
    Math.round(pool + lunchDirector - HOME_WAGE_LUNCH_MONTHLY_LIMIT)
  );
}

/**
 * 센터장 UI — 연차 = 관리자 연차 + 자동중식 + (20만 한도 축소분 복원)
 * ★ 센터장 입력 중식비(lunchDirector)는 더하지 않음
 */
export function calcHomeWageDirectorCaregiverAnnualDisplay(
  adminAnnual: number | null | undefined,
  autoLunch: number | null | undefined,
  capInput?: {
    recipientTotal?: number;
    basic?: number;
    weekend?: number;
    lunchDirector?: number;
    svc?: string | null;
  }
): number {
  const folded = dbWageAmount(adminAnnual) + dbWageAmount(autoLunch);
  if (!capInput) return folded;
  return (
    folded
    + resolveHomeWageLunchDirectorCapAnnualRestore({
    ...capInput,
      annual: adminAnnual,
    })
  );
}

/** slip·명세서 — 센터장 연차는 저장/입력값 그대로 (자동중식 합산 없음) */
function resolveHomeWageDirectorSlipAnnualDisplay(
  _row: HomeWageClientPayDisplayRow,
  _basic: number,
  _weekend: number,
  adminAnnual: number,
  _lunchAuto: number
): number {
  return dbWageAmount(adminAnnual);
}

/** @deprecated getHomeWageCaregiverSlipPayDisplay(row).annual 사용 */
export function getHomeWageDisplayAnnualWithLunch(row: HomeWageDisplayAnnualLunchRow): number {
  return getHomeWageCaregiverSlipPayDisplay(row).annual;
}

/** 고객용 임금대장·명세서(센터장) — 표시 항목 합계 (= 지급합계, DB grossPay와 분리) */
export type HomeWageClientReportRow = HomeWageClientPayDisplayRow & {
  senior?: number | null;
  sunim?: number | null;
  bosu?: number | null;
  severe?: number | null;
  bonus?: number | null;
  car?: number | null;
  child?: number | null;
  welfare?: number | null;
  other?: number | null;
  laborDay?: number | null;
  _patientWageInfo?: Array<{ isSevere?: boolean; amount?: number }>;
};

/** 중증수급자가산 표시금액 — DB(cwhd_severe) → patient → cwhd_svc 순 (0이면 다음 소스) */
export function resolveHomeWageSevereDisplayAmount(
  row: HomeWageClientReportRow & {
    svc?: string | null;
    cwhd_svc?: string | null;
    _patientWageInfo?: Array<{ isSevere?: boolean; amount?: number }>;
  },
  patientRows?: Array<{ isSevere?: boolean; amount?: number }>
): number {
  const rec = row as Record<string, unknown>;
  if (hasHomeWageStoredCtParts(rec)) {
    return pickHomeWageRowNumber(rec, 'ctSevere', 'cwhdir_ct_severe', 'severe');
  }
  if (isHomeWageDirectorCanonicalRow(rec)) {
    return dbWageAmount(row.severe);
  }
  const fromDb = dbWageAmount(row.severe);
  if (fromDb > 0) return fromDb;

  const rows = patientRows ?? row._patientWageInfo;
  if (rows?.length) {
    let sum = 0;
    for (const p of rows) {
      if (!p.isSevere) continue;
      sum += dbWageAmount(p.amount);
    }
    if (sum > 0) return sum;
  }

  const svc = row.svc ?? row.cwhd_svc;
  const summary = parseHomeWageRecipientSummaryFromSvc(svc);
  if (summary.length) {
    const fromSvc = summary
      .filter((r) => r.isSevereRecipientSurcharge)
      .reduce((s, r) => s + dbWageAmount(r.totalAmount), 0);
    if (fromSvc > 0) return fromSvc;
  }

  return 0;
}

export function calcHomeWageClientReportGrossPay(
  row: HomeWageClientReportRow,
  yearMonth: string
): number {
  const rec = row as Record<string, unknown>;
  if (hasHomeWageStoredCtParts(rec)) {
    return sumHomeWageStoredCtPayParts(readHomeWageStoredCtParts(rec, yearMonth));
  }
  const slip = getHomeWageCaregiverSlipPayDisplay(row);
  /** 센터장: 자동 식대는 slip.annual에 포함 → 중식비는 센터장 입력(slip.lunch)만 합산 */
  const lunchForGross = slip.lunch;
  const laborDay = isHomeWageLaborDayMonth(yearMonth) ? dbWageAmount(row.laborDay) : 0;
  return (
    slip.basic
    + slip.weekend
    + slip.annual
    + slip.overtime
    + slip.night
    + slip.holiday
    + lunchForGross
    + laborDay
    + dbWageAmount(row.senior)
    + dbWageAmount(row.sunim)
    + dbWageAmount(row.bosu)
    + resolveHomeWageSevereDisplayAmount(row)
    + dbWageAmount(row.bonus)
    + dbWageAmount(row.car)
    + dbWageAmount(row.child)
    + dbWageAmount(row.welfare)
    + dbWageAmount(row.other)
  );
}

/**
 * 요양보호사 — 관리자·센터장 시점 전환과 무관한 slip·지급금액 입력
 * (연차=관리자 순수+자동중식 slip 합산, 연·야·휴=DB 전액, skipPremiumAnnualAbsorb)
 */
export function buildHomeWageCaregiverUnifiedGrossReportRow(input: {
  form: Pick<
    HomeWagePayrollFormData,
    | 'basic'
    | 'weekend'
    | 'annual'
    | 'lunchDirector'
    | 'overtime'
    | 'overtimeNontax'
    | 'night'
    | 'nightNontax'
    | 'holiday'
    | 'holidayNontax'
    | 'overtimeHours'
    | 'nightHours'
    | 'holidayHours'
    | 'senior'
    | 'sunim'
    | 'bosu'
    | 'severe'
    | 'bonus'
    | 'car'
    | 'child'
    | 'welfare'
    | 'other'
    | 'laborDay'
    | 'wageTotal'
    | 'nonTax'
    | 'grossPay'
  >;
  job: string;
  hour: number;
  autoLunch: number;
  /** shadow·DB 역산 관리자 순수 연차 (없으면 form.annual에서 분리) */
  adminAnnual?: number;
  svc?: string | null;
  recipientTotal?: number;
  forceFamilyCareOnly?: boolean;
  payOvertime?: boolean | null;
  payHoliday?: boolean | null;
  payNight?: boolean | null;
}): HomeWageClientReportRow {
  const isCg = isHomeWageCaregiverJob(input.job);
  const generalCg = isCg && !input.forceFamilyCareOnly;
  const autoLunch = Math.max(0, dbWageAmount(input.autoLunch));
  const extractCtx: HomeWageLunchExtractContext = {
    basic: input.form.basic,
    weekend: input.form.weekend,
    recipientTotal: input.recipientTotal,
    svc: input.svc,
  };
  const adminAnnual =
    input.adminAnnual != null
      ? dbWageAmount(input.adminAnnual)
      : resolveHomeWageAdminAnnualForSlip(input.form.annual, autoLunch, extractCtx);

  return {
    basic: dbWageAmount(input.form.basic),
    weekend: dbWageAmount(input.form.weekend),
    annual: generalCg ? adminAnnual : dbWageAmount(input.form.annual),
    lunch: autoLunch,
    lunchDirector: dbWageAmount(input.form.lunchDirector),
    overtime: dbWageAmount(input.form.overtime),
    overtimeNontax: dbWageAmount(input.form.overtimeNontax),
    night: dbWageAmount(input.form.night),
    nightNontax: dbWageAmount(input.form.nightNontax),
    holiday: dbWageAmount(input.form.holiday),
    holidayNontax: dbWageAmount(input.form.holidayNontax),
    overtimeHours: input.form.overtimeHours,
    nightHours: input.form.nightHours,
    holidayHours: input.form.holidayHours,
    hour: input.hour,
    job: input.job,
    svc: input.svc ?? null,
    forceFamilyCareOnly: input.forceFamilyCareOnly,
    payOvertime: input.payOvertime,
    payHoliday: input.payHoliday,
    payNight: input.payNight,
    skipPremiumAnnualAbsorb: generalCg,
    recipientTotal: generalCg ? input.recipientTotal : undefined,
    wageTotal: dbWageAmount(input.form.wageTotal),
    nonTax: dbWageAmount(input.form.nonTax),
    grossPay: dbWageAmount(input.form.grossPay),
    senior: input.form.senior,
    sunim: input.form.sunim,
    bosu: input.form.bosu,
    severe: input.form.severe,
    bonus: input.form.bonus,
    car: input.form.car,
    child: input.form.child,
    welfare: input.form.welfare,
    other: input.form.other,
    laborDay: input.form.laborDay,
  };
}

/** 요양보호사 UI·명세서 — 관리자·센터장 동일 지급금액 (= slip·기본급+수당 합) */
export function calcHomeWageCaregiverUnifiedDisplayGrossPay(
  reportRow: HomeWageClientReportRow,
  yearMonth: string
): number {
  return calcHomeWageClientReportGrossPay(reportRow, yearMonth);
}

/** DB 저장 행 — 통합 slip 지급금액 (목록·검증용) */
export function calcHomeWageStoredUnifiedGrossPay(
  row: Record<string, unknown>,
  yearMonth: string
): number {
  const job = String(row.job ?? row.cwhd_job ?? '');
  if (!isHomeWageCaregiverJob(job)) return 0;

  const autoLunch = getHomeWageAdminSettingLunchAuto(row);
  const reportRow = buildHomeWageCaregiverUnifiedGrossReportRow({
    form: {
      basic: pickHomeWageRowNumber(row, 'basic', 'cwhd_basic'),
      weekend: pickHomeWageRowNumber(row, 'weekend', 'cwhd_weekend'),
      annual: pickHomeWageRowNumber(row, 'annual', 'cwhd_annual'),
      lunchDirector: pickHomeWageRowNumber(row, 'lunchDirector', 'cwhd_lunch_director'),
      overtime: pickHomeWageRowNumber(row, 'overtime', 'cwhd_overtime'),
      overtimeNontax: pickHomeWageRowNumber(row, 'overtimeNontax', 'cwhd_overtime_nontax'),
      night: pickHomeWageRowNumber(row, 'night', 'cwhd_night'),
      nightNontax: pickHomeWageRowNumber(row, 'nightNontax', 'cwhd_night_nontax'),
      holiday: pickHomeWageRowNumber(row, 'holiday', 'cwhd_holiday'),
      holidayNontax: pickHomeWageRowNumber(row, 'holidayNontax', 'cwhd_holiday_nontax'),
      overtimeHours: pickHomeWageRowNumber(row, 'overtimeHours', 'cwhd_overtime_hours'),
      nightHours: pickHomeWageRowNumber(row, 'nightHours', 'cwhd_night_hours'),
      holidayHours: pickHomeWageRowNumber(row, 'holidayHours', 'cwhd_holiday_hours'),
      senior: pickHomeWageRowNumber(row, 'senior', 'cwhd_senior'),
      sunim: pickHomeWageRowNumber(row, 'sunim', 'cwhd_sunim'),
      bosu: pickHomeWageRowNumber(row, 'bosu', 'cwhd_bosu'),
      severe: pickHomeWageRowNumber(row, 'severe', 'cwhd_severe'),
      bonus: pickHomeWageRowNumber(row, 'bonus', 'cwhd_bonus'),
      car: pickHomeWageRowNumber(row, 'car', 'cwhd_car'),
      child: pickHomeWageRowNumber(row, 'child', 'cwhd_child'),
      welfare: pickHomeWageRowNumber(row, 'welfare', 'cwhd_welfare'),
      other: pickHomeWageRowNumber(row, 'other', 'cwhd_other'),
      laborDay: pickHomeWageRowNumber(row, 'laborDay', 'cwhd_labor_day'),
    },
    job,
    hour: parseHomeWageRowWorkHours(row.hour ?? row.cwhd_hour),
    autoLunch,
    svc: String(row.svc ?? row.cwhd_svc ?? '') || null,
    forceFamilyCareOnly: isHomeWageRowAllFamilyCareFromSvc(String(row.svc ?? row.cwhd_svc ?? '')),
    payOvertime: parseHomeWageDirectorPremiumPayFlag(row.payOvertime ?? row.cwhd_pay_overtime),
    payHoliday: parseHomeWageDirectorPremiumPayFlag(row.payHoliday ?? row.cwhd_pay_holiday),
    payNight: parseHomeWageDirectorPremiumPayFlag(row.payNight ?? row.cwhd_pay_night),
  });
  return calcHomeWageCaregiverUnifiedDisplayGrossPay(reportRow, yearMonth);
}

/** slip payItems 합계 */
export function sumHomeWageSlipPayItems(
  items: Array<{ amount?: number | null }>
): number {
  return items.reduce((sum, item) => sum + dbWageAmount(item.amount), 0);
}

/** 연·야·휴 수동조정 메모 접두어 (관리자 자동 산출값과 별도로 센터장 수동 입력값 보관) */
export const HOME_WAGE_PREMIUM_MANUAL_MEMO_PREFIX = '__PM__';

/** 센터장 캐논 재저장 완료 표시 (목록 빨간 이름 해제용) */
export const HOME_WAGE_DIRECTOR_RESAVED_MEMO_PREFIX = '__DR__';

export function isHomeWageDirectorResavedForMonth(
  row: Record<string, unknown>,
  yearMonth?: string | null
): boolean {
  const ym = String(yearMonth || '').replace(/\D/g, '').slice(0, 6);
  if (!ym) return false;
  const memo = String(row.memo ?? row.cwhd_memo ?? '');
  const line = memo.split('\n').find((l) => l.startsWith(HOME_WAGE_DIRECTOR_RESAVED_MEMO_PREFIX));
  if (!line) return false;
  const savedYm = line
    .slice(HOME_WAGE_DIRECTOR_RESAVED_MEMO_PREFIX.length)
    .trim()
    .replace(/\D/g, '')
    .slice(0, 6);
  return savedYm.length === 6 && savedYm >= ym;
}

export function encodeHomeWageDirectorResavedMemo(
  memo: string | null | undefined,
  yearMonth: string
): string {
  const ym = String(yearMonth).replace(/\D/g, '').slice(0, 6);
  const clean = String(memo || '').replace(/^__DR__[^\n]*\n?/, '').trim();
  return `${HOME_WAGE_DIRECTOR_RESAVED_MEMO_PREFIX}${ym}${clean ? `\n${clean}` : ''}`;
}

export function stripHomeWageDirectorResavedMemo(memo: string | null | undefined): string {
  return String(memo || '').replace(/^__DR__[^\n]*\n?/, '').trim();
}

/** 연·야·휴 수동조정 메모(__PM__)에서 센터장 수동 입력값 복원 */
export function decodeHomeWagePremiumManualMemo(
  memo: string | null | undefined
): { overtime: number; night: number; holiday: number } | null {
  const line = String(memo || '')
    .split('\n')
    .find((l) => l.startsWith(HOME_WAGE_PREMIUM_MANUAL_MEMO_PREFIX));
  if (!line) return null;
  try {
    const parsed = JSON.parse(line.slice(HOME_WAGE_PREMIUM_MANUAL_MEMO_PREFIX.length)) as {
      o?: number;
      n?: number;
      h?: number;
    };
    return {
      overtime: Math.max(0, Number(parsed.o) || 0),
      night: Math.max(0, Number(parsed.n) || 0),
      holiday: Math.max(0, Number(parsed.h) || 0),
    };
  } catch {
    return null;
  }
}

/** 연·야·휴 수동조정 플래그 파싱 (cwhd_premium_manual) */
export function parseHomeWagePremiumManualFlag(value: unknown): boolean {
  return value === 'Y' || value === true;
}

/** 시점 불일치 판정 — 청구·수급자 집계 컨텍스트 (목록 갱신용) */
export interface HomeWageViewMismatchContext {
  minHourlyWage: number;
  summaryRows: HomeWageRecipientFeeSummary[];
  claimRows: HomeWageClaimRow[];
  yearMonth: string;
  joinDate?: string | null;
  workDayCount?: number;
  lunchWorkDayCount?: number;
}

/** DB/API 행 → 시점 불일치 판정용 정규화 */
export type HomeWageViewMismatchRow = HomeWageClientPayDisplayRow & {
  premiumManual?: string | boolean | null;
  memo?: string | null;
  payCalcHourly?: number | string | null;
};

const HOME_WAGE_VIEW_MISMATCH_AMOUNT_TOLERANCE = 100;

function pickWageRowField(row: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    if (row[key] != null && row[key] !== '') return row[key];
  }
  return null;
}

export function normalizeHomeWageViewMismatchRow(
  row: Record<string, unknown>
): HomeWageViewMismatchRow {
  const base: HomeWageViewMismatchRow = {
    basic: pickWageRowField(row, 'basic', 'cwhd_basic') as number | null,
    weekend: pickWageRowField(row, 'weekend', 'cwhd_weekend') as number | null,
    annual: pickWageRowField(row, 'annual', 'cwhd_annual') as number | null,
    lunch: pickWageRowField(row, 'lunch', 'cwhd_lunch') as number | null,
    lunchDirector: pickWageRowField(row, 'lunchDirector', 'cwhd_lunch_director') as number | null,
    overtime: pickWageRowField(row, 'overtime', 'cwhd_overtime') as number | null,
    overtimeNontax: pickWageRowField(row, 'overtimeNontax', 'cwhd_overtime_nontax') as number | null,
    night: pickWageRowField(row, 'night', 'cwhd_night') as number | null,
    nightNontax: pickWageRowField(row, 'nightNontax', 'cwhd_night_nontax') as number | null,
    holiday: pickWageRowField(row, 'holiday', 'cwhd_holiday') as number | null,
    holidayNontax: pickWageRowField(row, 'holidayNontax', 'cwhd_holiday_nontax') as number | null,
    overtimeHours: pickWageRowField(row, 'overtimeHours', 'cwhd_overtime_hours') as number | string | null,
    nightHours: pickWageRowField(row, 'nightHours', 'cwhd_night_hours') as number | string | null,
    holidayHours: pickWageRowField(row, 'holidayHours', 'cwhd_holiday_hours') as number | string | null,
    hour: pickWageRowField(row, 'hour', 'cwhd_hour') as number | string | null,
    svc: pickWageRowField(row, 'svc', 'cwhd_svc') as string | null,
    job: pickWageRowField(row, 'job', 'cwhd_job') as string | null,
    premiumManual: pickWageRowField(row, 'premiumManual', 'cwhd_premium_manual') as string | boolean | null,
    memo: pickWageRowField(row, 'memo', 'cwhd_memo') as string | null,
    payCalcHourly: pickWageRowField(row, 'payCalcHourly', 'cwhd_pay_calc_hourly') as number | string | null,
    payOvertime: pickWageRowField(row, 'payOvertime', 'cwhd_pay_overtime') as string | boolean | null,
    payHoliday: pickWageRowField(row, 'payHoliday', 'cwhd_pay_holiday') as string | boolean | null,
    payNight: pickWageRowField(row, 'payNight', 'cwhd_pay_night') as string | boolean | null,
    recipientTotal: pickWageRowField(row, 'recipientTotal') as number | null,
    wageTotal: pickWageRowField(row, 'wageTotal', 'cwhd_wage_total') as number | null,
    nonTax: pickWageRowField(row, 'nonTax', 'cwhd_non_tax') as number | null,
    grossPay: pickWageRowField(row, 'grossPay') as number | null,
    cwhd_wage_total: pickWageRowField(row, 'cwhd_wage_total', 'wageTotal') as number | null,
    cwhd_non_tax: pickWageRowField(row, 'cwhd_non_tax', 'nonTax') as number | null,
  };
  const isFamilyOnly =
    isHomeWageRowAllFamilyCareFromSvc(base.svc)
    || (!base.svc && inferHomeWageFamilyCareOnlyFromPayRow(base));
  base.skipPremiumAnnualAbsorb =
    isHomeWageCaregiverJob(base.job) && !isFamilyOnly;
  return base;
}

/**
 * DB/API 급여 row → 센터장·고객용 임금대장·명세서 공통 표시 row
 * (payOvertime·svc·자동 중식대 등 slip 규칙 입력 통일)
 */
export function buildHomeWageClientReportRowFromDb(
  row: Record<string, unknown>
): HomeWageClientReportRow {
  if (hasHomeWageStoredCtParts(row) || isHomeWageDirectorCanonicalRow(row)) {
    const ym = String(row.yearMonth ?? row.cwhdir_ym ?? row.cwhd_ym ?? '')
      .replace(/\D/g, '')
      .slice(0, 6);
    const ct = hasHomeWageStoredCtParts(row)
      ? readHomeWageStoredCtParts(row, ym)
      : null;
    const store = ct ? null : mapHomeWageRowToDirectorStore(row, ym);
    const basic = ct?.basic ?? store!.basic;
    const weekend = ct?.weekend ?? store!.weekend;
    const annual = ct?.annual ?? store!.annual;
    const lunch = ct?.lunch ?? store!.lunch;
    const job = String(row.job ?? row.cwhdir_job ?? row.cwhd_job ?? store?.job ?? '');
    const svc = String(row.svc ?? row.cwhdir_svc ?? row.cwhd_svc ?? store?.svc ?? '') || null;
    return {
      basic,
      weekend,
      annual,
      lunch: 0,
      lunchDirector: lunch,
      overtime: ct?.overtime ?? store!.overtime,
      overtimeNontax: 0,
      night: ct?.night ?? store!.night,
      nightNontax: 0,
      holiday: ct?.holiday ?? store!.holiday,
      holidayNontax: 0,
      overtimeHours: ct?.overtimeHours ?? store!.overtimeHours,
      nightHours: ct?.nightHours ?? store!.nightHours,
      holidayHours: ct?.holidayHours ?? store!.holidayHours,
      hour: parseHomeWageRowWorkHours(row.hour ?? row.cwhdir_hour ?? row.cwhd_hour),
      job,
      svc,
      payCalcHourly: pickHomeWageRowNumber(
        row,
        'payCalcHourly',
        'cwhdir_pay_calc_hourly',
        'cwhd_pay_calc_hourly'
      ),
      payOvertime: (row.payOvertime ?? row.cwhdir_pay_overtime ?? row.cwhd_pay_overtime) as
        | boolean
        | string
        | null,
      payHoliday: (row.payHoliday ?? row.cwhdir_pay_holiday ?? row.cwhd_pay_holiday) as
        | boolean
        | string
        | null,
      payNight: (row.payNight ?? row.cwhdir_pay_night ?? row.cwhd_pay_night) as
        | boolean
        | string
        | null,
      senior: ct?.senior ?? store!.senior,
      sunim: ct?.sunim ?? store!.sunim,
      bosu: ct?.bosu ?? store!.bosu,
      severe: ct?.severe ?? store!.severe,
      bonus: ct?.bonus ?? store!.bonus,
      car: ct?.car ?? store!.car,
      child: ct?.child ?? store!.child,
      welfare: ct?.welfare ?? store!.welfare,
      other: ct?.other ?? store!.other,
      laborDay: ct?.laborDay ?? store!.laborDay,
      wageTotal: pickHomeWageRowNumber(row, 'ctWageTotal', 'wageTotal', 'cwhdir_wage_total'),
      nonTax: pickHomeWageRowNumber(row, 'ctNonTax', 'nonTax', 'cwhdir_non_tax'),
      grossPay: pickHomeWageRowNumber(row, 'ctGross', 'grossPay', 'cwhdir_gross'),
      _patientWageInfo: row._patientWageInfo as HomeWageClientReportRow['_patientWageInfo'],
      skipPremiumAnnualAbsorb: true,
      isDirectorCanonical: true,
    };
  }
  const slipBase = normalizeHomeWageViewMismatchRow(row);
  const dbAutoLunch = dbWageAmount(slipBase.lunch);
  const extractCtx = buildHomeWageLunchExtractContext({
    ...row,
    ...slipBase,
  } as Record<string, unknown>);
  const adminAnnual = resolveHomeWageAdminAnnualForSlip(
    slipBase.annual,
    dbAutoLunch,
    extractCtx
  );
  const isFamilyOnly =
    isHomeWageRowAllFamilyCareFromSvc(slipBase.svc)
    || (!slipBase.svc && inferHomeWageFamilyCareOnlyFromPayRow(slipBase));
  return {
    ...slipBase,
    annual: adminAnnual,
    // 센터장 상세와 동일 — 실지급 중식만 연차 합산 (DB 고스트 111,100 이중가산 금지)
    lunch: resolveHomeWageFundedAutoLunch({
      ...row,
      ...slipBase,
      lunch: dbAutoLunch,
    }),
    skipPremiumAnnualAbsorb: isHomeWageCaregiverJob(slipBase.job) && !isFamilyOnly,
    senior: pickWageRowField(row, 'senior', 'cwhd_senior') as number | null,
    sunim: pickWageRowField(row, 'sunim', 'cwhd_sunim') as number | null,
    bosu: pickWageRowField(row, 'bosu', 'cwhd_bosu') as number | null,
    severe: pickWageRowField(row, 'severe', 'cwhd_severe') as number | null,
    bonus: pickWageRowField(row, 'bonus', 'cwhd_bonus') as number | null,
    car: pickWageRowField(row, 'car', 'cwhd_car') as number | null,
    child: pickWageRowField(row, 'child', 'cwhd_child') as number | null,
    welfare: pickWageRowField(row, 'welfare', 'cwhd_welfare') as number | null,
    other: pickWageRowField(row, 'other', 'cwhd_other') as number | null,
    laborDay: pickWageRowField(row, 'laborDay', 'cwhd_labor_day') as number | null,
    _patientWageInfo: row._patientWageInfo as HomeWageClientReportRow['_patientWageInfo'],
    isDirectorCanonical: false,
  };
}

/** 센터장 정본 행 — 연차에 자동중식이 이미 포함. 다시 더하지 않음 */
export function isHomeWageDirectorCanonicalRow(
  row: Record<string, unknown> | null | undefined
): boolean {
  if (!row) return false;
  return (
    row.isDirectorCanonical === true
    || row._fromDirectorStore === true
    || row.cwhdir_annual != null
    || row.ctAnnual != null
    || row.cwhdir_ct_annual != null
    || row.ctBasic != null
    || row.cwhdir_ct_basic != null
  );
}

/**
 * 센터장 정본 저장 칸
 * — annual/basic 은 센터장 화면 금액
 * — lunch 는 센터장 화면 중식
 * — lunchAuto 는 표시용 파생값(저장하지 않음). ct_/ad_ 차이 또는 구 lunch_auto 폴백
 */
export interface HomeWageDirectorStoreParts {
  basic: number;
  weekend: number;
  annual: number;
  laborDay: number;
  overtime: number;
  night: number;
  holiday: number;
  overtimeHours: number;
  nightHours: number;
  holidayHours: number;
  senior: number;
  sunim: number;
  bosu: number;
  severe: number;
  lunch: number;
  lunchAuto: number;
  bonus: number;
  car: number;
  child: number;
  welfare: number;
  other: number;
  hour: number;
  days: number;
  payCalcHourly: number;
  lunchExpense: boolean;
  productionNontax: boolean;
  job: string;
  svc: string | null;
  payOvertime: HomeWageDirectorPremiumPayOverride;
  payHoliday: HomeWageDirectorPremiumPayOverride;
  payNight: HomeWageDirectorPremiumPayOverride;
}

function pickHomeWageDirectorStoreFlag(row: Record<string, unknown>, ...keys: string[]): boolean {
  for (const key of keys) {
    const v = row[key];
    if (v === 'Y' || v === true || v === 1 || v === '1') return true;
    if (v === 'N' || v === false || v === 0 || v === '0') return false;
  }
  return false;
}

/** 구 대장 행 → 센터장 정본 (이관·폴백) */
export function buildHomeWageDirectorStoreFromLegacyRow(
  row: Record<string, unknown>,
  yearMonth = ''
): HomeWageDirectorStoreParts {
  const clientRow = buildHomeWageClientReportRowFromDb({
    ...row,
    isDirectorCanonical: false,
  });
  const slip = getHomeWageCaregiverSlipPayDisplay(clientRow);
  const ym = String(yearMonth || row.yearMonth || row.cwhd_ym || '')
    .replace(/\D/g, '')
    .slice(0, 6);
  const job = String(row.job ?? row.cwhd_job ?? '');
  const lunchAuto = isHomeWageCaregiverJob(job)
    ? resolveHomeWageFundedAutoLunch(row)
    : getHomeWageAdminSettingLunchAuto(row);

  return {
    basic: slip.basic,
    weekend: slip.weekend,
    annual: slip.annual,
    laborDay: isHomeWageLaborDayMonth(ym)
      ? pickHomeWageRowNumber(row, 'laborDay', 'cwhd_labor_day')
      : 0,
    overtime: slip.overtime,
    night: slip.night,
    holiday: slip.holiday,
    overtimeHours: pickHomeWageRowNumber(row, 'overtimeHours', 'cwhd_overtime_hours'),
    nightHours: pickHomeWageRowNumber(row, 'nightHours', 'cwhd_night_hours'),
    holidayHours: pickHomeWageRowNumber(row, 'holidayHours', 'cwhd_holiday_hours'),
    senior: pickHomeWageRowNumber(row, 'senior', 'cwhd_senior'),
    sunim: pickHomeWageRowNumber(row, 'sunim', 'cwhd_sunim'),
    bosu: pickHomeWageRowNumber(row, 'bosu', 'cwhd_bosu'),
    severe: resolveHomeWageSevereDisplayAmount({
      ...clientRow,
      severe: pickHomeWageRowNumber(row, 'severe', 'cwhd_severe'),
    }),
    lunch: slip.lunch,
    lunchAuto,
    bonus: pickHomeWageRowNumber(row, 'bonus', 'cwhd_bonus'),
    car: pickHomeWageRowNumber(row, 'car', 'cwhd_car'),
    child: pickHomeWageRowNumber(row, 'child', 'cwhd_child'),
    welfare: pickHomeWageRowNumber(row, 'welfare', 'cwhd_welfare'),
    other: pickHomeWageRowNumber(row, 'other', 'cwhd_other'),
    hour: parseHomeWageRowWorkHours(row.hour ?? row.cwhd_hour),
    days: pickHomeWageRowNumber(row, 'days', 'cwhd_days'),
    payCalcHourly: pickHomeWageRowNumber(row, 'payCalcHourly', 'cwhd_pay_calc_hourly'),
    lunchExpense: pickHomeWageDirectorStoreFlag(row, 'lunchExpense', 'cwhd_lunch_expense'),
    productionNontax: pickHomeWageDirectorStoreFlag(
      row,
      'productionNontax',
      'cwhd_production_nontax'
    ),
    job,
    svc: String(row.svc ?? row.cwhd_svc ?? '') || null,
    payOvertime: parseHomeWageDirectorPremiumPayFlag(
      row.payOvertime ?? row.cwhd_pay_overtime
    ),
    payHoliday: parseHomeWageDirectorPremiumPayFlag(
      row.payHoliday ?? row.cwhd_pay_holiday
    ),
    payNight: parseHomeWageDirectorPremiumPayFlag(row.payNight ?? row.cwhd_pay_night),
  };
}

export function mapHomeWageRowToDirectorStore(
  row: Record<string, unknown>,
  yearMonth = ''
): HomeWageDirectorStoreParts {
  if (isHomeWageDirectorCanonicalRow(row)) {
    const ym = String(yearMonth || row.yearMonth || row.cwhd_ym || row.cwhdir_ym || '')
      .replace(/\D/g, '')
      .slice(0, 6);
    return {
      basic: pickHomeWageRowNumber(row, 'ctBasic', 'cwhdir_ct_basic', 'basic', 'cwhdir_basic', 'cwhd_basic'),
      weekend: pickHomeWageRowNumber(row, 'ctWeekend', 'cwhdir_ct_weekend', 'weekend', 'cwhdir_weekend', 'cwhd_weekend'),
      annual: pickHomeWageRowNumber(row, 'ctAnnual', 'cwhdir_ct_annual', 'annual', 'cwhdir_annual', 'cwhd_annual'),
      laborDay: isHomeWageLaborDayMonth(ym)
        ? pickHomeWageRowNumber(row, 'ctLaborDay', 'cwhdir_ct_labor_day', 'laborDay', 'cwhdir_labor_day', 'cwhd_labor_day')
        : 0,
      overtime: pickHomeWageRowNumber(row, 'ctOvertime', 'cwhdir_ct_overtime', 'overtime', 'cwhdir_overtime', 'cwhd_overtime'),
      night: pickHomeWageRowNumber(row, 'ctNight', 'cwhdir_ct_night', 'night', 'cwhdir_night', 'cwhd_night'),
      holiday: pickHomeWageRowNumber(row, 'ctHoliday', 'cwhdir_ct_holiday', 'holiday', 'cwhdir_holiday', 'cwhd_holiday'),
      overtimeHours: pickHomeWageRowNumber(
        row,
        'ctOvertimeHours',
        'cwhdir_ct_overtime_hours',
        'overtimeHours',
        'cwhdir_overtime_hours',
        'cwhd_overtime_hours'
      ),
      nightHours: pickHomeWageRowNumber(
        row,
        'ctNightHours',
        'cwhdir_ct_night_hours',
        'nightHours',
        'cwhdir_night_hours',
        'cwhd_night_hours'
      ),
      holidayHours: pickHomeWageRowNumber(
        row,
        'ctHolidayHours',
        'cwhdir_ct_holiday_hours',
        'holidayHours',
        'cwhdir_holiday_hours',
        'cwhd_holiday_hours'
      ),
      senior: pickHomeWageRowNumber(row, 'ctSenior', 'cwhdir_ct_senior', 'senior', 'cwhdir_senior', 'cwhd_senior'),
      sunim: pickHomeWageRowNumber(row, 'ctSunim', 'cwhdir_ct_sunim', 'sunim', 'cwhdir_sunim', 'cwhd_sunim'),
      bosu: pickHomeWageRowNumber(row, 'ctBosu', 'cwhdir_ct_bosu', 'bosu', 'cwhdir_bosu', 'cwhd_bosu'),
      severe: pickHomeWageRowNumber(row, 'ctSevere', 'cwhdir_ct_severe', 'severe', 'cwhdir_severe', 'cwhd_severe'),
      lunch: pickHomeWageRowNumber(row, 'ctLunch', 'cwhdir_ct_lunch', 'lunchDirector', 'cwhdir_lunch'),
      lunchAuto: hasHomeWageStoredAdParts(row)
        ? deriveHomeWageAdminAutoLunchFromCtAd(row)
        : pickHomeWageRowNumber(row, 'lunchAuto', 'cwhdir_lunch_auto'),
      bonus: pickHomeWageRowNumber(row, 'ctBonus', 'cwhdir_ct_bonus', 'bonus', 'cwhdir_bonus', 'cwhd_bonus'),
      car: pickHomeWageRowNumber(row, 'ctCar', 'cwhdir_ct_car', 'car', 'cwhdir_car', 'cwhd_car'),
      child: pickHomeWageRowNumber(row, 'ctChild', 'cwhdir_ct_child', 'child', 'cwhdir_child', 'cwhd_child'),
      welfare: pickHomeWageRowNumber(row, 'ctWelfare', 'cwhdir_ct_welfare', 'welfare', 'cwhdir_welfare', 'cwhd_welfare'),
      other: pickHomeWageRowNumber(row, 'ctOther', 'cwhdir_ct_other', 'other', 'cwhdir_other', 'cwhd_other'),
      hour: parseHomeWageRowWorkHours(
        row.hour ?? row.cwhdir_hour ?? row.cwhd_hour
      ),
      days: pickHomeWageRowNumber(row, 'days', 'cwhdir_days', 'cwhd_days'),
      payCalcHourly: pickHomeWageRowNumber(
        row,
        'payCalcHourly',
        'cwhdir_pay_calc_hourly',
        'cwhd_pay_calc_hourly'
      ),
      lunchExpense: pickHomeWageDirectorStoreFlag(
        row,
        'lunchExpense',
        'cwhdir_lunch_expense',
        'cwhd_lunch_expense'
      ),
      productionNontax: pickHomeWageDirectorStoreFlag(
        row,
        'productionNontax',
        'cwhdir_production_nontax',
        'cwhd_production_nontax'
      ),
      job: String(row.job ?? row.cwhdir_job ?? row.cwhd_job ?? ''),
      svc: String(row.svc ?? row.cwhdir_svc ?? row.cwhd_svc ?? '') || null,
      payOvertime: parseHomeWageDirectorPremiumPayFlag(
        row.payOvertime ?? row.cwhdir_pay_overtime ?? row.cwhd_pay_overtime
      ),
      payHoliday: parseHomeWageDirectorPremiumPayFlag(
        row.payHoliday ?? row.cwhdir_pay_holiday ?? row.cwhd_pay_holiday
      ),
      payNight: parseHomeWageDirectorPremiumPayFlag(
        row.payNight ?? row.cwhdir_pay_night ?? row.cwhd_pay_night
      ),
    };
  }
  return buildHomeWageDirectorStoreFromLegacyRow(row, yearMonth);
}

/** 센터장 정본 → 관리자 칸 (연차/기본급에서 자동중식만 분리) */
export function normalizeHomeWagePersonBirth(birth: unknown): string {
  if (birth == null || birth === '') return '';
  return String(birth).split('T')[0].replace(/\./g, '-').slice(0, 10);
}

export function matchHomeWageRowByNameBirth(
  rows: Record<string, unknown>[] | null | undefined,
  name: string,
  birth: unknown
): Record<string, unknown> | undefined {
  const n = String(name || '').trim();
  const b = normalizeHomeWagePersonBirth(birth);
  if (!n || !rows?.length) return undefined;
  return rows.find((row) => {
    const rn = String(row.name ?? row.cwhdir_name ?? row.cwhd_name ?? '').trim();
    if (rn !== n) return false;
    const rb = normalizeHomeWagePersonBirth(
      row.birth ?? row.cwhdir_birth ?? row.cwhd_birth
    );
    return !b || !rb || rb === b;
  });
}

/** 구 대장 행 위에 센터장 정본을 덮어 관리자 실시간 분해·센터장 표시에 사용 */
export function mergeHomeWageDirectorCanonicalOntoRow(
  legacy: Record<string, unknown>,
  director: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (!director) return legacy;
  const store = mapHomeWageRowToDirectorStore({
    ...director,
    isDirectorCanonical: true,
  });
  return {
    ...legacy,
    ...director,
    idx: legacy.idx ?? director.srcIdx ?? director.idx,
    srcIdx: director.srcIdx ?? director.idx,
    basic: store.basic,
    weekend: store.weekend,
    annual: store.annual,
    laborDay: store.laborDay,
    overtime: store.overtime,
    night: store.night,
    holiday: store.holiday,
    overtimeHours: store.overtimeHours,
    nightHours: store.nightHours,
    holidayHours: store.holidayHours,
    senior: store.senior,
    sunim: store.sunim,
    bosu: store.bosu,
    severe: store.severe,
    lunch: store.lunchAuto,
    lunchDirector: store.lunch,
    lunchAuto: store.lunchAuto,
    bonus: store.bonus,
    car: store.car,
    child: store.child,
    welfare: store.welfare,
    other: store.other,
    hour: store.hour,
    days: store.days,
    payCalcHourly: store.payCalcHourly,
    job: store.job,
    svc: store.svc ?? legacy.svc,
    payOvertime: store.payOvertime,
    payHoliday: store.payHoliday,
    payNight: store.payNight,
    isDirectorCanonical: true,
    _fromDirectorStore: true,
  };
}

export function overlayHomeWageDirectorRows(
  legacyRows: Record<string, unknown>[],
  directorRows: Record<string, unknown>[] | null | undefined
): Record<string, unknown>[] {
  if (directorRows?.length) {
    return directorRows.map((row) => ({
      ...row,
      isDirectorCanonical: true,
      _fromDirectorStore: true,
    }));
  }
  return legacyRows;
}

export function getHomeWageAdminPartsFromDirectorStore(
  store: HomeWageDirectorStoreParts,
  yearMonth = ''
): HomeWageAdminSettingDisplayParts {
  const ym = String(yearMonth || '').replace(/\D/g, '').slice(0, 6);
  const lunchAuto = dbWageAmount(store.lunchAuto);
  const isCg = isHomeWageCaregiverJob(store.job);
  const isFamily =
    isHomeWageRowAllFamilyCareFromSvc(store.svc)
    || inferHomeWageFamilyCareOnlyFromPayRow({
      annual: Math.max(0, store.annual - lunchAuto),
      weekend: store.weekend,
      basic: store.basic,
    });

  let basic = store.basic;
  let annual = store.annual;
  if (isCg && isFamily) {
    basic = Math.max(0, store.basic - lunchAuto);
    annual = 0;
  } else if (isCg) {
    annual = Math.max(0, store.annual - lunchAuto);
  } else {
    basic = Math.max(0, store.basic - lunchAuto);
  }

  // 관리자 AD 칸 — 생산직 체크·가족 여부와 무관, 연야휴 전액 기준 월 20만 비과세
  const split = calcHomeWageOvertimeNontax(
    store.overtime,
    store.night,
    store.holiday,
    true
  );

  return {
    basic,
    weekend: store.weekend,
    annual,
    laborDay: isHomeWageLaborDayMonth(ym) ? store.laborDay : 0,
    overtime: split.overtimeTax,
    overtimeNontax: split.overtimeNontax,
    night: split.nightTax,
    nightNontax: split.nightNontax,
    holiday: split.holidayTax,
    holidayNontax: split.holidayNontax,
    senior: store.senior,
    sunim: store.sunim,
    bosu: store.bosu,
    severe: store.severe,
    bonus: store.bonus,
    lunchAuto,
    lunchDirector: store.lunch,
    car: store.car,
    child: store.child,
    welfare: store.welfare,
    other: store.other,
  };
}

export function hasHomeWageStoredCtParts(
  row: Record<string, unknown> | null | undefined
): boolean {
  if (!row) return false;
  return row.ctBasic != null || row.cwhdir_ct_basic != null;
}

/** 센터장(고객용) 저장칸 — 가공 없이 그대로 표시 */
export type HomeWageStoredCtParts = {
  basic: number;
  weekend: number;
  annual: number;
  laborDay: number;
  overtime: number;
  night: number;
  holiday: number;
  overtimeHours: number;
  nightHours: number;
  holidayHours: number;
  senior: number;
  sunim: number;
  bosu: number;
  severe: number;
  bonus: number;
  lunch: number;
  car: number;
  child: number;
  welfare: number;
  other: number;
};

export function readHomeWageStoredCtParts(
  row: Record<string, unknown>,
  yearMonth = ''
): HomeWageStoredCtParts {
  const ym = String(yearMonth || row.yearMonth || row.cwhdir_ym || row.cwhd_ym || '')
    .replace(/\D/g, '')
    .slice(0, 6);
  return {
    basic: pickHomeWageRowNumber(row, 'ctBasic', 'cwhdir_ct_basic'),
    weekend: pickHomeWageRowNumber(row, 'ctWeekend', 'cwhdir_ct_weekend'),
    annual: pickHomeWageRowNumber(row, 'ctAnnual', 'cwhdir_ct_annual'),
    laborDay: isHomeWageLaborDayMonth(ym)
      ? pickHomeWageRowNumber(row, 'ctLaborDay', 'cwhdir_ct_labor_day')
      : 0,
    overtime: pickHomeWageRowNumber(row, 'ctOvertime', 'cwhdir_ct_overtime'),
    night: pickHomeWageRowNumber(row, 'ctNight', 'cwhdir_ct_night'),
    holiday: pickHomeWageRowNumber(row, 'ctHoliday', 'cwhdir_ct_holiday'),
    overtimeHours: pickHomeWageRowNumber(row, 'ctOvertimeHours', 'cwhdir_ct_overtime_hours'),
    nightHours: pickHomeWageRowNumber(row, 'ctNightHours', 'cwhdir_ct_night_hours'),
    holidayHours: pickHomeWageRowNumber(row, 'ctHolidayHours', 'cwhdir_ct_holiday_hours'),
    senior: pickHomeWageRowNumber(row, 'ctSenior', 'cwhdir_ct_senior'),
    sunim: pickHomeWageRowNumber(row, 'ctSunim', 'cwhdir_ct_sunim'),
    bosu: pickHomeWageRowNumber(row, 'ctBosu', 'cwhdir_ct_bosu'),
    severe: pickHomeWageRowNumber(row, 'ctSevere', 'cwhdir_ct_severe'),
    bonus: pickHomeWageRowNumber(row, 'ctBonus', 'cwhdir_ct_bonus'),
    lunch: pickHomeWageRowNumber(row, 'ctLunch', 'cwhdir_ct_lunch'),
    car: pickHomeWageRowNumber(row, 'ctCar', 'cwhdir_ct_car'),
    child: pickHomeWageRowNumber(row, 'ctChild', 'cwhdir_ct_child'),
    welfare: pickHomeWageRowNumber(row, 'ctWelfare', 'cwhdir_ct_welfare'),
    other: pickHomeWageRowNumber(row, 'ctOther', 'cwhdir_ct_other'),
  };
}

export function sumHomeWageStoredCtPayParts(parts: HomeWageStoredCtParts): number {
  return (
    parts.basic
    + parts.weekend
    + parts.annual
    + parts.laborDay
    + parts.overtime
    + parts.night
    + parts.holiday
    + parts.senior
    + parts.sunim
    + parts.bosu
    + parts.severe
    + parts.bonus
    + parts.lunch
    + parts.car
    + parts.child
    + parts.welfare
    + parts.other
  );
}

export function hasHomeWageStoredAdParts(
  row: Record<string, unknown> | null | undefined
): boolean {
  if (!row) return false;
  return row.adBasic != null || row.cwhdir_ad_basic != null;
}

/** 저장된 관리자 자동중식 — 구 대장 값을 그대로. 없으면 칸 차이 */
export function readHomeWageStoredAdminAutoLunch(
  row: Record<string, unknown>
): number {
  if (row.lunchAuto != null || row.cwhdir_lunch_auto != null) {
    return pickHomeWageRowNumber(row, 'lunchAuto', 'cwhdir_lunch_auto');
  }
  return deriveHomeWageAdminAutoLunchFromCtAd(row);
}

/** 자동중식은 저장하지 않음. 센터장·관리자 칸 차이로만 본다 */
export function deriveHomeWageAdminAutoLunchFromCtAd(row: Record<string, unknown>): number {
  const annualGap = Math.max(
    0,
    pickHomeWageRowNumber(row, 'ctAnnual', 'cwhdir_ct_annual')
      - pickHomeWageRowNumber(row, 'adAnnual', 'cwhdir_ad_annual')
  );
  const basicGap = Math.max(
    0,
    pickHomeWageRowNumber(row, 'ctBasic', 'cwhdir_ct_basic')
      - pickHomeWageRowNumber(row, 'adBasic', 'cwhdir_ad_basic')
  );
  return annualGap + basicGap;
}

export function readHomeWageStoredAdParts(
  row: Record<string, unknown>,
  yearMonth = ''
): HomeWageAdminSettingDisplayParts {
  const ym = String(yearMonth || row.yearMonth || row.cwhdir_ym || '')
    .replace(/\D/g, '')
    .slice(0, 6);
  return {
    basic: pickHomeWageRowNumber(row, 'adBasic', 'cwhdir_ad_basic'),
    weekend: pickHomeWageRowNumber(row, 'adWeekend', 'cwhdir_ad_weekend'),
    annual: pickHomeWageRowNumber(row, 'adAnnual', 'cwhdir_ad_annual'),
    laborDay: isHomeWageLaborDayMonth(ym)
      ? pickHomeWageRowNumber(row, 'adLaborDay', 'cwhdir_ad_labor_day')
      : 0,
    overtime: pickHomeWageRowNumber(row, 'adOvertime', 'cwhdir_ad_overtime'),
    overtimeNontax: pickHomeWageRowNumber(row, 'adOvertimeNontax', 'cwhdir_ad_overtime_nontax'),
    night: pickHomeWageRowNumber(row, 'adNight', 'cwhdir_ad_night'),
    nightNontax: pickHomeWageRowNumber(row, 'adNightNontax', 'cwhdir_ad_night_nontax'),
    holiday: pickHomeWageRowNumber(row, 'adHoliday', 'cwhdir_ad_holiday'),
    holidayNontax: pickHomeWageRowNumber(row, 'adHolidayNontax', 'cwhdir_ad_holiday_nontax'),
    senior: pickHomeWageRowNumber(row, 'adSenior', 'cwhdir_ad_senior'),
    sunim: pickHomeWageRowNumber(row, 'adSunim', 'cwhdir_ad_sunim'),
    bosu: pickHomeWageRowNumber(row, 'adBosu', 'cwhdir_ad_bosu'),
    severe: pickHomeWageRowNumber(row, 'adSevere', 'cwhdir_ad_severe'),
    bonus: pickHomeWageRowNumber(row, 'adBonus', 'cwhdir_ad_bonus'),
    lunchAuto: readHomeWageStoredAdminAutoLunch(row),
    lunchDirector: pickHomeWageRowNumber(
      row,
      'adLunch',
      'cwhdir_ad_lunch',
      'ctLunch',
      'cwhdir_ct_lunch'
    ),
    car: pickHomeWageRowNumber(row, 'adCar', 'cwhdir_ad_car'),
    child: pickHomeWageRowNumber(row, 'adChild', 'cwhdir_ad_child'),
    welfare: pickHomeWageRowNumber(row, 'adWelfare', 'cwhdir_ad_welfare'),
    other: pickHomeWageRowNumber(row, 'adOther', 'cwhdir_ad_other'),
  };
}

export function packHomeWageDirectorCtAdPayload(
  store: HomeWageDirectorStoreParts,
  admin: HomeWageAdminSettingDisplayParts,
  extras: {
    wageTotal?: number;
    nonTax?: number;
    ctGross?: number;
    adGross?: number;
    net?: number;
  } = {}
): Record<string, number> {
  const net = extras.net ?? 0;
  const wageTotal = extras.wageTotal ?? 0;
  const nonTax = extras.nonTax ?? 0;
  const ctGross = extras.ctGross ?? 0;
  const adGross = extras.adGross ?? ctGross;
  return {
    ctBasic: store.basic,
    ctWeekend: store.weekend,
    ctAnnual: store.annual,
    ctLaborDay: store.laborDay,
    ctOvertime: store.overtime,
    ctNight: store.night,
    ctHoliday: store.holiday,
    ctOvertimeHours: store.overtimeHours,
    ctNightHours: store.nightHours,
    ctHolidayHours: store.holidayHours,
    ctSenior: store.senior,
    ctSunim: store.sunim,
    ctBosu: store.bosu,
    ctSevere: store.severe,
    ctLunch: store.lunch,
    ctBonus: store.bonus,
    ctCar: store.car,
    ctChild: store.child,
    ctWelfare: store.welfare,
    ctOther: store.other,
    ctWageTotal: wageTotal,
    ctNonTax: nonTax,
    ctGross,
    ctNet: net,
    adBasic: admin.basic,
    adWeekend: admin.weekend,
    adAnnual: admin.annual,
    adLaborDay: admin.laborDay,
    adOvertime: admin.overtime,
    adOvertimeNontax: admin.overtimeNontax,
    adNight: admin.night,
    adNightNontax: admin.nightNontax,
    adHoliday: admin.holiday,
    adHolidayNontax: admin.holidayNontax,
    adOvertimeHours: store.overtimeHours,
    adNightHours: store.nightHours,
    adHolidayHours: store.holidayHours,
    adSenior: admin.senior,
    adSunim: admin.sunim,
    adBosu: admin.bosu,
    adSevere: admin.severe,
    adLunch: admin.lunchDirector,
    adBonus: admin.bonus,
    adCar: admin.car,
    adChild: admin.child,
    adWelfare: admin.welfare,
    adOther: admin.other,
    adWageTotal: wageTotal,
    adNonTax: nonTax,
    adGross,
    adNet: net,
  };
}

function getHomeWagePremiumNontaxTotalFromRow(
  row: Pick<
    HomeWageClientPayDisplayRow,
    'overtimeNontax' | 'nightNontax' | 'holidayNontax'
  >
): number {
  return (
    dbWageAmount(row.overtimeNontax)
    + dbWageAmount(row.nightNontax)
    + dbWageAmount(row.holidayNontax)
  );
}

function isHomeWageViewMismatchAmountClose(a: number, b: number): boolean {
  return Math.abs(Math.round(a) - Math.round(b)) <= HOME_WAGE_VIEW_MISMATCH_AMOUNT_TOLERANCE;
}

function parseHomeWageRowBooleanFlag(value: unknown): boolean {
  return value === 'Y' || value === true || value === 1 || value === '1';
}

function pickHomeWageRowNumber(row: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    if (row[key] == null || row[key] === '') continue;
    const n = Number(row[key]);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function parseClaimTimeToMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = String(value).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

function claimRowIncludesLunchWindow(row: HomeWageClaimRow): boolean {
  const start = parseClaimTimeToMinutes(row.plan_start_time);
  const end = parseClaimTimeToMinutes(row.plan_end_time);
  if (start == null || end == null || end <= start) return false;
  const lunchStart = 11 * 60 + 30;
  const lunchEnd = 13 * 60 + 30;
  return start < lunchEnd && end > lunchStart;
}

/** 청구 행 기준 점심 시간대(11:30~13:30) 포함 근무일 수 — 목록 불일치 판정용 */
export function calcHomeWageLunchWorkDayCountFromClaims(rows: HomeWageClaimRow[]): number {
  const dates = new Set<string>();
  for (const row of rows) {
    if (!claimRowIncludesLunchWindow(row)) continue;
    const dateKey = String(row.date || '').trim().split(/[T\s]/)[0];
    if (dateKey) dates.add(dateKey);
  }
  return dates.size;
}

/** 수급자별 시급·수가시간 표 하단 합계 (= Σ totalAmount) */
export function calcHomeWageClaimSummaryGrandTotal(
  summaryRows: HomeWageRecipientFeeSummary[]
): number {
  return summaryRows.reduce((sum, row) => sum + (Number(row.totalAmount) || 0), 0);
}

/** live 청구 집계 — 중증 제외 수급자별 합계 (센터장 연차 cap용) */
export function calcHomeWageNonSevereRecipientTotalFromSummaryRows(
  summaryRows: HomeWageRecipientFeeSummary[]
): number {
  return summaryRows
    .filter((row) => !row.isSevereRecipientSurcharge)
    .reduce((sum, row) => sum + (Number(row.totalAmount) || 0), 0);
}

function recalcHomeWageFormPayTotals(
  form: HomeWagePayrollFormData,
  productionNontax: boolean,
  yearMonth: string
): number {
  let wageTotal = HOME_WAGE_TAXABLE_COMPONENT_KEYS.reduce(
    (sum, key) => sum + (Number(form[key]) || 0),
    0
  );
  wageTotal += calcHomeWagePremiumTaxableAmount(form, productionNontax);
  form.wageTotal = wageTotal;
  form.nonTax = calcHomeWageTotalNontax(form, productionNontax, yearMonth);
  form.grossPay = form.wageTotal + form.nonTax;
  return form.grossPay;
}

function applyAdminPremiumAmountsToForm(input: {
  form: HomeWagePayrollFormData;
  hourly: number;
  nonSevereTarget: number;
  linkedBasic: number;
  linkedWeekend: number;
  linkedAnnual: number;
  finalLunch: number;
  productionNontax: boolean;
  totalSugaHours: number;
  isFamilyOnly: boolean;
  claimRows: HomeWageClaimRow[];
  yearMonth: string;
}): number {
  const adminHours = calcPremiumHoursFromClaims(input.claimRows, input.yearMonth, 'admin');
  input.form.overtimeHours = adminHours.overtimeHours;
  input.form.nightHours = adminHours.nightHours;
  input.form.holidayHours = adminHours.holidayHours;

  const mode = resolveHomeWagePremiumPayMode({
    totalSugaHours: input.totalSugaHours,
    isFamilyCare: input.isFamilyOnly,
  });
  const premiumPool =
    mode === 'pool'
      ? Math.max(0, input.nonSevereTarget - input.finalLunch)
      : undefined;

  const allocated = allocateHomeWagePremiumPays({
    mode,
    hourlyWage: input.hourly,
    overtimeHours: input.form.overtimeHours,
    nightHours: input.form.nightHours,
    holidayHours: input.form.holidayHours,
    premiumPool,
    yearMonth: input.yearMonth,
  });

  const split = splitHomeWagePremiumForStorage(
    allocated.overtime,
    allocated.night,
    allocated.holiday,
    input.productionNontax
  );
  applyHomeWagePremiumSplitToForm(input.form, split);

  return (
    split.overtime
    + split.night
    + split.holiday
    + split.overtimeNontax
    + split.nightNontax
    + split.holidayNontax
  );
}

/** DB 저장 센터장 시점 지급금액 (과세 wageTotal + 비과세 nonTax) */
export function calcHomeWageDirectorStoredGrossPay(
  row: Record<string, unknown>,
  yearMonth: string,
  claimRows?: HomeWageClaimRow[]
): number {
  const grossPay = getHomeWageLedgerGrossPay(row as HomeWageLedgerAmountRow);
  if (grossPay > 0) return grossPay;

  const storedLaborDay = pickHomeWageRowNumber(row, 'laborDay', 'cwhd_labor_day');
  const wageTotalRaw = pickHomeWageRowNumber(row, 'wageTotal', 'cwhd_wage_total');
  const wageTotal =
    isHomeWageLaborDayMonth(yearMonth) && storedLaborDay > 0
      ? Math.max(0, wageTotalRaw - storedLaborDay)
      : wageTotalRaw;

  const job = String(row.job ?? row.cwhd_job ?? '');
  const payCalcHourly = pickHomeWageRowNumber(row, 'payCalcHourly', 'cwhd_pay_calc_hourly');
  const totalSugaHours = pickHomeWageRowNumber(row, 'hour', 'cwhd_hour');
  const workDays =
    pickHomeWageRowNumber(row, 'days', 'cwhd_days')
    || (claimRows?.length ? calcWorkDaysFromClaimRows(claimRows) : 0);

  let laborDay = storedLaborDay;
  if (claimRows?.length && isHomeWageLaborDayMonth(yearMonth) && job.includes('요양보호사')) {
    const computed = calcHomeWageLaborDayPay({
      yearMonth,
      payCalcHourlyWage: payCalcHourly,
      totalSugaHours,
      workDays,
      isCaregiver: true,
      workedOnMayFirst: hasWorkedOnMayFirstFromClaims(claimRows, yearMonth),
    });
    if (computed > 0) laborDay = computed;
  }

  const productionNontax = parseHomeWageRowBooleanFlag(
    row.productionNontax ?? row.cwhd_production_nontax
  );
  const nonTax = calcHomeWageAccountingAddonNonTax(
    {
      wageTotal,
      basic: pickHomeWageRowNumber(row, 'basic', 'cwhd_basic'),
      weekend: pickHomeWageRowNumber(row, 'weekend', 'cwhd_weekend'),
      annual: pickHomeWageRowNumber(row, 'annual', 'cwhd_annual'),
      senior: pickHomeWageRowNumber(row, 'senior', 'cwhd_senior'),
      sunim: pickHomeWageRowNumber(row, 'sunim', 'cwhd_sunim'),
      bosu: pickHomeWageRowNumber(row, 'bosu', 'cwhd_bosu'),
      severe: pickHomeWageRowNumber(row, 'severe', 'cwhd_severe'),
      bonus: pickHomeWageRowNumber(row, 'bonus', 'cwhd_bonus'),
      welfare: pickHomeWageRowNumber(row, 'welfare', 'cwhd_welfare'),
      other: pickHomeWageRowNumber(row, 'other', 'cwhd_other'),
      lunch: pickHomeWageRowNumber(row, 'lunch', 'cwhd_lunch'),
      lunchDirector: pickHomeWageRowNumber(row, 'lunchDirector', 'cwhd_lunch_director'),
      car: pickHomeWageRowNumber(row, 'car', 'cwhd_car'),
      child: pickHomeWageRowNumber(row, 'child', 'cwhd_child'),
      laborDay,
      overtime: pickHomeWageRowNumber(row, 'overtime', 'cwhd_overtime'),
      night: pickHomeWageRowNumber(row, 'night', 'cwhd_night'),
      holiday: pickHomeWageRowNumber(row, 'holiday', 'cwhd_holiday'),
      overtimeNontax: pickHomeWageRowNumber(row, 'overtimeNontax', 'cwhd_overtime_nontax'),
      nightNontax: pickHomeWageRowNumber(row, 'nightNontax', 'cwhd_night_nontax'),
      holidayNontax: pickHomeWageRowNumber(row, 'holidayNontax', 'cwhd_holiday_nontax'),
    },
    productionNontax,
    yearMonth
  );

  return wageTotal + nonTax;
}

/** 관리자 시점 — 청구·수급자별 표와 대조할 기본급 연동 합계 (basic+weekend+annual+severe, 수당 제외) */
export function calcHomeWageClaimComparablePayFromSummary(
  summaryRows: HomeWageRecipientFeeSummary[]
): number {
  return summaryRows.reduce(
    (sum, row) => sum + (Number(row.totalAmount) || 0),
    0
  );
}

/** DB 저장값 — 기본급 연동 합계 (센터장 slip basic+weekend+annual + severe) */
export function calcHomeWageStoredClaimComparablePay(
  row: Record<string, unknown>
): number {
  const job = String(row.job ?? row.cwhd_job ?? '');
  if (!isHomeWageCaregiverJob(job)) return 0;

  const displayRow = normalizeHomeWageViewMismatchRow(row);
  const severe = pickHomeWageRowNumber(row, 'severe', 'cwhd_severe');
  return calcHomeWageDirectorCaregiverBasePayTotal({ severe }, displayRow);
}

/** 관리자 시점 — 청구 연동 급여 form 산출 (목록 불일치·shadow 공용) */
/**
 * 관리자(청구 forward) 급여 form — 불일치 패널·관리자 재저장 공용
 * (기본급·주휴·연차·자동중식·연야휴 등)
 */
export function buildHomeWageAdminForwardPayrollForm(
  row: Record<string, unknown>,
  ctx: HomeWageViewMismatchContext
): HomeWagePayrollFormData | null {
  const job = String(row.job ?? row.cwhd_job ?? '');
  if (!isHomeWageCaregiverJob(job)) return null;

  const summaryRows = ctx.summaryRows;
  if (!summaryRows.length || !ctx.claimRows.length) return null;

  const hourly = Math.max(
    0,
    Math.floor(pickHomeWageRowNumber(row, 'payCalcHourly', 'cwhd_pay_calc_hourly'))
      || ctx.minHourlyWage
  );
  if (hourly <= 0) return null;

  const totalSugaHours = calcTotalSugaHoursFromSummary(summaryRows);
  if (totalSugaHours <= 0) return null;

  const nonSevereRows = summaryRows.filter((r) => !r.isSevereRecipientSurcharge);
  if (!nonSevereRows.length) return null;

  const claimGrandTotal = calcHomeWageClaimSummaryGrandTotal(summaryRows);
  const severeSum = summaryRows
    .filter((r) => r.isSevereRecipientSurcharge)
    .reduce((sum, r) => sum + (Number(r.totalAmount) || 0), 0);
  const nonSevereTarget = claimGrandTotal - severeSum;

  const productionNontax = parseHomeWageRowBooleanFlag(
    row.productionNontax ?? row.cwhd_production_nontax
  );
  const lunchExpense = parseHomeWageRowBooleanFlag(
    row.lunchExpense ?? row.cwhd_lunch_expense
  );

  const ym = String(ctx.yearMonth || '');
  const year = ym.length >= 4 ? parseInt(ym.slice(0, 4), 10) : new Date().getFullYear();
  const month = ym.length >= 6 ? parseInt(ym.slice(4, 6), 10) : new Date().getMonth() + 1;
  const workDays = ctx.workDayCount ?? calcWorkDaysFromClaimRows(ctx.claimRows);
  const lunchWorkDayCount =
    ctx.lunchWorkDayCount ?? calcHomeWageLunchWorkDayCountFromClaims(ctx.claimRows);
  const annualLeaveDays = getAnnualLeaveDays(ctx.joinDate ?? null, year, month);

  const isFamilyOnly = isAllFamilyCaregiver(
    nonSevereRows.map((r) => ({ feeName: r.feeName, sugaName: r.feeName }))
  );

  const form = createEmptyPayrollForm();
  form.job = job;
  form.laborDay = pickHomeWageRowNumber(row, 'laborDay', 'cwhd_labor_day');
  form.senior = pickHomeWageRowNumber(row, 'senior', 'cwhd_senior');
  form.sunim = pickHomeWageRowNumber(row, 'sunim', 'cwhd_sunim');
  form.bosu = pickHomeWageRowNumber(row, 'bosu', 'cwhd_bosu');
  form.severe = severeSum;
  form.bonus = pickHomeWageRowNumber(row, 'bonus', 'cwhd_bonus');
  form.car = pickHomeWageRowNumber(row, 'car', 'cwhd_car');
  form.child = pickHomeWageRowNumber(row, 'child', 'cwhd_child');
  form.welfare = pickHomeWageRowNumber(row, 'welfare', 'cwhd_welfare');
  form.other = pickHomeWageRowNumber(row, 'other', 'cwhd_other');
  form.lunchDirector = pickHomeWageRowNumber(row, 'lunchDirector', 'cwhd_lunch_director');

  if (isFamilyOnly) {
    const { autoLunch: finalLunch } = calcHomeWageAutoLunch({
      lunchExpense,
      isFamilyCareOnly: true,
      nonSevereTarget,
      lunchWorkDayCount,
    });
    form.lunch = finalLunch;
    form.weekend = 0;
    form.annual = 0;

    const premiumTotal = applyAdminPremiumAmountsToForm({
      form,
      hourly,
      nonSevereTarget,
      linkedBasic: 0,
      linkedWeekend: 0,
      linkedAnnual: 0,
      finalLunch,
      productionNontax,
      totalSugaHours,
      isFamilyOnly,
      claimRows: ctx.claimRows,
      yearMonth: ym,
    });
    form.basic = Math.max(0, nonSevereTarget - finalLunch - premiumTotal);
  } else {
    const linkedMin = calcHomeWageHourlyLinkedPay({
      payCalcHourlyWage: hourly,
      totalSugaHours,
      workDays,
      annualLeaveDays,
    });
    const breakdown = calcHomeWagePayBreakdown({
      targetMonthly: nonSevereTarget,
      payCalcHourlyWage: hourly,
      minHourlyWage: ctx.minHourlyWage,
      totalSugaHours,
      workDays,
      annualLeaveDays,
    });

    const lunchResult = calcHomeWageAutoLunch({
      lunchExpense,
      isFamilyCareOnly: false,
      nonSevereTarget,
      lunchWorkDayCount,
      annualLeaveBeforeLunch: breakdown.annualLeave,
      linkedMinAnnual: linkedMin.annual,
    });
    const finalLunch = lunchResult.autoLunch;
    let finalAnnual = lunchResult.annualAfterLunch ?? breakdown.annualLeave;

    if (!lunchExpense) {
      form.lunch = 0;
    } else {
      form.lunch = finalLunch;
    }

    applyAdminPremiumAmountsToForm({
      form,
      hourly,
      nonSevereTarget,
      linkedBasic: linkedMin.basic,
      linkedWeekend: linkedMin.weekend,
      linkedAnnual: linkedMin.annual,
      finalLunch,
      productionNontax,
      totalSugaHours,
      isFamilyOnly,
      claimRows: ctx.claimRows,
      yearMonth: ym,
    });

    form.basic = breakdown.base;
    form.weekend = breakdown.weeklyHoliday;
    form.annual = Math.max(0, finalAnnual);
  }

  return form;
}

function calcHomeWageAdminClaimComparablePayFromForm(
  form: HomeWagePayrollFormData
): number {
  return (
    (Number(form.basic) || 0)
    + (Number(form.weekend) || 0)
    + (Number(form.annual) || 0)
    + (Number(form.severe) || 0)
  );
}

/** 관리자 시점 지급금액 (기본급·수당·비과세 전체 — 4대보험 shadow와 동일 산출) */
export function calcHomeWageAdminGrossPayFromContext(
  row: Record<string, unknown>,
  ctx: HomeWageViewMismatchContext
): number | null {
  const form = buildHomeWageAdminForwardPayrollForm(row, ctx);
  if (!form) return null;

  const productionNontax = parseHomeWageRowBooleanFlag(
    row.productionNontax ?? row.cwhd_production_nontax
  );
  const ym = String(ctx.yearMonth || '');
  return recalcHomeWageFormPayTotals(form, productionNontax, ym);
}

/** 관리자 시점 — 청구 forward form 기준 통합 지급금액 (센터장 slip 합계와 동일 규칙) */
export function calcHomeWageAdminUnifiedGrossFromContext(
  row: Record<string, unknown>,
  ctx: HomeWageViewMismatchContext
): number | null {
  const form = buildHomeWageAdminForwardPayrollForm(row, ctx);
  if (!form) return null;

  const ym = String(ctx.yearMonth || '');
  const summaryRows = ctx.summaryRows;
  const claimGrandTotal = calcHomeWageClaimSummaryGrandTotal(summaryRows);
  const severeSum = summaryRows
    .filter((r) => r.isSevereRecipientSurcharge)
    .reduce((sum, r) => sum + (Number(r.totalAmount) || 0), 0);
  const nonSevereRows = summaryRows.filter((r) => !r.isSevereRecipientSurcharge);
  const isFamilyOnly = isAllFamilyCaregiver(
    nonSevereRows.map((r) => ({ feeName: r.feeName, sugaName: r.feeName }))
  );

  const reportRow = buildHomeWageCaregiverUnifiedGrossReportRow({
    form,
    job: form.job,
    hour: calcTotalSugaHoursFromSummary(summaryRows),
    autoLunch: form.lunch,
    svc: String(row.svc ?? row.cwhd_svc ?? '') || null,
    recipientTotal: claimGrandTotal - severeSum,
    forceFamilyCareOnly: isFamilyOnly,
    payOvertime: parseHomeWageDirectorPremiumPayFlag(row.payOvertime ?? row.cwhd_pay_overtime),
    payHoliday: parseHomeWageDirectorPremiumPayFlag(row.payHoliday ?? row.cwhd_pay_holiday),
    payNight: parseHomeWageDirectorPremiumPayFlag(row.payNight ?? row.cwhd_pay_night),
  });

  return calcHomeWageCaregiverUnifiedDisplayGrossPay(reportRow, ym);
}

/** 관리자 시점 — 청구·수급자별 표 대조용 기본급 연동 합계 (수당·비과세 제외) */
export function calcHomeWageAdminClaimComparablePayFromContext(
  row: Record<string, unknown>,
  ctx: HomeWageViewMismatchContext
): number | null {
  const form = buildHomeWageAdminForwardPayrollForm(row, ctx);
  if (!form) return null;
  return calcHomeWageAdminClaimComparablePayFromForm(form);
}

/** 센터장·관리자 시점 비교용 — 기본급·수당·지급금액 스냅샷 */
export interface HomeWageViewPayTotalsSnapshot {
  basePayTotal: number;
  allowanceTotal: number;
  grossPay: number;
}

const HOME_WAGE_VIEW_PAY_LABELS = {
  director: '센터장 시점',
  admin: '관리자 시점',
  basePay: '기본급 합계',
  allowance: '수당 합계',
  grossPay: '지급금액',
} as const;

export interface HomeWagePayAmountMismatchPair {
  labelA: string;
  labelB: string;
  amountA: number;
  amountB: number;
  diff: number;
  matched: boolean;
}

/** 사원목록 빨간 표시 — 센터장 vs 관리자 시점 불일치 상세 */
export interface HomeWagePayAmountMismatchReport {
  mismatch: boolean;
  skipReason?: string;
  director: HomeWageViewPayTotalsSnapshot;
  admin: HomeWageViewPayTotalsSnapshot | null;
  pairs: HomeWagePayAmountMismatchPair[];
  summaryLines: string[];
}

function pickCaregiverAllowanceFieldsFromRow(
  row: Record<string, unknown>
): Pick<
  HomeWagePayrollFormData,
  'senior' | 'sunim' | 'bosu' | 'bonus' | 'welfare' | 'other' | 'car' | 'child' | 'laborDay'
> {
  return {
    senior: pickHomeWageRowNumber(row, 'senior', 'cwhd_senior'),
    sunim: pickHomeWageRowNumber(row, 'sunim', 'cwhd_sunim'),
    bosu: pickHomeWageRowNumber(row, 'bosu', 'cwhd_bosu'),
    bonus: pickHomeWageRowNumber(row, 'bonus', 'cwhd_bonus'),
    welfare: pickHomeWageRowNumber(row, 'welfare', 'cwhd_welfare'),
    other: pickHomeWageRowNumber(row, 'other', 'cwhd_other'),
    car: pickHomeWageRowNumber(row, 'car', 'cwhd_car'),
    child: pickHomeWageRowNumber(row, 'child', 'cwhd_child'),
    laborDay: pickHomeWageRowNumber(row, 'laborDay', 'cwhd_labor_day'),
  };
}

function buildHomeWageUnifiedReportRowFromAdminForm(
  row: Record<string, unknown>,
  ctx: HomeWageViewMismatchContext,
  form: HomeWagePayrollFormData
): HomeWageClientReportRow {
  const summaryRows = ctx.summaryRows;
  const claimGrandTotal = calcHomeWageClaimSummaryGrandTotal(summaryRows);
  const severeSum = summaryRows
    .filter((r) => r.isSevereRecipientSurcharge)
    .reduce((sum, r) => sum + (Number(r.totalAmount) || 0), 0);
  const nonSevereRows = summaryRows.filter((r) => !r.isSevereRecipientSurcharge);
  const isFamilyOnly = isAllFamilyCaregiver(
    nonSevereRows.map((r) => ({ feeName: r.feeName, sugaName: r.feeName }))
  );

  return buildHomeWageCaregiverUnifiedGrossReportRow({
    form,
    job: form.job,
    hour: calcTotalSugaHoursFromSummary(summaryRows),
    autoLunch: form.lunch,
    svc: String(row.svc ?? row.cwhd_svc ?? '') || null,
    recipientTotal: claimGrandTotal - severeSum,
    forceFamilyCareOnly: isFamilyOnly,
    payOvertime: parseHomeWageDirectorPremiumPayFlag(row.payOvertime ?? row.cwhd_pay_overtime),
    payHoliday: parseHomeWageDirectorPremiumPayFlag(row.payHoliday ?? row.cwhd_pay_holiday),
    payNight: parseHomeWageDirectorPremiumPayFlag(row.payNight ?? row.cwhd_pay_night),
  });
}

/** 센터장 시점 — DB·저장 row 기준 slip 합계 */
export function calcHomeWageDirectorViewPayTotalsFromRow(
  row: Record<string, unknown>,
  yearMonth: string
): HomeWageViewPayTotalsSnapshot | null {
  const job = String(row.job ?? row.cwhd_job ?? '');
  if (!isHomeWageCaregiverJob(job)) return null;

  const reportRow = buildHomeWageClientReportRowFromDb(row);
  const allowanceForm = pickCaregiverAllowanceFieldsFromRow(row);
  const severe = pickHomeWageRowNumber(row, 'severe', 'cwhd_severe');

  return {
    basePayTotal: calcHomeWageDirectorCaregiverBasePayTotal({ severe }, reportRow),
    allowanceTotal: calcHomeWageDirectorCaregiverAllowanceTotal(
      allowanceForm,
      reportRow,
      yearMonth
    ),
    grossPay: calcHomeWageClientReportGrossPay(reportRow, yearMonth),
  };
}

/**
 * 관리자 시점 — DB 저장값 기준 통합 slip 합계
 * ★ 불일치 패널용: 청구 forward 재계산이 아님 (5·6월 lunch 휴리스틱·달력 차이로 오탐 방지)
 *   센터장 slip과 동일 지급금액이어야 함 (요보 통합 규칙)
 */
export function calcHomeWageAdminViewPayTotalsFromStoredRow(
  row: Record<string, unknown>,
  yearMonth: string
): HomeWageViewPayTotalsSnapshot | null {
  return calcHomeWageDirectorViewPayTotalsFromRow(row, yearMonth);
}

/**
 * 관리자 시점 — 청구 forward form 기준 slip 합계
 * (재저장·청구 동기화용 — 불일치 빨간 표시에는 사용하지 않음)
 */
export function calcHomeWageAdminViewPayTotalsFromContext(
  row: Record<string, unknown>,
  ctx: HomeWageViewMismatchContext
): HomeWageViewPayTotalsSnapshot | null {
  const form = buildHomeWageAdminForwardPayrollForm(row, ctx);
  if (!form) return null;

  const ym = String(ctx.yearMonth || '');
  const reportRow = buildHomeWageUnifiedReportRowFromAdminForm(row, ctx, form);

  return {
    basePayTotal: calcHomeWageDirectorCaregiverBasePayTotal(
      { severe: form.severe },
      reportRow
    ),
    allowanceTotal: calcHomeWageDirectorCaregiverAllowanceTotal(form, reportRow, ym),
    grossPay: calcHomeWageCaregiverUnifiedDisplayGrossPay(reportRow, ym),
  };
}

function buildHomeWagePayAmountMismatchPair(
  labelA: string,
  labelB: string,
  amountA: number,
  amountB: number
): HomeWagePayAmountMismatchPair {
  return {
    labelA,
    labelB,
    amountA,
    amountB,
    diff: Math.abs(Math.round(amountA) - Math.round(amountB)),
    matched: isHomeWageViewMismatchAmountClose(amountA, amountB),
  };
}

/** 사원목록 빨간 표시 — 센터장 vs 관리자 시점 지급금액 불일치 리포트 */
export function buildHomeWagePayAmountTripleMismatchReport(
  row: Record<string, unknown>,
  context?: HomeWageViewMismatchContext
): HomeWagePayAmountMismatchReport {
  const emptyDirector: HomeWageViewPayTotalsSnapshot = {
    basePayTotal: 0,
    allowanceTotal: 0,
    grossPay: 0,
  };

  const emptyReport = (
    skipReason?: string,
    partial?: Partial<HomeWagePayAmountMismatchReport>
  ): HomeWagePayAmountMismatchReport => ({
    mismatch: false,
    skipReason,
    director: partial?.director ?? emptyDirector,
    admin: partial?.admin ?? null,
    pairs: partial?.pairs ?? [],
    summaryLines: partial?.summaryLines ?? [],
  });

  if (!context) {
    return emptyReport('청구·수급자 집계 데이터가 없습니다.');
  }

  const job = String(row.job ?? row.cwhd_job ?? '');
  if (!isHomeWageCaregiverJob(job)) {
    return emptyReport('요양보호사가 아닙니다.');
  }
  if (!context.summaryRows.length || !context.claimRows.length) {
    return emptyReport('청구·수급자 집계 데이터가 없습니다.');
  }

  const nonSevereRows = context.summaryRows.filter((r) => !r.isSevereRecipientSurcharge);
  const hasHourly = nonSevereRows.some((rowItem) => (Number(rowItem.hourlyWage) || 0) > 0);
  if (!hasHourly) {
    return emptyReport('수급자별 시급이 입력되지 않았습니다.');
  }

  const ym = String(context.yearMonth || '');
  const director = calcHomeWageDirectorViewPayTotalsFromRow(row, ym);
  if (!director) {
    return emptyReport('센터장 시점 금액을 계산할 수 없습니다.');
  }

  /** ★ DB 저장값·표시 규칙만 비교 (청구 forward 재계산 오탐 — 김영남 5월 vs 김순희 6월) */
  const admin = calcHomeWageAdminViewPayTotalsFromStoredRow(row, ym);
  if (!admin) {
    return emptyReport('관리자 시점 금액을 계산할 수 없습니다.', { director, admin: null });
  }

  const grossPair = buildHomeWagePayAmountMismatchPair(
    `${HOME_WAGE_VIEW_PAY_LABELS.director} ${HOME_WAGE_VIEW_PAY_LABELS.grossPay}`,
    `${HOME_WAGE_VIEW_PAY_LABELS.admin} ${HOME_WAGE_VIEW_PAY_LABELS.grossPay}`,
    director.grossPay,
    admin.grossPay
  );
  const basePair = buildHomeWagePayAmountMismatchPair(
    `${HOME_WAGE_VIEW_PAY_LABELS.director} ${HOME_WAGE_VIEW_PAY_LABELS.basePay}`,
    `${HOME_WAGE_VIEW_PAY_LABELS.admin} ${HOME_WAGE_VIEW_PAY_LABELS.basePay}`,
    director.basePayTotal,
    admin.basePayTotal
  );
  const allowancePair = buildHomeWagePayAmountMismatchPair(
    `${HOME_WAGE_VIEW_PAY_LABELS.director} ${HOME_WAGE_VIEW_PAY_LABELS.allowance}`,
    `${HOME_WAGE_VIEW_PAY_LABELS.admin} ${HOME_WAGE_VIEW_PAY_LABELS.allowance}`,
    director.allowanceTotal,
    admin.allowanceTotal
  );

  // 빨간 표시는 지급금액(gross)만 판정 — 방문목욕 등은 기본급·수당 배분만 다르고 지급금액은 같은 경우가 있음
  const mismatch = !grossPair.matched;
  const pairs: HomeWagePayAmountMismatchPair[] = mismatch
    ? [grossPair, basePair, allowancePair]
    : [grossPair];

  const summaryLines: string[] = [
    `${HOME_WAGE_VIEW_PAY_LABELS.director} ${HOME_WAGE_VIEW_PAY_LABELS.basePay}: ${director.basePayTotal.toLocaleString('ko-KR')}원`,
    `${HOME_WAGE_VIEW_PAY_LABELS.admin} ${HOME_WAGE_VIEW_PAY_LABELS.basePay}: ${admin.basePayTotal.toLocaleString('ko-KR')}원`,
    `${HOME_WAGE_VIEW_PAY_LABELS.director} ${HOME_WAGE_VIEW_PAY_LABELS.allowance}: ${director.allowanceTotal.toLocaleString('ko-KR')}원`,
    `${HOME_WAGE_VIEW_PAY_LABELS.admin} ${HOME_WAGE_VIEW_PAY_LABELS.allowance}: ${admin.allowanceTotal.toLocaleString('ko-KR')}원`,
    `${HOME_WAGE_VIEW_PAY_LABELS.director} ${HOME_WAGE_VIEW_PAY_LABELS.grossPay}: ${director.grossPay.toLocaleString('ko-KR')}원`,
    `${HOME_WAGE_VIEW_PAY_LABELS.admin} ${HOME_WAGE_VIEW_PAY_LABELS.grossPay}: ${admin.grossPay.toLocaleString('ko-KR')}원`,
  ];

  if (!grossPair.matched) {
    summaryLines.push(
      `${grossPair.labelA} ↔ ${grossPair.labelB}: 차이 ${grossPair.diff.toLocaleString('ko-KR')}원`
    );
    if (!basePair.matched) {
      summaryLines.push(
        `(참고) ${basePair.labelA} ↔ ${basePair.labelB}: 차이 ${basePair.diff.toLocaleString('ko-KR')}원`
      );
    }
    if (!allowancePair.matched) {
      summaryLines.push(
        `(참고) ${allowancePair.labelA} ↔ ${allowancePair.labelB}: 차이 ${allowancePair.diff.toLocaleString('ko-KR')}원`
      );
    }
    summaryLines.push(
      '센터장·관리자 시점의 지급금액이 다릅니다. 확인 후 재저장하세요.'
    );
  }

  return {
    mismatch,
    director,
    admin,
    pairs,
    summaryLines,
  };
}

/**
 * 사원목록 빨간 표시 — 센터장 vs 관리자 시점 지급금액 불일치
 * 지급금액(gross)만 비교 (±100원 허용). 기본급·수당 배분 차이는 빨간 표시 제외
 */
export function hasHomeWagePayAmountTripleMismatch(
  row: Record<string, unknown>,
  context?: HomeWageViewMismatchContext
): boolean {
  return buildHomeWagePayAmountTripleMismatchReport(row, context).mismatch;
}

/**
 * 레거시 DB — 센터장 캐논 저장값과 다른 형태(식대·연야휴 비과세 컬럼)가 남아 있는지
 * 센터장 재저장 후에는 lunch=0, 연·야·휴 nontax=0 이므로 false
 */
function hasHomeWageLegacyDirectorStoredPayDrift(row: HomeWageViewMismatchRow): boolean {
  if (!isHomeWageCaregiverJob(row.job)) return false;
  if (dbWageAmount(row.lunch) > 0) return true;
  if (getHomeWagePremiumNontaxTotalFromRow(row) > 0) return true;
  return false;
}

/**
 * 연·야·휴 표시 불일치 — 수동조정·60h 미만·가족요양 마스킹
 */
function hasHomeWagePremiumManualOrMaskMismatch(row: HomeWageViewMismatchRow): boolean {
  const adminPremiumTotal = getHomeWagePremiumFullTotalFromRow(row);

  let directorPremiumTotal: number;
  if (parseHomeWagePremiumManualFlag(row.premiumManual)) {
    const manual = decodeHomeWagePremiumManualMemo(row.memo);
    directorPremiumTotal = manual
      ? manual.overtime + manual.night + manual.holiday
      : adminPremiumTotal;
  } else if (isHomeWageCaregiverJob(row.job)) {
    const slip = getHomeWageCaregiverSlipPayDisplay(row);
    directorPremiumTotal = slip.overtime + slip.night + slip.holiday;
  } else if (shouldHomeWageMaskPremiumClientDisplay(row)) {
    directorPremiumTotal = 0;
  } else {
    directorPremiumTotal = adminPremiumTotal;
  }

  return Math.round(adminPremiumTotal) !== Math.round(directorPremiumTotal);
}

/**
 * 청구+시급+센터장 정책 forward 계산 vs DB 저장 basic/weekend/annual/lunch/연·야·휴
 */
/** 생산직 비과세·식대 실비변상 대상 + 급여대장 저장 — forward 검증 전 마이그레이션 후보 */
function hasHomeWageProductionNontaxMigrationCandidate(
  raw: Record<string, unknown>,
  row: HomeWageViewMismatchRow
): boolean {
  if (!isHomeWageCaregiverJob(row.job)) return false;

  const productionNontax =
    raw.productionNontax === 'Y'
    || raw.productionNontax === true
    || raw.productionNontax === 1
    || raw.cwhd_production_nontax === 1
    || raw.cwhd_production_nontax === 'Y';
  const lunchExpense =
    raw.lunchExpense === 'Y'
    || raw.lunchExpense === true
    || raw.lunchExpense === 1
    || raw.cwhd_lunch_expense === 1
    || raw.cwhd_lunch_expense === 'Y';
  if (!productionNontax && !lunchExpense) return false;

  const grossPay =
    dbWageAmount(raw.wageTotal as number | string | null)
    + dbWageAmount(raw.nonTax as number | string | null);
  const hasPay = grossPay > 0 || dbWageAmount(row.basic) > 0;
  if (!hasPay) return false;

  if (hasHomeWageLegacyDirectorStoredPayDrift(row)) return true;
  if (parseHomeWagePremiumManualFlag(row.premiumManual)) return true;

  return productionNontax || lunchExpense;
}

function hasHomeWageDirectorForwardPayMismatch(
  row: HomeWageViewMismatchRow,
  ctx: HomeWageViewMismatchContext
): boolean {
  if (!isHomeWageCaregiverJob(row.job)) return false;

  const summaryRows = ctx.summaryRows;
  if (!summaryRows.length || !ctx.claimRows.length) return false;

  const hourly = Math.max(
    0,
    Math.floor(Number(row.payCalcHourly) || 0) || ctx.minHourlyWage
  );
  if (hourly <= 0) return false;

  const totalSugaHours = calcTotalSugaHoursFromSummary(summaryRows);
  if (totalSugaHours <= 0) return false;

  const nonSevereRows = summaryRows.filter((r) => !r.isSevereRecipientSurcharge);
  if (!nonSevereRows.length) return false;

  const claimGrandTotal = summaryRows.reduce((sum, r) => sum + (r.totalAmount || 0), 0);
  const severeSum = summaryRows
    .filter((r) => r.isSevereRecipientSurcharge)
    .reduce((sum, r) => sum + (r.totalAmount || 0), 0);
  const nonSevereTarget = claimGrandTotal - severeSum;

  const workDays = calcWorkDaysFromClaimRows(ctx.claimRows);
  const ym = String(ctx.yearMonth || '');
  const year = ym.length >= 4 ? parseInt(ym.slice(0, 4), 10) : new Date().getFullYear();
  const month = ym.length >= 6 ? parseInt(ym.slice(4, 6), 10) : new Date().getMonth() + 1;
  const annualLeaveDays = getAnnualLeaveDays(ctx.joinDate ?? null, year, month);

  const isFamilyOnly = isAllFamilyCaregiver(
    nonSevereRows.map((r) => ({ feeName: r.feeName, sugaName: r.feeName }))
  );

  const premiumHours = calcPremiumHoursFromClaims(ctx.claimRows, ym, 'director');
  const hoursForAlloc = resolvePremiumHoursForAllocation({
    overtimeHours: premiumHours.overtimeHours,
    nightHours: premiumHours.nightHours,
    holidayHours: premiumHours.holidayHours,
    viewMode: 'director',
    isFamilyCareOnly: isFamilyOnly,
    totalSugaHours,
    directorPayOvertime: parseHomeWageDirectorPremiumPayFlag(row.payOvertime),
    directorPayHoliday: parseHomeWageDirectorPremiumPayFlag(row.payHoliday),
    directorPayNight: parseHomeWageDirectorPremiumPayFlag(row.payNight),
  });
  const mode = resolveHomeWagePremiumPayMode({
    totalSugaHours,
    isFamilyCare: isFamilyOnly,
  });
  const premiumPool =
    mode === 'pool' ? Math.max(0, nonSevereTarget) : undefined;

  const allocated = allocateHomeWagePremiumPays({
    mode,
    hourlyWage: hourly,
    overtimeHours: hoursForAlloc.overtimeHours,
    nightHours: hoursForAlloc.nightHours,
    holidayHours: hoursForAlloc.holidayHours,
    premiumPool,
    yearMonth: ym,
  });
  const expectedPremiumTotal =
    allocated.overtime + allocated.night + allocated.holiday;

  let expectedBasic: number;
  let expectedWeekend: number;
  let expectedAnnual: number;

  if (isFamilyOnly) {
    expectedWeekend = 0;
    expectedAnnual = 0;
    expectedBasic = Math.max(0, nonSevereTarget - expectedPremiumTotal);
  } else {
    const directorLinked = calcDirectorCaregiverLinkedPay({
      targetMonthly: nonSevereTarget,
      payCalcHourlyWage: hourly,
      minHourlyWage: ctx.minHourlyWage,
      totalSugaHours,
      workDays,
      annualLeaveDays,
    });
    expectedBasic = directorLinked.breakdown.base;
    expectedWeekend = directorLinked.breakdown.weeklyHoliday;
    expectedAnnual = directorLinked.annual;
  }

  const storedPremiumTotal = getHomeWagePremiumFullTotalFromRow(row);

  if (!isHomeWageViewMismatchAmountClose(storedPremiumTotal, expectedPremiumTotal)) return true;
  if (!isHomeWageViewMismatchAmountClose(dbWageAmount(row.basic), expectedBasic)) return true;
  if (!isHomeWageViewMismatchAmountClose(dbWageAmount(row.weekend), expectedWeekend)) return true;
  if (!isHomeWageViewMismatchAmountClose(dbWageAmount(row.annual), expectedAnnual)) return true;
  if (!isHomeWageViewMismatchAmountClose(dbWageAmount(row.lunch), 0)) return true;

  return false;
}

/**
 * 관리자 시점 ↔ 센터장(client) 시점 급여 표시 불일치 여부 (최고관리자 감사용)
 * 1. 레거시 DB — 식대·연·야·휴 비과세 컬럼이 센터장 캐논과 다름
 * 2. 연·야·휴 수동조정·60h 미만·가족요양 마스킹
 * 3. (선택) 청구+시급 기준 센터장 forward 재계산 vs DB 저장값
 */
export function hasHomeWageAdminDirectorPremiumViewMismatch(
  row: Record<string, unknown>,
  context?: HomeWageViewMismatchContext
): boolean {
  const normalized = normalizeHomeWageViewMismatchRow(row);
  const ym =
    context?.yearMonth
    || String(row.yearMonth ?? row.cwhd_ym ?? '').replace(/\D/g, '').slice(0, 6);

  if (hasHomeWageStoredCtParts(row) || isHomeWageDirectorCanonicalRow(row)) return false;
  if (isHomeWageDirectorResavedForMonth(row, ym)) return false;

  if (hasHomeWageLegacyDirectorStoredPayDrift(normalized)) return true;
  if (hasHomeWagePremiumManualOrMaskMismatch(normalized)) return true;
  if (context && hasHomeWageDirectorForwardPayMismatch(normalized, context)) return true;

  return hasHomeWageProductionNontaxMigrationCandidate(row, normalized);
}

/**
 * 센터장 nonTax 합산용 — wageTotal + nonTax(센터장 입력 식대만)
 * ★ UI·명세서·임금대장 지급합계 표시에는 사용하지 않음 → calcHomeWageDirectorDisplayGrossPay
 */
export function calcHomeWageDirectorGrossPay(
  form: Pick<HomeWagePayrollFormData, 'wageTotal' | 'nonTax'>
): number {
  return dbWageAmount(form.wageTotal) + dbWageAmount(form.nonTax);
}

/**
 * UI·명세서 공통 지급금액
 * - 요양보호사: reportRow slip 합계 (관리자·센터장 동일)
 * - 그 외: 과세합계 + 비과세
 */
export function calcHomeWageUnifiedDisplayGrossPay(
  form: Parameters<typeof calcHomeWageAdminDisplayGrossPay>[0],
  productionNontax = false,
  yearMonth = '',
  reportRow?: HomeWageClientReportRow | null
): number {
  if (reportRow && isHomeWageCaregiverJob(reportRow.job)) {
    return calcHomeWageCaregiverUnifiedDisplayGrossPay(reportRow, yearMonth);
  }
  return calcHomeWageAdminDisplayGrossPay(form, productionNontax, yearMonth);
}

/** @deprecated calcHomeWageUnifiedDisplayGrossPay — slip 합계와 불일치 방지 */
export function calcHomeWageDirectorDisplayGrossPay(
  row: HomeWageClientReportRow & { wageTotal?: number | null; cwhd_wage_total?: number | null },
  yearMonth: string,
  productionNontax = false
): number {
  const wageTotal = dbWageAmount(row.wageTotal ?? row.cwhd_wage_total);
  if (wageTotal > 0) {
    return calcHomeWageUnifiedDisplayGrossPay(row, productionNontax, yearMonth);
  }
  return calcHomeWageClientReportGrossPay(row, yearMonth);
}

/** 관리자 UI·회계대장 — 지급금액 = 과세합계 + 비과세(연·야·휴 중복 가산 없음) */
export function calcHomeWageAdminDisplayGrossPay(
  form: Pick<
    HomeWagePayrollFormData,
    | 'wageTotal'
    | 'basic'
    | 'weekend'
    | 'annual'
    | 'senior'
    | 'sunim'
    | 'bosu'
    | 'severe'
    | 'bonus'
    | 'welfare'
    | 'other'
    | 'lunch'
    | 'lunchDirector'
    | 'car'
    | 'child'
    | 'laborDay'
    | 'overtime'
    | 'night'
    | 'holiday'
    | 'overtimeNontax'
    | 'nightNontax'
    | 'holidayNontax'
  > & Record<string, unknown>,
  productionNontax = false,
  yearMonth = ''
): number {
  const job = String(form.job ?? form.cwhd_job ?? '');
  // 시설장·사복·운전원 — 목록·상세 동일 (풀+센터장중식+차량, 이중차감 DB 복원)
  if (!isHomeWageCaregiverJob(job)) {
    const row = {
      ...form,
      yearMonth: yearMonth || form.yearMonth || form.cwhd_ym || '',
    } as Record<string, unknown>;
    return getHomeWageStaffLedgerGrossPay(row);
  }

  const wageTotal = dbWageAmount(form.wageTotal);
  /**
   * 미차감(wageTotal=풀, nonTax에 자동중식) → 풀에서 중식 차감 후 비과세 가산
   * = 시급×시간 + 센터장중식·차량 등 (자동중식 이중계산 없음)
   */
  const unfunded = calcHomeWageUnfundedAutoLunch(form as Record<string, unknown>);
  const nonTax = calcHomeWageAccountingAddonNonTax(
    form,
    productionNontax,
    yearMonth,
    'admin'
  );
  return Math.max(0, wageTotal - unfunded) + nonTax;
}

/** 센터장 화면 — 요양보호사 기본급 카드 합계 (명세서 slip 규칙 + severe) */
export function calcHomeWageDirectorCaregiverBasePayTotal(
  form: Pick<HomeWagePayrollFormData, 'severe'>,
  displayRow: HomeWageClientPayDisplayRow
): number {
  const slip = getHomeWageCaregiverSlipPayDisplay(displayRow);
  return slip.basic + slip.weekend + slip.annual + dbWageAmount(form.severe);
}

/** 센터장 — 수급자별 합계 대조용 (기본+주휴+연차, 중증·수당 제외) */
export function calcHomeWageDirectorCaregiverCorePayTotal(
  displayRow: HomeWageClientPayDisplayRow
): number {
  const slip = getHomeWageCaregiverSlipPayDisplay(displayRow);
  return slip.basic + slip.weekend + slip.annual;
}

/**
 * 센터장 화면 — 요양보호사 수당 카드 합계
 * ★ slip 표시(연·야·휴 지급 마스크 반영) + 통합 수당 키 직접 합산
 *   — wageTotal 재산출 타이밍에 gross−기본급 차액이 0으로 깜빡이는 현상 방지
 */
export function calcHomeWageDirectorCaregiverAllowanceTotal(
  form: Pick<
    HomeWagePayrollFormData,
    'senior' | 'sunim' | 'bosu' | 'bonus' | 'welfare' | 'other' | 'car' | 'child' | 'laborDay'
  >,
  displayRow: HomeWageClientPayDisplayRow,
  yearMonth?: string
): number {
  const slip = getHomeWageCaregiverSlipPayDisplay(displayRow);

  let sum = slip.overtime + slip.night + slip.holiday + slip.lunch;

  for (const key of HOME_WAGE_DIRECTOR_MERGED_ALLOWANCE_KEYS) {
    if (key === 'lunchDirector') continue;
    if (!(key in form)) continue;
    sum += dbWageAmount(form[key as keyof typeof form]);
  }

  if (yearMonth && isHomeWageLaborDayMonth(yearMonth)) {
    sum += dbWageAmount(form.laborDay);
  }

  return Math.max(0, sum);
}

/**
 * 센터장 화면 — 센터장/사복/운전원 근무정보 카드 합계
 * ★ 기본급 = 시급×시간(풀). 자동중식은 풀에 포함·별도 표시 없음
 */
export function calcHomeWageDirectorStaffWorkInfoTotal(
  displayRow: HomeWageClientPayDisplayRow,
  _options?: { foldAutoLunchIntoBasic?: boolean }
): number {
  const slip = getHomeWageCaregiverSlipPayDisplay(displayRow);
  return (
    slip.basic
    + slip.weekend
    + slip.annual
    + slip.overtime
    + slip.night
    + slip.holiday
  );
}

/**
 * 센터장 화면 — 센터장/사복 수당 카드 합계
 * ★ 중식비 = 센터장 입력만 (자동중식은 기본급에 숨김)
 */
export function calcHomeWageDirectorStaffAllowanceTotal(
  form: Pick<
    HomeWagePayrollFormData,
    | 'annual'
    | 'senior'
    | 'sunim'
    | 'bosu'
    | 'bonus'
    | 'welfare'
    | 'other'
    | 'car'
    | 'child'
    | 'lunch'
    | 'lunchDirector'
  >,
  displayRow: HomeWageClientPayDisplayRow
): number {
  const slip = getHomeWageCaregiverSlipPayDisplay(displayRow);
  let sum = slip.lunch; // 센터장 입력 중식만

  for (const key of HOME_WAGE_STAFF_DIRECTOR_MERGED_ALLOWANCE_KEYS) {
    if (key === 'lunchDirector') continue;
    if (!(key in form)) continue;
    sum += dbWageAmount(form[key as keyof typeof form]);
  }

  return Math.max(0, sum);
}

// ─────────────────────────────────────────────
// 9-2. 대표자 기관공제 정책
// ─────────────────────────────────────────────
//
// ★ 대표자(isRepresentative) 체크 시 기관공제 자동계산에서 제외:
// | 항목 | DB | 처리 |
// |------|-----|------|
// | 퇴직금 | cwhd_c_out | applyRetirement 체크 시 산출 (대표자·60h도 체크 시 가능) |
// | 고용보험(기관) | cwhd_c_go | 0 |
// | 실업급여 | cwhd_c_sil | 과세임금 × 실업급여요율 (대표자·나이 무관) |
// | 산재보험 | cwhd_c_san | 0 |
//
// DB: cwhd_is_representative CHAR(1) 'Y'|'N'
// ─────────────────────────────────────────────

export function parseHomeWageRepresentativeFlag(value: unknown): boolean {
  return value === 'Y' || value === true || value === 1 || value === '1';
}

export function formatHomeWageRepresentativeFlag(isRepresentative: boolean): 'Y' | 'N' {
  return isRepresentative ? 'Y' : 'N';
}

export interface HomeWageRepresentativeCorpAmounts {
  c_go: number;
  c_sil: number;
  c_san: number;
  c_out: number;
  l_go?: number;
}

/** 대표자: 기관 고용보험·산재보험·실업급여(및 근로자 고용보험) 0 처리 */
export function applyHomeWageRepresentativeCorpDeduction(
  amounts: HomeWageRepresentativeCorpAmounts,
  isRepresentative: boolean
): HomeWageRepresentativeCorpAmounts {
  if (!isRepresentative) return amounts;
  return {
    ...amounts,
    c_go: 0,
    c_sil: 0,
    c_san: 0,
    l_go: 0,
  };
}

// ─────────────────────────────────────────────
// 10. 센터장/사회복지사 임금설정 정책 (비요양보호사)
// ─────────────────────────────────────────────
//
// ★★★ 적용 대상 ★★★
// - ce_position IN ('센터장', '사회복지사')
// - 요양보호사가 아닌 관리직/전문직 직종
// - isCaregiverEmployee() === false인 사원
//
// ★★★ 핵심 차이점 (레거시 Hive 기준) ★★★
// | 구분 | 요양보호사 | 센터장/사회복지사 |
// |------|-----------|-----------------|
// | 기준 데이터 | 청구내역(수가) 기반 | 근무시간 직접 입력 (기본 209시간) |
// | 시급 계산 | 수급자별 시급 입력 → 합계금액 산출 | 시급 × 근무시간 = 풀 |
// | 주휴수당 | 기본급 × 0.2 | **0원 (없음)** |
// | 연차수당 | 차액 보정 포함 자동계산 | **수동 입력** (연차→자동중식 로직 없음) |
// | 중식대 | 연차 잉여에서 비과세 분리 | **자동중식 있으면 시급×시간 풀에서 차감** |
// |          |                          | 관리자: 기본급+자동중식=시급×시간 / 센터장 표시: 기본급=시급×시간 |
// | 근로일수 | 청구 데이터에서 산출 | **없음 (service_days = 0)** |
// | 퇴직금 | 과세임금(+고정식대) ÷ 12 | 과세임금(+고정식대) ÷ 12 |
// | 수급자별 설정 | 있음 | 없음 |
//
// ★★★ 가산수당 배율 (요양보호사와 동일) ★★★
// - 연장수당 = 시급 × 연장시간 × 1.5
// - 야간수당 = 시급 × 야간시간 × 0.5
// - 휴일수당 = 시급 × 휴일시간 × 1.5
//
// ─────────────────────────────────────────────

/** 센터장/사회복지사 임금설정 입력 */
export interface HomeWageStaffInput {
  /** 시급 (직접 입력) */
  hourlyWage: number;
  /** 월 근무시간 (직접 입력, 기본값 209시간) */
  workHours: number;
  /** 연차수당 (수동 입력) */
  annualPay?: number;
  /** 연장시간 */
  overtimeHours?: number;
  /** 야간시간 */
  nightHours?: number;
  /** 휴일시간 */
  holidayHours?: number;
  /** 최저시급 (참고용, 검증에 사용) */
  minHourlyWage?: number;
  /** YYYYMM — 2026-09부터 원단위 절삭 */
  yearMonth?: string;
}

/** 센터장/사회복지사 임금 계산 결과 */
export interface HomeWageStaffCalcResult {
  /** 시급×근무시간 풀 (= 관리자 기본급+자동중식, 센터장 기본급) */
  basePay: number;
  /** 주휴수당 = 0 (센터장/사회복지사는 주휴수당 없음) */
  weeklyHolidayPay: number;
  /** 연차수당 (수동 입력) */
  annualLeavePay: number;
  /** 연장수당 = round(시급 × 연장시간 × 1.5) */
  overtimePay: number;
  /** 야간수당 = round(시급 × 야간시간 × 0.5) */
  nightPay: number;
  /** 휴일수당 = round(시급 × 휴일시간 × 1.5) */
  holidayPay: number;
  /** 총 급여 = 기본급 + 연차 + 연장 + 야간 + 휴일 + 기타수당 */
  totalPay: number;
  /** 검증 결과 */
  validation: HomeWageStaffValidation;
}

/** 센터장/사회복지사 임금 검증 결과 */
export interface HomeWageStaffValidation {
  /** 시급이 최저시급 이상인지 */
  isMinimumWageMet: boolean;
  /** 근무시간이 주 15시간(월 60시간) 이상인지 (4대보험/퇴직금 가입 기준) */
  isInsuranceEligible: boolean;
  /** 경고 메시지 목록 */
  warnings: string[];
}

/** 센터장/사회복지사 기본 근무시간 (월 209시간) */
export const HOME_WAGE_STAFF_DEFAULT_WORK_HOURS = 209;

/** 센터장/사회복지사 기본급. 2026-09부터 1의자리 절삭 (8월 이하 신고분 보호) */
export function calcHomeWageStaffBasePay(
  hourlyWage: number,
  workHours: number,
  yearMonth?: string
): number {
  return truncateWageOnes(hourlyWage * workHours, yearMonth);
}

/**
 * 센터장/사회복지사 주휴수당: 항상 0
 * 레거시 정책: "사회복지사는 주휴수당 없음"
 */
export function calcHomeWageStaffWeeklyHolidayPay(): number {
  return 0;
}

/**
 * 센터장/사회복지사 연장수당
 * 2026-09부터 1의자리 절삭, 그 이전은 반올림
 */
export function calcHomeWageStaffOvertimePay(
  hourlyWage: number,
  overtimeHours: number,
  yearMonth?: string
): number {
  return truncateWageOnes(hourlyWage * overtimeHours * 1.5, yearMonth);
}

/**
 * 센터장/사회복지사 야간수당
 * 2026-09부터 1의자리 절삭, 그 이전은 반올림
 */
export function calcHomeWageStaffNightPay(
  hourlyWage: number,
  nightHours: number,
  yearMonth?: string
): number {
  return truncateWageOnes(hourlyWage * nightHours * 0.5, yearMonth);
}

/**
 * 센터장/사회복지사 휴일수당
 * 2026-09부터 1의자리 절삭, 그 이전은 반올림
 */
export function calcHomeWageStaffHolidayPay(
  hourlyWage: number,
  holidayHours: number,
  yearMonth?: string
): number {
  return truncateWageOnes(hourlyWage * holidayHours * 1.5, yearMonth);
}

/**
 * @deprecated calcHomeWageInstitutionRetirement(form, options) 사용
 */
export function calcHomeWageStaffRetirement(
  grossPay: number,
  _workHours: number,
  options: {
    isRepresentative?: boolean;
    lunch?: number;
    lunchExpense?: boolean;
  } = {}
): number {
  let taxable = grossPay;
  if (options.lunchExpense !== false) {
    taxable -= Math.min(Number(options.lunch) || 0, HOME_WAGE_LUNCH_MONTHLY_LIMIT);
  }
  return calcHomeWageInstitutionRetirement(taxable, {
    isRepresentative: options.isRepresentative ?? false,
    lunch: options.lunch,
    lunchExpense: options.lunchExpense ?? true,
  });
}

/**
 * 센터장/사회복지사 임금 검증
 * @param input 임금설정 입력
 */
export function validateHomeWageStaff(input: HomeWageStaffInput): HomeWageStaffValidation {
  const warnings: string[] = [];

  // 1. 최저시급 검증
  const minWage = input.minHourlyWage || HOME_WAGE_DEFAULT_MIN_HOURLY;
  const isMinimumWageMet = input.hourlyWage >= minWage;
  if (!isMinimumWageMet) {
    warnings.push(`시급(${input.hourlyWage.toLocaleString()}원)이 최저시급(${minWage.toLocaleString()}원) 미만입니다.`);
  }

  // 2. 4대보험/퇴직금 가입 기준 검증 (월 60시간 이상)
  const isInsuranceEligible = input.workHours >= HOME_WAGE_INSURANCE_MIN_HOURS;
  if (!isInsuranceEligible) {
    warnings.push(`월 근무시간(${input.workHours}시간)이 60시간 미만으로 4대보험/퇴직금 적용 대상이 아닐 수 있습니다.`);
  }

  return {
    isMinimumWageMet,
    isInsuranceEligible,
    warnings,
  };
}

/**
 * 센터장/사회복지사 전체 임금 계산
 * 레거시 정책: 주휴수당=0, 연차수당=수동입력, 기본급=floor(시급×근무시간)
 * @param input 임금설정 입력
 */
export function calcHomeWageStaffTotal(input: HomeWageStaffInput): HomeWageStaffCalcResult {
  const {
    hourlyWage,
    workHours,
    annualPay = 0,
    overtimeHours = 0,
    nightHours = 0,
    holidayHours = 0,
    yearMonth,
  } = input;

  // 1. 기본급 계산 (주휴수당 = 0)
  const basePay = calcHomeWageStaffBasePay(hourlyWage, workHours, yearMonth);
  const weeklyHolidayPay = calcHomeWageStaffWeeklyHolidayPay(); // 항상 0

  // 2. 연차수당 (수동 입력값 그대로)
  const annualLeavePay = annualPay;

  // 3. 가산수당 계산
  const overtimePay = calcHomeWageStaffOvertimePay(hourlyWage, overtimeHours, yearMonth);
  const nightPay = calcHomeWageStaffNightPay(hourlyWage, nightHours, yearMonth);
  const holidayPay = calcHomeWageStaffHolidayPay(hourlyWage, holidayHours, yearMonth);

  // 4. 총 급여 (비과세 수당 제외)
  const totalPay = basePay + annualLeavePay + overtimePay + nightPay + holidayPay;

  // 5. 검증
  const validation = validateHomeWageStaff(input);

  return {
    basePay,
    weeklyHolidayPay,
    annualLeavePay,
    overtimePay,
    nightPay,
    holidayPay,
    totalPay,
    validation,
  };
}

/** 4대보험 가입 최소 월 근로시간 (60시간) */
export const HOME_WAGE_INSURANCE_MIN_HOURS = 60;

/** 가족요양 전용 + 60h 미만 — 건강·장기요양 0원 안내 (체크박스 고정 아님 — 미체크 시 정책 자동계산) */
export const HOME_WAGE_FAMILY_UNDER60_HEALTH_EXEMPT_MESSAGE =
  '★ 가족요양 전용 · 월 수가시간 60h 미만 → 건강·장기요양 0원 (정책 자동계산)';

/** 가족 60h 미만 면제 시 고정 해제 대상 (건강·장기요양 4항목) */
export type HomeWageFamilyHealthInsuranceFixedFlags = {
  l_gun: boolean;
  l_jang: boolean;
  c_gun: boolean;
  c_jang: boolean;
};

export interface HomeWageFamilyHealthInsuranceExemptInput {
  isAllFamilyCare: boolean;
  totalSugaHours: number;
}

/** 가족요양 전용 + 월 수가시간 60h 미만 → 건강·장기요양보험 0원 */
export function resolveHomeWageFamilyUnder60HealthInsuranceExempt(
  input: HomeWageFamilyHealthInsuranceExemptInput
): boolean {
  if (!input.isAllFamilyCare) return false;
  const hours = Number(input.totalSugaHours);
  if (!Number.isFinite(hours) || hours <= 0) return false;
  return hours < HOME_WAGE_INSURANCE_MIN_HOURS;
}

/**
 * 가족 60h 미만 면제 시 건강·장기요양 고정(체크) 해제.
 * 0원은 buildDeductionRates → runAutoFill* 정책 자동계산으로 반영 (체크박스 고정 사용 안 함).
 */
export function applyHomeWageFamilyUnder60HealthInsuranceFixedRelease(
  exempt: boolean,
  fixed: HomeWageFamilyHealthInsuranceFixedFlags
): { changed: boolean; fixed: HomeWageFamilyHealthInsuranceFixedFlags } {
  if (!exempt) return { changed: false, fixed };
  if (!fixed.l_gun && !fixed.l_jang && !fixed.c_gun && !fixed.c_jang) {
    return { changed: false, fixed };
  }
  return {
    changed: true,
    fixed: { l_gun: false, l_jang: false, c_gun: false, c_jang: false },
  };
}

/** 기본 최저시급 (2024년 기준) */
export const HOME_WAGE_DEFAULT_MIN_HOURLY = 10030;

// ─────────────────────────────────────────────
// 11. 센터장/사회복지사 UI 필드 정의 (레거시 기준)
// ─────────────────────────────────────────────
//
// | 필드 | 레이블 | 타입 | 계산 방식 |
// |------|--------|------|----------|
// | hourlyWage | 시급 | 입력 | 직접 입력 (기본: 최저시급) |
// | workHours | 근무시간 | 입력 | 직접 입력 (기본: 209시간) |
// | basePay | 급여 | 계산 | floor(시급 × 근무시간) |
// | weeklyHolidayPay | 주휴수당 | 계산 | **0원 (없음)** |
// | annualPay | 연차수당 | 입력 | **수동 입력** |
// | overtimeHours | 연장시간 | 입력 | 직접 입력 |
// | overtimePay | 연장수당 | 계산 | round(시급 × 연장시간 × 1.5) |
// | nightHours | 야간시간 | 입력 | 직접 입력 |
// | nightPay | 야간수당 | 계산 | round(시급 × 야간시간 × 0.5) |
// | holidayHours | 휴일시간 | 입력 | 직접 입력 |
// | holidayPay | 휴일수당 | 계산 | round(시급 × 휴일시간 × 1.5) |
// | totalPay | 총 급여 | 계산 | 기본급 + 연차 + 연장 + 야간 + 휴일 |
//
// ★ 근로일수 필드 없음 (service_days = 0 고정)
// ★ 중식비/차량비/양육비 등은 별도 비과세 수당 카드에서 입력
// ★ UI 필드 정의는 homeWagePayrollFields.ts (HOME_WAGE_STAFF_*_FIELDS) 참조
//
// ─────────────────────────────────────────────
// 지난달 데이터 가져오기 — 전월 금액 고정 복사 수당
// (당월 근무일·근무시간·청구와 무관, 전월 값 그대로)
// HOME_WAGE_PREV_MONTH_FIXED_COPY_KEYS (homeWagePayrollFields.ts)
// ─────────────────────────────────────────────
// | key | 항목 |
// |-----|------|
// | senior | 장기근속수당 |
// | bosu | 보수교육비 |
// | bonus | 상여금 |
// | welfare | 처우개선비 |
// | other | 기타수당 |
// | lunchDirector | 중식대(센터장 입력분) |
// | car | 차량보조비 |
// | child | 양육비 |
//
// ★ lunch ↔ lunchDirector 승격 없음 — 전월 컬럼 값 그대로 복사
// ★ 위 목록 외(기본급·주휴·연차·연·야·휴·자동중식대·노동절 등)는
//   당월 청구/근무시간 기준으로 재산출할 수 있음
// ─────────────────────────────────────────────
